/* ══════════════════════════════════════════════════════════════
   REWORKED DEVIS DASHBOARD v3
   PIN, registre KV-synced, chat Claude, clients Shopify,
   tarifs editables, stats avancees, export CSV/XLSX/PDF/Sheets
══════════════════════════════════════════════════════════════ */

/* Secret d'acces stocke localement, envoye en header a chaque appel Worker.
   Le Worker rejette (401) toute requete sans le bon secret : c'est lui qui protege,
   pas le navigateur. */
function dashSecret() { return localStorage.getItem('rw_dash_secret') || ''; }
function wfetch(url, opts = {}) {
  const headers = Object.assign({}, opts.headers || {}, { 'X-Dash-Auth': dashSecret() });
  return fetch(url, Object.assign({}, opts, { headers }));
}

class DevisDashboard {
  constructor() {
    this.pin = '';
    this.devis = [];
    this.factures = [];
    this._fb = null; // etat du builder de facture
    this.clients = [];
    this.tarifs = {};
    this.tarifsDev = {}; // grille "Développement produit" (vêtements, coefficients, options)
    this._dev = null; // état du builder de devis développement
    this._devEditingId = null; // id du devis en cours d'édition (null = nouveau devis)
    this.tarifsConfection = {};
    this._conf = null; // état du builder de devis confection
    this._confEditingId = null;
    this.catalogue = []; // registre des produits déjà développés
    this._catalogueDraft = null;
    this._catalogueEditingId = null;
    this.matieresLib = []; // bibliothèque de matières réutilisables entre pièces/clients
    this.trimsLib = []; // bibliothèque de trims réutilisables entre pièces/clients
    this.ficheTarifsIntro = ''; // texte d'intro mémorisé de la fiche tarifs client
    this.company = null;
    this.chatHistory = [];
    this.isStreaming = false;
    this._chatContext = null; // infos client du formulaire pre-chat
    this._conversations = [];
  }

  /* ═══════════ ACCES (secret verifie par le Worker) ═══════════ */
  async submitAccess() {
    const input = document.getElementById('accessInput');
    const errEl = document.getElementById('accessError');
    const val = (input?.value || '').trim();
    if (errEl) errEl.textContent = '';
    if (!val) return;
    localStorage.setItem('rw_dash_secret', val);
    try {
      const r = await wfetch(`${WORKER_URL}/tarifs`);
      if (r.status === 401) {
        localStorage.removeItem('rw_dash_secret');
        if (errEl) errEl.textContent = "Code d'acces invalide.";
        if (input) { input.value = ''; input.focus(); }
        return;
      }
      this.unlock();
    } catch (e) {
      if (errEl) errEl.textContent = 'Erreur reseau, reessayez.';
    }
  }

  // Reconnexion silencieuse si un secret valide est deja memorise sur cet appareil.
  async tryStoredAccess() {
    if (!dashSecret()) return;
    try {
      const r = await wfetch(`${WORKER_URL}/tarifs`);
      if (r.ok) this.unlock();
      else if (r.status === 401) localStorage.removeItem('rw_dash_secret');
    } catch (e) {}
  }

  unlock() {
    document.getElementById('scr-pin').classList.remove('active');
    document.getElementById('scr-dashboard').classList.add('active');
    this.toast('Dashboard ouvert');
    this.loadAll();
  }
  lock() {
    localStorage.removeItem('rw_dash_secret');
    document.getElementById('scr-dashboard').classList.remove('active');
    document.getElementById('scr-pin').classList.add('active');
    const input = document.getElementById('accessInput');
    if (input) input.value = '';
  }

  /* ═══════════ TABS ═══════════ */
  switchTab(tab) {
    document.querySelectorAll('.dash-tab').forEach(t => t.classList.toggle('on', t.dataset.tab === tab));
    document.querySelectorAll('.tab-content').forEach(c => c.classList.toggle('on', c.id === `tab-${tab}`));
    if (tab === 'stats') this.renderStats();
    if (tab === 'factures') this.renderFactures();
    if (tab === 'tarifs') this.renderTarifs();
    if (tab === 'devdev') this.renderTarifsDev();
    if (tab === 'confection') this.renderTarifsConfection();
    if (tab === 'catalogue') this.renderCatalogue();
    if (tab === 'infos') this.renderSettings();
  }

  /* ═══════════ LOAD ALL FROM KV ═══════════ */
  async loadAll() {
    await Promise.all([this.loadDevis(), this.loadFactures(), this.loadClients(), this.loadTarifs(), this.loadTarifsDev(), this.loadTarifsConfection(), this.loadCatalogue(), this.loadMatieresLib(), this.loadTrimsLib(), this.loadFicheTarifsIntro(), this.loadConversations(), this.loadEmails(), this.loadSettings()]);
    this.renderDevis();
    this.renderFactures();
    this.renderClients();
    this.renderStats();
    this.renderEmailHistory();
    this.populateClientSuggestions();
  }

  async loadFactures() {
    try { const r = await wfetch(`${WORKER_URL}/factures`); if (r.ok) { const d = await r.json(); if (Array.isArray(d)) this.factures = d; } } catch (e) {}
  }

  async loadDevis() {
    try { const r = await wfetch(`${WORKER_URL}/devis`); if (r.ok) { const d = await r.json(); if (d.length) this.devis = d; } } catch (e) {}
    if (!this.devis.length) this.devis = this._defaultDevis();
  }

  async loadClients() {
    try {
      const r = await wfetch(`${WORKER_URL}/clients`);
      if (r.ok) { const d = await r.json(); this.clients = d.merged || []; }
    } catch (e) {}
    if (!this.clients.length) this.clients = this._defaultClients();
  }

  async loadTarifs() {
    try { const r = await wfetch(`${WORKER_URL}/tarifs`); if (r.ok) this.tarifs = await r.json(); } catch (e) {}
    if (!Object.keys(this.tarifs).length) this.tarifs = this._defaultTarifs();
  }

  async loadTarifsDev() {
    try { const r = await wfetch(`${WORKER_URL}/tarifs-dev`); if (r.ok) this.tarifsDev = await r.json(); } catch (e) {}
    if (!this.tarifsDev || !Object.keys(this.tarifsDev.garments || {}).length) this.tarifsDev = this._defaultTarifsDev();
  }

  async loadTarifsConfection() {
    try { const r = await wfetch(`${WORKER_URL}/tarifs-confection`); if (r.ok) this.tarifsConfection = await r.json(); } catch (e) {}
    if (!this.tarifsConfection || !Object.keys(this.tarifsConfection.garments || {}).length) this.tarifsConfection = this._defaultTarifsConfection();
  }

  async loadCatalogue() {
    try { const r = await wfetch(`${WORKER_URL}/catalogue`); if (r.ok) { const d = await r.json(); if (Array.isArray(d)) this.catalogue = d; } } catch (e) {}
  }

  async loadMatieresLib() {
    try { const r = await wfetch(`${WORKER_URL}/matieres`); if (r.ok) { const d = await r.json(); if (Array.isArray(d)) this.matieresLib = d; } } catch (e) {}
  }

  async loadTrimsLib() {
    try { const r = await wfetch(`${WORKER_URL}/trims`); if (r.ok) { const d = await r.json(); if (Array.isArray(d)) this.trimsLib = d; } } catch (e) {}
  }

  async loadFicheTarifsIntro() {
    try { const r = await wfetch(`${WORKER_URL}/fiche-tarifs-texte`); if (r.ok) { const d = await r.json(); this.ficheTarifsIntro = d.intro || ''; } } catch (e) {}
    if (!this.ficheTarifsIntro) this.ficheTarifsIntro = this._defaultFicheTarifsIntro();
  }

  /* ═══════════ REGISTRE ═══════════ */
  renderDevis() {
    const tbody = document.getElementById('devisTableBody');
    if (!tbody) return;
    const q = (document.getElementById('devisSearch')?.value || '').toLowerCase();
    const f = document.getElementById('devisFilter')?.value || 'all';

    const list = this.devis
      .filter(d => (f === 'all' || d.statut === f) && (!q || `${d.id} ${d.client} ${d.objet} ${d.montant} ${d.type}`.toLowerCase().includes(q)))
      .sort((a, b) => b.date.localeCompare(a.date));

    tbody.innerHTML = list.map(d => `<tr onclick="app.toggleDetail('${d.id}')" style="cursor:pointer">
      <td class="td-id" style="font-weight:600;font-family:monospace;font-size:11px">${d.id}</td>
      <td class="td-date" style="font-size:11px;color:var(--66)">${this.fmtDate(d.date)}</td>
      <td class="td-client" style="font-weight:600">${d.client}</td>
      <td class="td-type"><span class="type-tag type-${d.type}">${this.typeLabel(d.type)}</span></td>
      <td class="td-objet" style="font-size:11px;color:var(--55);max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${d.objet}</td>
      <td class="td-montant" style="font-weight:600;text-align:right;white-space:nowrap">${d.montant}</td>
      <td class="td-statut"><select class="status status-${d.statut}" onchange="app.changeStatus('${d.id}',this.value)" onclick="event.stopPropagation()" style="border:none;cursor:pointer;font-family:inherit;font-size:9px;font-weight:600;padding:3px 6px;border-radius:4px;background:inherit;color:inherit">
        ${['brouillon','envoye','accepte','refuse'].map(s => `<option value="${s}" ${d.statut===s?'selected':''}>${this.statusLabel(s)}</option>`).join('')}
      </select></td>
      <td class="td-actions"><div class="devis-actions">
        <button onclick="event.stopPropagation();app.viewHtml('${d.id}')">Voir</button>
        <button onclick="event.stopPropagation();app.editDevis('${d.id}')">Modifier</button>
        <button onclick="event.stopPropagation();app.showEmailModal('${d.id}')">Email</button>
        <button onclick="event.stopPropagation();app.openFactureBuilder('${d.id}')">Facture</button>
        <button class="devis-action-delete" onclick="event.stopPropagation();app.deleteDevis('${d.id}')">Suppr.</button>
      </div></td>
    </tr>
    <tr class="devis-detail-row" id="detail-${d.id}" style="display:none">
      <td colspan="8" style="padding:0">
        <div class="devis-detail">
          <div class="devis-detail-grid">
            <div><span class="detail-label">Client</span><span class="detail-value">${d.client}</span></div>
            <div><span class="detail-label">Type</span><span class="detail-value">${this.typeLabel(d.type)}</span></div>
            <div><span class="detail-label">Montant</span><span class="detail-value">${d.montant}</span></div>
            <div><span class="detail-label">Statut</span><span class="detail-value">${this.statusLabel(d.statut)}</span></div>
          </div>
          <div style="font-size:11px;color:var(--55);margin:8px 0">${d.objet}</div>
          ${d.producedIn?.length ? `<div style="font-size:10px;color:var(--66);margin-bottom:8px">Produit en confection : ${d.producedIn.map(p => `<span class="conf-trace-link" onclick="event.stopPropagation();app.viewHtml('${p.id}')">${p.id}</span>`).join(', ')}</div>` : ''}
          <div class="devis-detail-actions">
            <button class="btn btn-primary btn-sm" onclick="app.viewHtml('${d.id}')">Aperçu complet</button>
            <button class="btn btn-ghost btn-sm" onclick="app.editDevis('${d.id}')">Modifier</button>
            <button class="btn btn-ghost btn-sm" onclick="app.openFactureBuilder('${d.id}')">Créer une facture</button>
          </div>
        </div>
      </td>
    </tr>`).join('');

    document.getElementById('devisBadge').textContent = this.devis.length;
  }

  filterDevis() { this.renderDevis(); }

  toggleDetail(id) {
    const row = document.getElementById(`detail-${id}`);
    if (!row) return;
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      row.classList.toggle('mobile-open');
    } else {
      row.style.display = row.style.display === 'none' ? '' : 'none';
    }
  }

  async deleteDevis(id) {
    if (!confirm(`Supprimer le devis ${id} ? Cette action est irreversible.`)) return;
    this.devis = this.devis.filter(d => d.id !== id);
    this.renderDevis();
    this.renderStats();
    this.renderClients();
    try {
      await wfetch(`${WORKER_URL}/devis/${encodeURIComponent(id)}`, { method: 'DELETE' });
    } catch (e) {
      // Fallback : sauvegarder la liste filtree en bulk
      try { await wfetch(`${WORKER_URL}/devis/bulk`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.devis) }); } catch (e2) {}
    }
    this.toast(`Devis ${id} supprim\u00E9`);
  }

  async changeStatus(id, s) {
    const d = this.devis.find(x => x.id === id);
    if (d) { d.statut = s; this.renderDevis(); try { await wfetch(`${WORKER_URL}/devis/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ statut: s }) }); } catch (e) {} this.toast(`${id} : ${this.statusLabel(s)}`); }
  }

  async viewHtml(id) {
    try { const r = await wfetch(`${WORKER_URL}/devis/${id}`); if (r.ok) { const d = await r.json(); if (d.html) { this.showPreview(d.html, id); return; } } } catch (e) {}
    this.toast('Pas de HTML pour ce devis. Utilisez Claude pour le créer.');
  }

  /* ═══════════ IMPORT DEVIS ═══════════ */
  triggerImport() {
    document.getElementById('importFileInput').click();
  }

  async handleImportFiles(files) {
    for (const file of files) {
      if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
        this.toast(`${file.name} : format non support\u00E9 (HTML uniquement)`, 'error');
        continue;
      }
      const html = await file.text();

      // Extraire les infos du HTML import\u00E9
      let id = '';
      const numMatch = html.match(/(?:devis|n\u00B0|numero)[^A-Z0-9]*(\d{4}-\d{2}-\d{3})/i);
      if (numMatch) id = numMatch[1];
      if (!id) {
        const now = new Date();
        id = `IMP-${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(this.devis.length+1).padStart(3,'0')}`;
      }

      // V\u00E9rifier doublon
      if (this.devis.find(d => d.id === id)) {
        this.toast(`Devis ${id} existe d\u00E9j\u00E0, import ignor\u00E9`, 'error');
        continue;
      }

      const client = this._extractClientFromHtml(html) || file.name.replace(/\.html?$/, '').toUpperCase();
      const type = this._extractTypeFromHtml(html);
      const objet = this._extractObjetFromHtml(html) || '';
      const montant = this._extractMontantFromHtml(html) || '';

      // Extraire la date du devis
      let date = new Date().toISOString().slice(0, 10);
      const dateMatch = html.match(/(\d{1,2})\s+(?:janvier|f\u00E9vrier|mars|avril|mai|juin|juillet|ao\u00FBt|septembre|octobre|novembre|d\u00E9cembre)\s+(\d{4})/i);
      if (dateMatch) {
        const months = {'janvier':'01','f\u00E9vrier':'02','mars':'03','avril':'04','mai':'05','juin':'06','juillet':'07','ao\u00FBt':'08','septembre':'09','octobre':'10','novembre':'11','d\u00E9cembre':'12'};
        const mName = dateMatch[0].match(/janvier|f\u00E9vrier|mars|avril|mai|juin|juillet|ao\u00FBt|septembre|octobre|novembre|d\u00E9cembre/i)[0].toLowerCase();
        date = `${dateMatch[2]}-${months[mName] || '01'}-${String(dateMatch[1]).padStart(2,'0')}`;
      }

      const newDevis = { id, date, client: client.toUpperCase(), type, objet, montant, statut: 'envoye' };
      this.devis.push(newDevis);

      // Sauvegarder dans le KV
      try {
        await wfetch(`${WORKER_URL}/devis`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newDevis) });
        await wfetch(`${WORKER_URL}/devis/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ html }) });
      } catch (e) {}

      this.toast(`Devis ${id} import\u00E9 (${client})`, 'success');
    }

    this.renderDevis();
    this.renderStats();
    this.renderClients();
  }

  /* ═══════════ TEMPLATE DE BASE ═══════════ */
  async useAsTemplate(id) {
    try {
      const r = await wfetch(`${WORKER_URL}/devis/${encodeURIComponent(id)}`);
      if (r.ok) {
        const d = await r.json();
        if (d.html) {
          this._templateHtml = d.html;
          this._templateId = id;
          this.switchTab('claude');
          // Afficher la bannière
          const banner = document.getElementById('templateBanner');
          const bannerText = document.getElementById('templateBannerText');
          if (banner) { banner.style.display = ''; }
          if (bannerText) { bannerText.textContent = `Modification du devis ${id}`; }
          // Masquer le formulaire de brief et afficher un message pret a modifier
          const w = document.getElementById('chatWelcome');
          if (w) w.style.display = 'none';
          this.addMsg('assistant', `Devis ${id} charg\u00E9. Dites-moi ce qu'il faut changer (prix, quantit\u00E9s, dates, texte...) et je le modifie sans tout refaire. Exemple : \u00AB passe la quantit\u00E9 \u00E0 80 \u00BB ou \u00AB ajoute une remise de 10 % \u00BB.`);
          const inp = document.getElementById('chatInput');
          if (inp) { inp.placeholder = 'D\u00E9crivez la modification \u00E0 apporter...'; inp.focus(); }
          this.toast(`Devis ${id} pr\u00EAt \u00E0 modifier`, 'success');
          return;
        }
      }
    } catch (e) {}
    this.toast('Pas de HTML pour ce devis', 'error');
  }

  clearTemplate() {
    this._templateHtml = null;
    this._templateId = null;
    const banner = document.getElementById('templateBanner');
    if (banner) banner.style.display = 'none';
  }

  /* ═══════════ EXPORTS ═══════════ */
  exportCSV() {
    const rows = [['N°','Date','Client','Type','Objet','Montant','Statut']];
    this.devis.forEach(d => rows.push([d.id, d.date, d.client, this.typeLabel(d.type), d.objet, d.montant, this.statusLabel(d.statut)]));
    const csv = rows.map(r => r.map(c => `"${(c||'').replace(/"/g,'""')}"`).join(',')).join('\n');
    this._download(csv, 'devis-reworked.csv', 'text/csv');
    this.toast('CSV exporté');
  }

  exportXLSX() {
    const rows = [['N°','Date','Client','Type','Objet','Montant','Statut']];
    this.devis.forEach(d => rows.push([d.id, d.date, d.client, this.typeLabel(d.type), d.objet, d.montant, this.statusLabel(d.statut)]));
    let xml = '<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="Devis"><Table>';
    rows.forEach((r, i) => { xml += '<Row>'; r.forEach(c => { xml += `<Cell><Data ss:Type="String">${this.escHtml(c||'')}</Data></Cell>`; }); xml += '</Row>'; });
    xml += '</Table></Worksheet></Workbook>';
    this._download(xml, 'devis-reworked.xls', 'application/vnd.ms-excel');
    this.toast('Excel exporté');
  }

  exportSheets() {
    const rows = [['N°','Date','Client','Type','Objet','Montant','Statut']];
    this.devis.forEach(d => rows.push([d.id, d.date, d.client, this.typeLabel(d.type), d.objet, d.montant, this.statusLabel(d.statut)]));
    const text = rows.map(r => r.join('\t')).join('\n');
    navigator.clipboard.writeText(text).then(() => this.toast('Copié ! Collez dans Google Sheets (Ctrl+V)', 'success'));
  }

  exportPDF() {
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Registre Devis REWORKED</title>
    <style>@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
    *{margin:0;padding:0;box-sizing:border-box}body{font-family:'Inter',sans-serif;font-size:9px;padding:30px 35px;max-width:210mm;margin:0 auto}
    h1{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px}
    .sub{font-size:8px;color:#666;margin-bottom:15px}
    table{width:100%;border-collapse:collapse}th{background:#111;color:#fff;font-size:7px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;padding:6px 8px;text-align:left}
    td{padding:6px 8px;border-bottom:1px solid #eee;font-size:8px}
    .footer{margin-top:20px;padding-top:8px;border-top:1px solid #ddd;font-size:7px;color:#888;text-align:center}</style></head><body>
    <h1>Registre des devis</h1><div class="sub">REWORKED — Exporté le ${new Date().toLocaleDateString('fr-FR')}</div>
    <table><thead><tr><th>N°</th><th>Date</th><th>Client</th><th>Type</th><th>Objet</th><th>Montant</th><th>Statut</th></tr></thead><tbody>
    ${this.devis.sort((a,b)=>b.date.localeCompare(a.date)).map(d => `<tr><td style="font-weight:600;font-family:monospace">${d.id}</td><td>${this.fmtDate(d.date)}</td><td style="font-weight:600">${d.client}</td><td>${this.typeLabel(d.type)}</td><td>${d.objet}</td><td style="font-weight:600;text-align:right">${d.montant}</td><td>${this.statusLabel(d.statut)}</td></tr>`).join('')}
    </tbody></table><div class="footer">REWORKED — 18 Rue Calliet, 69001 Lyon — atelier@rework-ed.com</div></body></html>`;
    this._download(html, 'registre-devis-reworked.html', 'text/html');
    this.toast('HTML registre exporté (ouvrir dans Chrome pour PDF)');
  }

  _download(content, name, type) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([content], { type }));
    a.download = name; a.click(); URL.revokeObjectURL(a.href);
  }

  /* ═══════════ CLAUDE CHAT ═══════════ */
  async sendMessage() {
    const input = document.getElementById('chatInput');
    const msg = input.value.trim();
    if (!msg || this.isStreaming) return;

    const w = document.getElementById('chatWelcome'); if (w) w.style.display = 'none';
    this.addMsg('user', msg);
    this.chatHistory.push({ role: 'user', content: msg });
    input.value = ''; this.autoResize(input);
    this.isStreaming = true;
    this.setChatStatus('thinking');

    try {
      const r = await wfetch(`${WORKER_URL}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: this.chatHistory,
          clients: this.clients,
          devis: this.devis,
          tarifs: this.tarifs,
          templateHtml: this._templateHtml || this._lastGeneratedHtml || null
        })
      });
      if (!r.ok) throw new Error(`Erreur ${r.status}`);

      this.setChatStatus('streaming');
      const reader = r.body.getReader(), dec = new TextDecoder();
      let full = '', buffer = '';
      const el = this.addMsg('assistant', '');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const d = line.slice(6); if (d === '[DONE]') continue;
          try { const p = JSON.parse(d); if (p.type === 'content_block_delta' && p.delta?.text) { full += p.delta.text; el.innerHTML = this.fmtMd(full); this.scrollChat(); } } catch (e) {}
        }
      }
      this.chatHistory.push({ role: 'assistant', content: full });
      this.setChatStatus('done');

      // Detect and extract HTML devis
      const htmlMatch = full.match(/```html\n([\s\S]*?)```/) || full.match(/(<!DOCTYPE html>[\s\S]*?<\/html>)/i);
      if (htmlMatch) {
        let html = htmlMatch[1].trim();

        // Bug 2 fix: forcer le logo R/ED STUDIO si absent du HTML (un ancien devis garde son logo REWORKED)
        const logoUrl = 'https://cdn.shopify.com/s/files/1/0798/6259/5851/t/17/assets/logo-red-studio.svg';
        if (!html.includes(logoUrl) && !html.includes('logo-reworked.svg')) {
          const logoTag = `<img class="logo" src="${logoUrl}" alt="R/ED STUDIO" style="height:10px">`;
          // Essayer d'injecter apres <body> ou au debut du premier conteneur
          if (html.includes('<body>')) {
            html = html.replace('<body>', '<body>\n' + logoTag);
          } else if (html.match(/<div[^>]*>/)) {
            html = html.replace(/<div[^>]*>/, (m) => m + '\n' + logoTag);
          }
        }

        // Bug 1 fix: extraire le bloc devis-meta JSON si present
        const metaMatch = full.match(/```devis-meta\n([\s\S]*?)```/);
        if (metaMatch) {
          try {
            this._lastDevisMeta = JSON.parse(metaMatch[1].trim());
          } catch (e) { this._lastDevisMeta = null; }
        } else {
          this._lastDevisMeta = null;
        }

        this._lastGeneratedHtml = html;

        // Add action buttons after the message
        const actions = document.createElement('div');
        actions.className = 'chat-msg system';
        actions.innerHTML = `
          <div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:center">
            <button class="btn btn-primary btn-sm" onclick="app.previewGenerated()">Aperçu</button>
            <button class="btn btn-green btn-sm" onclick="app.saveGeneratedDevis()">Sauvegarder dans le registre</button>
            <button class="btn btn-ghost btn-sm" onclick="app.downloadGenerated()">Télécharger HTML</button>
          </div>
        `;
        document.getElementById('chatMessages').appendChild(actions);
        this.scrollChat();
        this.toast('Devis généré ! Utilisez les boutons ci-dessous.', 'success');
      }

    } catch (err) { this.addMsg('system', `Erreur : ${err.message}`); this.setChatStatus('error'); }

    this.isStreaming = false;
  }

  setChatStatus(status) {
    const send = document.getElementById('chatSend');
    const statusEl = document.getElementById('chatStatus');
    if (status === 'thinking') {
      send.disabled = true;
      send.innerHTML = '<span class="spin">↻</span>';
      if (statusEl) statusEl.textContent = 'Claude réfléchit...';
    } else if (status === 'streaming') {
      if (statusEl) statusEl.textContent = 'Claude écrit...';
    } else if (status === 'done') {
      send.disabled = false;
      send.innerHTML = '↑';
      if (statusEl) statusEl.textContent = '';
    } else if (status === 'error') {
      send.disabled = false;
      send.innerHTML = '↑';
      if (statusEl) statusEl.textContent = 'Erreur';
    }
  }

  previewGenerated() {
    if (this._lastGeneratedHtml) this.showPreview(this._lastGeneratedHtml, 'nouveau');
  }

  downloadGenerated() {
    if (this._lastGeneratedHtml) {
      this._download(this._lastGeneratedHtml, 'devis-reworked-nouveau.html', 'text/html');
      this.toast('HTML téléchargé');
    }
  }

  async saveGeneratedDevis() {
    if (!this._lastGeneratedHtml) return;

    // Get next number
    let numero = '';
    try {
      const r = await wfetch(`${WORKER_URL}/next-number`);
      if (r.ok) { const d = await r.json(); numero = d.number; }
    } catch (e) {}
    if (!numero) {
      const now = new Date();
      numero = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(this.devis.length+1).padStart(3,'0')}`;
    }

    const html = this._lastGeneratedHtml;
    const meta = this._lastDevisMeta;

    // Extraire les infos : priorite formulaire > devis-meta > parsing HTML
    const ctx = this._chatContext;
    let client = ctx?.client || meta?.client || this._extractClientFromHtml(html) || 'NOUVEAU CLIENT';
    let type = ctx?.type || meta?.type || this._extractTypeFromHtml(html) || 'production';
    let objet = meta?.objet || this._extractObjetFromHtml(html) || '(voir d\u00E9tail dans le devis)';
    let montant = meta?.montant || this._extractMontantFromHtml(html) || 'Voir devis';

    // Normaliser le client en majuscules
    client = client.toUpperCase();

    // Valider le type
    const validTypes = ['event','production','creation','location','sourcing','dev'];
    if (!validTypes.includes(type)) type = 'production';

    // Create devis entry
    const newDevis = {
      id: numero,
      date: new Date().toISOString().slice(0, 10),
      client: client,
      type: type,
      objet: objet,
      montant: montant,
      statut: 'brouillon'
    };

    // Save to KV
    this.devis.push(newDevis);
    try {
      await wfetch(`${WORKER_URL}/devis`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newDevis) });
      await wfetch(`${WORKER_URL}/devis/${numero}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ html: html }) });
    } catch (e) {}

    // Add client if new
    if (!this.clients.find(c => c.name.toUpperCase() === client.toUpperCase())) {
      const newClient = { name: client, address: '', siret: '', contacts: '', source: 'local' };
      this.clients.push(newClient);
      try { await wfetch(`${WORKER_URL}/clients`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newClient) }); } catch (e) {}
    }

    this.renderDevis();
    this.renderClients();
    this._lastSavedDevisId = numero;
    await this.saveConversation();
    this.toast(`Devis ${numero} sauvegardé pour ${client}`, 'success');
  }

  addMsg(role, content) {
    const el = document.createElement('div');
    el.className = `chat-msg ${role}`;
    el.innerHTML = role === 'user' ? this.escHtml(content) : this.fmtMd(content);
    document.getElementById('chatMessages').appendChild(el);
    this.scrollChat(); return el;
  }
  fmtMd(t) {
    return t
      .replace(/```html\n([\s\S]*?)```/g, '<div style="background:var(--f5);border:1px solid var(--dd);border-radius:6px;padding:8px 12px;margin:8px 0;font-size:10px;color:var(--green);font-weight:600">Devis HTML généré (utilisez les boutons ci-dessous)</div>')
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
      .replace(/`([^`]+)`/g, '<code style="background:var(--f5);padding:2px 5px;border-radius:3px;font-size:11px">$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>');
  }
  escHtml(t) { const e = document.createElement('div'); e.textContent = t; return e.innerHTML; }
  scrollChat() { const c = document.getElementById('chatMessages'); c.scrollTop = c.scrollHeight; }
  onChatKey(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.sendMessage(); } }
  autoResize(el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; }
  useExample(el) { document.getElementById('chatInput').value = el.textContent; document.getElementById('chatInput').focus(); }

  async newChat() {
    if (this.chatHistory.length > 1) await this.saveConversation();
    this.chatHistory = [];
    this._currentConvId = null;
    this._chatContext = null;
    this._lastDevisMeta = null;
    this._lastGeneratedHtml = null;

    const container = document.getElementById('chatMessages');
    // Detacher le welcome AVANT de vider (sinon innerHTML='' le detruit)
    const welcome = document.getElementById('chatWelcome');
    if (welcome) welcome.remove();
    container.innerHTML = '';
    // Re-attacher et afficher
    if (welcome) {
      container.appendChild(welcome);
      welcome.style.display = '';
      const form = document.getElementById('chatForm');
      if (form) {
        form.style.display = '';
        form.querySelectorAll('input, textarea').forEach(el => el.value = '');
        const sel = form.querySelector('select#formPrestation');
        if (sel) sel.selectedIndex = 0;
      }
    }
    this.populateClientSuggestions();
    this.renderConversationsList();
    this.toast('Nouvelle conversation');
  }

  async saveConversation() {
    if (this.chatHistory.length < 2) return;
    const id = this._currentConvId || `conv-${Date.now()}`;
    this._currentConvId = id;
    const firstMsg = this.chatHistory.find(m => m.role === 'user')?.content || '';
    const title = firstMsg.slice(0, 60) + (firstMsg.length > 60 ? '...' : '');
    const conv = {
      id,
      title,
      date: new Date().toISOString().slice(0, 10),
      messages: this.chatHistory,
      devisId: this._lastSavedDevisId || null
    };
    // Mettre a jour le cache local pour que openConvForDevis fonctionne
    if (!this._conversations) this._conversations = [];
    const idx = this._conversations.findIndex(c => c.id === conv.id);
    if (idx >= 0) this._conversations[idx] = conv;
    else this._conversations.unshift(conv);
    this.renderConversationsList();
    try { await wfetch(`${WORKER_URL}/conversations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(conv) }); } catch (e) {}
  }

  async loadConversations() {
    try {
      const r = await wfetch(`${WORKER_URL}/conversations`);
      if (r.ok) this._conversations = await r.json();
    } catch (e) {}
    this.renderConversationsList();
  }

  renderConversationsList() {
    const sidebar = document.getElementById('chatHistory');
    if (!sidebar) return;
    const convs = this._conversations || [];
    const activeId = this._currentConvId;
    sidebar.innerHTML = convs.length ? convs.map(c => `
      <div class="conv-item ${c.id === activeId ? 'conv-active' : ''}" onclick="app.loadConversation('${c.id}')">
        <div class="conv-item-title">${this.escHtml(c.title)}</div>
        <div class="conv-item-meta">${this.fmtDate(c.date)}${c.devisId ? ` · <span class="conv-devis-tag">${c.devisId}</span>` : ''}</div>
      </div>
    `).join('') : '<div style="font-size:11px;color:var(--aa);padding:10px">Aucune conversation</div>';
  }

  async loadConversation(id) {
    const conv = (this._conversations || []).find(c => c.id === id);
    if (!conv) return;
    this.chatHistory = conv.messages || [];
    this._currentConvId = id;
    this._lastGeneratedHtml = null;
    this._lastDevisMeta = null;

    const container = document.getElementById('chatMessages');
    const welcome = document.getElementById('chatWelcome');
    // Detacher welcome, vider, cacher welcome et re-attacher
    if (welcome) welcome.remove();
    container.innerHTML = '';
    if (welcome) { welcome.style.display = 'none'; container.appendChild(welcome); }

    // Trouver quels messages contiennent du HTML devis
    const htmlIndexes = [];
    for (let i = 0; i < this.chatHistory.length; i++) {
      const msg = this.chatHistory[i];
      if (msg.role === 'assistant') {
        const hm = msg.content.match(/```html\n([\s\S]*?)```/) || msg.content.match(/(<!DOCTYPE html>[\s\S]*?<\/html>)/i);
        if (hm) htmlIndexes.push(i);
      }
    }
    const lastHtmlIdx = htmlIndexes.length ? htmlIndexes[htmlIndexes.length - 1] : -1;

    for (let i = 0; i < this.chatHistory.length; i++) {
      const msg = this.chatHistory[i];
      this.addMsg(msg.role, msg.content);

      // Si ce message contient un devis HTML, ajouter les boutons
      if (msg.role === 'assistant' && htmlIndexes.includes(i)) {
        const hm = msg.content.match(/```html\n([\s\S]*?)```/) || msg.content.match(/(<!DOCTYPE html>[\s\S]*?<\/html>)/i);
        if (hm) {
          let html = hm[1].trim();
          // Injection logo
          const logoUrl = 'https://cdn.shopify.com/s/files/1/0798/6259/5851/t/17/assets/logo-red-studio.svg';
          if (!html.includes(logoUrl) && !html.includes('logo-reworked.svg')) {
            const logoTag = `<img class="logo" src="${logoUrl}" alt="R/ED STUDIO" style="height:10px">`;
            if (html.includes('<body>')) html = html.replace('<body>', '<body>\n' + logoTag);
            else if (html.match(/<div[^>]*>/)) html = html.replace(/<div[^>]*>/, m => m + '\n' + logoTag);
          }
          this._lastGeneratedHtml = html;

          const isLast = (i === lastHtmlIdx);
          if (isLast) {
            const metaMatch = msg.content.match(/```devis-meta\n([\s\S]*?)```/);
            if (metaMatch) { try { this._lastDevisMeta = JSON.parse(metaMatch[1].trim()); } catch(e) {} }
          }

          const actions = document.createElement('div');
          actions.className = 'chat-msg system';
          if (isLast) {
            actions.innerHTML = `<div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:center">
              <button class="btn btn-primary btn-sm" onclick="app.previewGenerated()">Apercu</button>
              <button class="btn btn-green btn-sm" onclick="app.saveGeneratedDevis()">Sauvegarder dans le registre</button>
              <button class="btn btn-ghost btn-sm" onclick="app.downloadGenerated()">Telecharger HTML</button>
            </div>`;
          } else {
            actions.innerHTML = `<div style="font-size:10px;color:var(--88);text-align:center;font-style:italic">Version pr\u00E9c\u00E9dente (remplac\u00E9e par la suite)</div>`;
          }
          container.appendChild(actions);
        }
      }
    }

    this.renderConversationsList();
    this.scrollChat();
    this.toast('Conversation charg\u00E9e');
  }

  openConvForDevis(devisId) {
    const conv = (this._conversations || []).find(c => c.devisId === devisId);
    if (conv) {
      this.switchTab('claude');
      this.loadConversation(conv.id);
    } else {
      this.switchTab('claude');
      this.toast('Pas de conversation li\u00E9e \u00E0 ce devis');
    }
  }

  // Modifier un devis sans repartir de zero : un devis développement rouvre le questionnaire
  // structuré (garments/coefs/options), sinon rouvre sa conversation Claude si elle existe,
  // sinon charge son HTML comme base editable dans le chat Claude.
  editDevis(devisId) {
    const d = this.devis.find(x => x.id === devisId);
    if (d && d.type === 'dev' && d.snap) {
      this.openDevDevisBuilderFromSnapshot(devisId);
      this.toast(`Devis ${devisId} rouvert dans le questionnaire`, 'success');
      return;
    }
    if (d && d.type === 'confection' && d.snap) {
      this.openConfectionBuilderFromSnapshot(devisId);
      this.toast(`Devis ${devisId} rouvert dans le questionnaire`, 'success');
      return;
    }
    const conv = (this._conversations || []).find(c => c.devisId === devisId);
    if (conv) {
      this.switchTab('claude');
      this.loadConversation(conv.id);
      this.toast(`Devis ${devisId} rouvert : dites ce qu'il faut changer`, 'success');
    } else {
      this.useAsTemplate(devisId);
    }
  }

  /* ═══════════ FORMULAIRE PRE-CHAT ═══════════ */
  populateClientSuggestions() {
    const dl = document.getElementById('clientSuggestions');
    if (!dl) return;
    dl.innerHTML = this.clients.map(c => `<option value="${c.name}">`).join('');
  }

  onFormClientSelect() {
    const name = document.getElementById('formClient').value.trim().toUpperCase();
    const client = this.clients.find(c => c.name.toUpperCase() === name);
    if (client) {
      if (client.address) document.getElementById('formAddress').value = client.address;
      if (client.siret) document.getElementById('formSiret').value = client.siret;
    }
  }

  startDevisChat() {
    const client = (document.getElementById('formClient').value || '').trim();
    const clientType = document.getElementById('formClientType').value;
    const address = (document.getElementById('formAddress').value || '').trim();
    const siret = (document.getElementById('formSiret').value || '').trim();
    const prestation = document.getElementById('formPrestation').value;
    const delai = (document.getElementById('formDelai').value || '').trim();
    const description = (document.getElementById('formDescription').value || '').trim();

    if (!client) { this.toast('Renseignez le nom du client', 'error'); return; }
    if (!description) { this.toast('D\u00E9crivez la prestation souhait\u00E9e', 'error'); return; }

    // Stocker le contexte pour saveGeneratedDevis
    this._chatContext = {
      client: client.toUpperCase(),
      type: prestation || 'production',
      clientType,
      address,
      siret
    };

    // Composer le message structure
    let msg = `Cree un devis pour :\n`;
    msg += `- Client : ${client.toUpperCase()}\n`;
    if (address) msg += `- Adresse : ${address}\n`;
    msg += `- Type client : ${clientType === 'professionnel' ? 'Professionnel' : 'Particulier'}\n`;
    if (siret) msg += `- SIRET : ${siret}\n`;
    if (prestation) msg += `- Prestation : ${this.typeLabel(prestation)}\n`;
    if (delai) msg += `- Delais : ${delai}\n`;
    msg += `- Demande : ${description}`;

    // Masquer le formulaire
    const form = document.getElementById('chatForm');
    if (form) form.style.display = 'none';
    const welcome = document.getElementById('chatWelcome');
    if (welcome) welcome.style.display = 'none';

    // Envoyer le message
    document.getElementById('chatInput').value = msg;
    this.sendMessage();
  }

  showFreeChat() {
    const form = document.getElementById('chatForm');
    if (form) form.style.display = 'none';
    const welcome = document.getElementById('chatWelcome');
    if (welcome) welcome.style.display = 'none';
    this._chatContext = null;
    document.getElementById('chatInput').focus();
  }

  showBriefInline() {
    // Retirer un brief inline existant
    const old = document.getElementById('inlineBrief');
    if (old) { old.remove(); return; }

    const ctx = this._chatContext || {};
    const container = document.getElementById('chatMessages');
    const el = document.createElement('div');
    el.id = 'inlineBrief';
    el.className = 'chat-msg system inline-brief';
    el.innerHTML = `
      <div class="chat-form" style="max-width:100%;padding:16px 20px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px">Brief devis</span>
          <span style="font-size:18px;cursor:pointer;color:var(--88)" onclick="document.getElementById('inlineBrief').remove()">x</span>
        </div>
        <div class="chat-form-row">
          <div class="chat-form-field">
            <label>Client</label>
            <input type="text" id="ib_client" list="clientSuggestions" value="${this.escHtml(ctx.client || '')}" placeholder="NOM CLIENT">
          </div>
          <div class="chat-form-field chat-form-field-sm">
            <label>Type client</label>
            <select id="ib_clientType">
              <option value="professionnel" ${ctx.clientType !== 'particulier' ? 'selected' : ''}>Pro</option>
              <option value="particulier" ${ctx.clientType === 'particulier' ? 'selected' : ''}>Particulier</option>
            </select>
          </div>
        </div>
        <div class="chat-form-row">
          <div class="chat-form-field">
            <label>Adresse</label>
            <input type="text" id="ib_address" value="${this.escHtml(ctx.address || '')}" placeholder="Rue, CP, ville">
          </div>
          <div class="chat-form-field chat-form-field-sm">
            <label>SIRET</label>
            <input type="text" id="ib_siret" value="${this.escHtml(ctx.siret || '')}" placeholder="XXX XXX XXX XXXXX">
          </div>
        </div>
        <div class="chat-form-row">
          <div class="chat-form-field">
            <label>Prestation</label>
            <select id="ib_prestation">
              <option value="">Choisir...</option>
              <option value="event" ${ctx.type==='event'?'selected':''}>Evenementielle (live)</option>
              <option value="production" ${ctx.type==='production'?'selected':''}>Production atelier</option>
              <option value="creation" ${ctx.type==='creation'?'selected':''}>Creation graphique</option>
              <option value="location" ${ctx.type==='location'?'selected':''}>Location materiel</option>
              <option value="sourcing" ${ctx.type==='sourcing'?'selected':''}>Sourcing</option>
              <option value="dev" ${ctx.type==='dev'?'selected':''}>D\u00E9veloppement produit</option>
            </select>
          </div>
          <div class="chat-form-field">
            <label>Delais</label>
            <input type="text" id="ib_delai" placeholder="Ex: Avant le 15 juin">
          </div>
        </div>
        <div class="chat-form-field chat-form-field-full" style="margin-bottom:12px">
          <label>Modifications / nouvelles demandes</label>
          <textarea id="ib_description" rows="2" placeholder="Ce que vous voulez changer ou ajouter..."></textarea>
        </div>
        <button class="btn btn-primary btn-sm" onclick="app.submitBriefInline()">Envoyer le brief</button>
      </div>
    `;
    container.appendChild(el);
    this.scrollChat();
  }

  submitBriefInline() {
    const client = (document.getElementById('ib_client').value || '').trim();
    const clientType = document.getElementById('ib_clientType').value;
    const address = (document.getElementById('ib_address').value || '').trim();
    const siret = (document.getElementById('ib_siret').value || '').trim();
    const prestation = document.getElementById('ib_prestation').value;
    const delai = (document.getElementById('ib_delai').value || '').trim();
    const description = (document.getElementById('ib_description').value || '').trim();

    if (!description && !client) { this.toast('Renseignez au moins le client ou une demande', 'error'); return; }

    // Mettre a jour le contexte
    if (client) {
      this._chatContext = this._chatContext || {};
      this._chatContext.client = client.toUpperCase();
      this._chatContext.type = prestation || this._chatContext.type || 'production';
      this._chatContext.clientType = clientType;
      if (address) this._chatContext.address = address;
      if (siret) this._chatContext.siret = siret;
    }

    // Composer le message
    let msg = '';
    if (this.chatHistory.length === 0) {
      msg = `Cree un devis pour :\n`;
    } else {
      msg = `Rectification du devis :\n`;
    }
    if (client) msg += `- Client : ${client.toUpperCase()}\n`;
    if (address) msg += `- Adresse : ${address}\n`;
    msg += `- Type client : ${clientType === 'professionnel' ? 'Professionnel' : 'Particulier'}\n`;
    if (siret) msg += `- SIRET : ${siret}\n`;
    if (prestation) msg += `- Prestation : ${this.typeLabel(prestation)}\n`;
    if (delai) msg += `- Delais : ${delai}\n`;
    if (description) msg += `- Demande : ${description}`;

    // Retirer le brief inline
    const el = document.getElementById('inlineBrief');
    if (el) el.remove();

    // Envoyer
    document.getElementById('chatInput').value = msg;
    this.sendMessage();
  }

  /* ═══════════ PREVIEW ═══════════ */
  _injectPrintCSS(html) {
    const rules = 'div[class]{page-break-inside:avoid}table{page-break-inside:avoid}tr{page-break-inside:avoid}thead{display:table-header-group}h1,h2,h3,h4{page-break-after:avoid}ul,ol{page-break-inside:avoid}p{orphans:3;widows:3}body{orphans:3;widows:3}';
    if (html.includes('</style>')) return html.replace('</style>', rules + '</style>');
    if (html.includes('</head>')) return html.replace('</head>', '<style>' + rules + '</style></head>');
    return '<style>' + rules + '</style>' + html;
  }

  showPreview(html, id) {
    const safeHtml = this._injectPrintCSS(html);
    document.getElementById('previewIframe').srcdoc = safeHtml;
    document.getElementById('previewTitle').textContent = `Aper\u00E7u : ${id}`;
    document.getElementById('previewOverlay').classList.add('active');
    this._html = safeHtml; this._pid = id;
  }
  closePreview() { document.getElementById('previewOverlay').classList.remove('active'); }
  _pidPrefix() { return String(this._pid || '').startsWith('FAC') ? 'facture' : 'devis'; }
  downloadPreview() {
    this._download(this._html || '', `${this._pidPrefix()}-${this._pid || 'reworked'}.html`, 'text/html');
    this.toast('HTML t\u00E9l\u00E9charg\u00E9');
  }
  async downloadPdf() {
    if (!this._html || !this._pid) return;
    this.toast('G\u00E9n\u00E9ration du PDF...', '');
    try {
      const r = await wfetch(`${WORKER_URL}/pdf`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: this._html })
      });
      if (!r.ok) { this.toast('Erreur g\u00E9n\u00E9ration PDF', 'error'); return; }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${this._pidPrefix()}-${this._pid}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      this.toast('PDF t\u00E9l\u00E9charg\u00E9', 'success');
    } catch (e) {
      this.toast('Erreur r\u00E9seau', 'error');
    }
  }

  printPreview() {
    const iframe = document.getElementById('previewIframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  }
  emailPreview() {
    if (this._pid) this.showEmailModal(this._pid);
  }

  /* ═══════════ EMAIL ═══════════ */
  _findEmailItem(id) {
    let d = this.devis.find(x => x.id === id);
    if (d) { this._emailKind = 'devis'; return d; }
    d = this.factures.find(x => x.id === id);
    if (d) { this._emailKind = 'facture'; return d; }
    return null;
  }

  getEmailTemplate(stage, devis) {
    const d = devis || {};
    const client = d.client || 'Client';
    const numero = d.id || 'XXXX-XX-XXX';
    const montant = d.montant || '';
    const objet = d.objet || '';

    if (this._emailKind === 'facture') {
      const facTemplates = {
        envoi: {
          subject: `Facture N° ${numero} - REWORKED`,
          body: `Bonjour,\n\nVeuillez trouver ci-joint la facture N° ${numero}${objet ? ` concernant : ${objet}` : ''}${montant ? `\n\nMontant : ${montant}` : ''}\n\nNous vous remercions de votre règlement à réception.\n\nBien cordialement,`
        },
        relance: {
          subject: `Relance - Facture N° ${numero} - REWORKED`,
          body: `Bonjour,\n\nSauf erreur de notre part, la facture N° ${numero} reste à ce jour en attente de règlement.\n\nNous vous remercions de bien vouloir procéder au paiement dès que possible. N'hésitez pas à nous contacter en cas de question.\n\nBien cordialement,`
        },
        accepte: {
          subject: `Reçu - Facture N° ${numero} réglée - REWORKED`,
          body: `Bonjour,\n\nNous confirmons la bonne réception de votre règlement pour la facture N° ${numero}.\n\nMerci pour votre confiance.\n\nBien cordialement,`
        },
        refuse: {
          subject: `Facture N° ${numero} - REWORKED`,
          body: `Bonjour,\n\nNous revenons vers vous concernant la facture N° ${numero}.\n\nBien cordialement,`
        }
      };
      return facTemplates[stage] || facTemplates.envoi;
    }

    const templates = {
      envoi: {
        subject: `Devis N\u00B0 ${numero} - REWORKED`,
        body: `Bonjour,\n\nSuite \u00E0 notre \u00E9change, veuillez trouver ci-joint notre proposition pour :\n${objet}${montant ? `\n\nMontant : ${montant}` : ''}\n\nCe devis est valable 30 jours. N'h\u00E9sitez pas \u00E0 revenir vers nous pour toute question ou ajustement.\n\nBonne r\u00E9ception,`
      },
      relance: {
        subject: `Relance - Devis N\u00B0 ${numero} - REWORKED`,
        body: `Bonjour,\n\nJe me permets de revenir vers vous concernant le devis N\u00B0 ${numero} que nous vous avons transmis.\n\nCe devis reste valable et nous sommes disponibles pour en discuter ou l'ajuster selon vos besoins.\n\nBien cordialement,`
      },
      accepte: {
        subject: `Confirmation - Devis N\u00B0 ${numero} accept\u00E9 - REWORKED`,
        body: `Bonjour,\n\nMerci pour votre confiance ! Nous confirmons la prise en charge de votre commande.\n\nProchaines \u00E9tapes :\n- Validation des fichiers / BAT\n- Acompte de 40% \u00E0 la validation\n- Production et livraison selon les d\u00E9lais convenus\n\nN'h\u00E9sitez pas si vous avez des questions.\n\n\u00C0 tr\u00E8s bient\u00F4t,`
      },
      refuse: {
        subject: `Merci pour votre retour - REWORKED`,
        body: `Bonjour,\n\nMerci pour votre retour concernant le devis N\u00B0 ${numero}.\n\nNous restons \u00E0 votre disposition pour de futurs projets. N'h\u00E9sitez pas \u00E0 nous recontacter.\n\nCordialement,`
      }
    };
    return templates[stage] || templates.envoi;
  }

  showEmailModal(devisId) {
    const d = this._findEmailItem(devisId);
    if (!d) { this.toast('Document introuvable', 'error'); return; }

    // Chercher l'email du client
    const cl = this.clients.find(c => (c.name || '').toUpperCase() === (d.client || '').toUpperCase());
    const clientEmail = cl?.email || '';

    const tpl = this.getEmailTemplate('envoi', d);

    const modal = document.getElementById('emailOverlay');
    document.getElementById('emailDevisId').textContent = d.id;
    document.getElementById('emailTo').value = clientEmail;
    document.getElementById('emailStage').value = 'envoi';
    document.getElementById('emailSubject').value = tpl.subject;
    document.getElementById('emailBody').value = tpl.body;
    // Reset PDF
    this._emailPdfBase64 = null;
    this._emailPdfName = null;
    const pdfInput = document.getElementById('emailPdfFile');
    if (pdfInput) pdfInput.value = '';
    const pdfStatus = document.getElementById('emailPdfStatus');
    if (pdfStatus) pdfStatus.textContent = '';

    modal.classList.add('active');
    this.loadSignature();
  }

  onEmailStageChange() {
    const stage = document.getElementById('emailStage').value;
    const devisId = document.getElementById('emailDevisId').textContent;
    const d = this._findEmailItem(devisId);
    const tpl = this.getEmailTemplate(stage, d);
    document.getElementById('emailSubject').value = tpl.subject;
    document.getElementById('emailBody').value = tpl.body;
  }

  closeEmailModal() {
    document.getElementById('emailOverlay').classList.remove('active');
  }

  openInGmail() {
    const to = document.getElementById('emailTo').value.trim();
    const subject = document.getElementById('emailSubject').value;
    const body = document.getElementById('emailBody').value;
    const stage = document.getElementById('emailStage').value;
    const devisId = document.getElementById('emailDevisId').textContent;
    const url = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank');

    // Logger l'email
    this.logEmail({ devisId, to, subject, stage, date: new Date().toISOString().slice(0, 10) });
    this.closeEmailModal();
    this.toast('Gmail ouvert dans un nouvel onglet', 'success');
  }

  onPdfFileSelected(input) {
    const status = document.getElementById('emailPdfStatus');
    if (input.files.length) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this._emailPdfBase64 = reader.result.split(',')[1]; // retirer le prefix data:...
        this._emailPdfName = file.name;
        if (status) status.textContent = `${file.name} (${(file.size / 1024).toFixed(0)} Ko)`;
      };
      reader.readAsDataURL(file);
    } else {
      this._emailPdfBase64 = null;
      this._emailPdfName = null;
      if (status) status.textContent = '';
    }
  }

  _getEmailPayload() {
    const to = document.getElementById('emailTo').value.trim();
    const subject = document.getElementById('emailSubject').value;
    let body = document.getElementById('emailBody').value;
    const devisId = document.getElementById('emailDevisId').textContent;
    const stage = document.getElementById('emailStage').value;
    const d = this._findEmailItem(devisId);
    const clientName = d?.client || '';
    const kind = this._emailKind || 'devis';
    // La signature est ajout\u00E9e dans le MIME en HTML
    // PDF manuel optionnel (override)
    const pdfBase64 = this._emailPdfBase64 || null;
    const pdfFilename = this._emailPdfName || `${kind === 'facture' ? 'facture' : 'devis'}-${devisId}.pdf`;
    return { to, subject, body, devisId, stage, clientName, pdfBase64, pdfFilename };
  }

  // G\u00E9n\u00E9rer le PDF via Browserless et le r\u00E9cup\u00E9rer en ArrayBuffer
  async _generatePdf(devisId) {
    // R\u00E9cup\u00E9rer le HTML du devis ou de la facture selon le contexte
    try {
      const base = (this._emailKind === 'facture') ? '/factures/' : '/devis/';
      const hr = await wfetch(`${WORKER_URL}${base}${encodeURIComponent(devisId)}`);
      if (!hr.ok) return null;
      const hd = await hr.json();
      if (!hd.html) return null;

      // Appeler Browserless via le worker (proxy pour ne pas exposer le token)
      const pr = await wfetch(`${WORKER_URL}/pdf`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: hd.html })
      });
      if (!pr.ok) return null;
      return await pr.arrayBuffer();
    } catch (e) { return null; }
  }

  _buildRawMime(to, subject, body, pdfArrayBuffer, pdfName) {
    const boundary = 'b_' + Date.now();
    const subjectB64 = btoa(unescape(encodeURIComponent(subject)));

    // Construire le body HTML avec signature Gmail
    const sig = localStorage.getItem('rw_email_signature') || '';
    const bodyHtml = '<div style="font-family:Arial,sans-serif;font-size:14px;color:#222">'
      + body.replace(/\n/g, '<br>') + '</div>'
      + (sig ? '<br>' + sig : '');
    const htmlB64 = btoa(unescape(encodeURIComponent(bodyHtml)));

    // PDF en base64
    let pdfB64 = '';
    if (pdfArrayBuffer) {
      const bytes = new Uint8Array(pdfArrayBuffer);
      let bin = '';
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      pdfB64 = btoa(bin);
    }

    let mime = 'To: ' + to + '\r\n';
    mime += 'Subject: =?UTF-8?B?' + subjectB64 + '?=\r\n';
    mime += 'Content-Type: multipart/mixed; boundary="' + boundary + '"\r\n';
    mime += 'MIME-Version: 1.0\r\n\r\n';
    mime += '--' + boundary + '\r\n';
    mime += 'Content-Type: text/html; charset=UTF-8\r\n';
    mime += 'Content-Transfer-Encoding: base64\r\n\r\n';
    mime += htmlB64 + '\r\n';

    if (pdfB64) {
      const wrapped = pdfB64.match(/.{1,76}/g).join('\r\n');
      mime += '\r\n--' + boundary + '\r\n';
      mime += 'Content-Type: application/pdf; name="' + pdfName + '"\r\n';
      mime += 'Content-Disposition: attachment; filename="' + pdfName + '"\r\n';
      mime += 'Content-Transfer-Encoding: base64\r\n\r\n';
      mime += wrapped + '\r\n';
    }

    mime += '--' + boundary + '--';
    return btoa(mime).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  async saveDraftGmail() {
    const p = this._getEmailPayload();
    if (!p.to) { this.toast('Renseignez le destinataire', 'error'); return; }

    this.toast('G\u00E9n\u00E9ration du PDF...', '');

    // PDF : soit upload\u00E9 manuellement, soit g\u00E9n\u00E9r\u00E9 via Browserless
    let pdfBuf = null;
    if (p.pdfBase64) {
      // Convertir le base64 manuel en ArrayBuffer
      const bin = atob(p.pdfBase64);
      pdfBuf = new ArrayBuffer(bin.length);
      const view = new Uint8Array(pdfBuf);
      for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
    } else {
      pdfBuf = await this._generatePdf(p.devisId);
    }

    this.toast('Cr\u00E9ation du brouillon...', '');
    const raw = this._buildRawMime(p.to, p.subject, p.body, pdfBuf, p.pdfFilename);

    try {
      const r = await wfetch(`${WORKER_URL}/gmail/draft-raw`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw })
      });
      const data = await r.json();
      if (data.ok) {
        this.logEmail({ devisId: p.devisId, to: p.to, subject: p.subject, body: p.body, stage: p.stage, date: new Date().toISOString().slice(0, 10), method: 'brouillon' });
        this.closeEmailModal();
        this.toast('Brouillon cr\u00E9\u00E9 dans Gmail avec PDF', 'success');
      } else {
        this.toast(data.error || 'Erreur Gmail', 'error');
      }
    } catch (e) {
      this.toast('Erreur r\u00E9seau', 'error');
    }
  }

  async sendDirectGmail() {
    const p = this._getEmailPayload();
    if (!p.to) { this.toast('Renseignez le destinataire', 'error'); return; }
    if (!confirm(`Envoyer directement \u00E0 ${p.to} ?`)) return;

    this.toast('G\u00E9n\u00E9ration du PDF...', '');

    let pdfBuf = null;
    if (p.pdfBase64) {
      const bin = atob(p.pdfBase64);
      pdfBuf = new ArrayBuffer(bin.length);
      const view = new Uint8Array(pdfBuf);
      for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
    } else {
      pdfBuf = await this._generatePdf(p.devisId);
    }

    this.toast('Envoi en cours...', '');
    const raw = this._buildRawMime(p.to, p.subject, p.body, pdfBuf, p.pdfFilename);

    try {
      const r = await wfetch(`${WORKER_URL}/gmail/send-raw`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw })
      });
      const data = await r.json();
      if (data.ok) {
        this.logEmail({ devisId: p.devisId, to: p.to, subject: p.subject, body: p.body, stage: p.stage, date: new Date().toISOString().slice(0, 10), method: 'envoi direct' });
        this.closeEmailModal();
        this.toast(`Email envoy\u00E9 \u00E0 ${p.to} avec PDF`, 'success');
      } else {
        this.toast(data.error || 'Erreur Gmail', 'error');
      }
    } catch (e) {
      this.toast('Erreur r\u00E9seau', 'error');
    }
  }

  copyEmailBody() {
    const body = document.getElementById('emailBody').value;
    navigator.clipboard.writeText(body).then(() => this.toast('Corps copi\u00E9 dans le presse-papiers', 'success'));
  }

  /* ═══════════ HISTORIQUE EMAILS ═══════════ */
  async logEmail(entry) {
    if (!this._emailLog) this._emailLog = [];
    this._emailLog.unshift(entry);
    this.renderEmailHistory();
    try { await wfetch(`${WORKER_URL}/emails`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entry) }); } catch (e) {}
  }

  async loadEmails() {
    try {
      const r = await wfetch(`${WORKER_URL}/emails`);
      if (r.ok) this._emailLog = await r.json();
    } catch (e) {}
  }

  renderEmailHistory() {
    const el = document.getElementById('emailHistoryBody');
    if (!el) return;
    const log = this._emailLog || [];
    const badge = document.getElementById('emailBadge');
    if (badge) badge.textContent = log.length;

    el.innerHTML = log.length ? log.map((e, i) => {
      const isDraft = e.method === 'brouillon local';
      const methodLabel = { 'envoi direct': 'Envoy\u00E9', 'brouillon': 'Brouillon Gmail', 'brouillon local': 'Brouillon' }[e.method] || e.method || 'Envoy\u00E9';
      return `<tr onclick="this.nextElementSibling.style.display=this.nextElementSibling.style.display==='none'?'':'none'" style="cursor:pointer">
        <td style="font-family:monospace;font-size:10px;font-weight:600">${e.devisId || ''}</td>
        <td style="font-size:11px">${this.fmtDate(e.date)}</td>
        <td style="font-size:11px">${this.escHtml(e.to || '')}</td>
        <td><span class="email-stage-tag email-stage-${e.stage}">${this.emailStageLabel(e.stage)}</span></td>
        <td style="font-size:10px;color:var(--66)">${this.escHtml(e.subject || '')}</td>
        <td style="font-size:9px;color:var(--88)">${methodLabel}</td>
        <td style="white-space:nowrap">
          ${e.devisId ? `<button class="btn btn-ghost btn-sm" onclick="event.stopPropagation();app.viewPdfForDevis('${e.devisId}')" style="padding:2px 6px;font-size:8px">PDF</button>` : ''}
          ${isDraft ? `<button class="btn btn-green btn-sm" onclick="event.stopPropagation();app.sendDraftEmail(${i})" style="padding:2px 6px;font-size:8px">Envoyer</button>` : ''}
        </td>
      </tr>
      <tr style="display:none"><td colspan="7" style="padding:10px 12px;background:var(--f9);font-size:10px;line-height:1.6;white-space:pre-wrap;color:var(--55)">${this.escHtml(e.body || '(pas de contenu)')}</td></tr>`;
    }).join('') : '<tr><td colspan="7" style="text-align:center;color:var(--aa);font-size:11px;padding:20px">Aucun email</td></tr>';
  }

  async viewPdfForDevis(devisId) {
    this.toast('G\u00E9n\u00E9ration du PDF...', '');
    const buf = await this._generatePdf(devisId);
    if (buf) {
      const blob = new Blob([buf], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
      this.toast('PDF ouvert', 'success');
    } else {
      this.toast('Impossible de g\u00E9n\u00E9rer le PDF', 'error');
    }
  }

  /* ═══════════ BROUILLONS LOCAUX ═══════════ */
  async saveDraftLocal() {
    const p = this._getEmailPayload();
    if (!p.to) { this.toast('Renseignez le destinataire', 'error'); return; }

    const entry = {
      devisId: p.devisId, to: p.to, subject: p.subject, body: p.body,
      stage: p.stage, date: new Date().toISOString().slice(0, 10),
      method: 'brouillon local', clientName: p.clientName
    };
    await this.logEmail(entry);
    this.closeEmailModal();
    this.toast('Brouillon sauvegard\u00E9', 'success');
  }

  async sendDraftEmail(index) {
    const log = this._emailLog || [];
    const draft = log[index];
    if (!draft || draft.method !== 'brouillon local') return;
    if (!confirm(`Envoyer le brouillon \u00E0 ${draft.to} ?`)) return;

    this.toast('G\u00E9n\u00E9ration du PDF + envoi...', '');
    const pdfBuf = await this._generatePdf(draft.devisId);
    const sig = localStorage.getItem('rw_email_signature') || '';
    const bodyWithSig = draft.body + (sig ? '\n\n' + sig : '');
    const raw = this._buildRawMime(draft.to, draft.subject, bodyWithSig, pdfBuf, `devis-${draft.devisId}.pdf`);

    try {
      const r = await wfetch(`${WORKER_URL}/gmail/send-raw`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw })
      });
      const data = await r.json();
      if (data.ok) {
        // Passer le brouillon en "envoi direct"
        draft.method = 'envoi direct';
        draft.date = new Date().toISOString().slice(0, 10);
        this.renderEmailHistory();
        try { await wfetch(`${WORKER_URL}/emails`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft) }); } catch (e) {}
        this.toast(`Email envoy\u00E9 \u00E0 ${draft.to}`, 'success');
      } else {
        this.toast(data.error || 'Erreur', 'error');
      }
    } catch (e) {
      this.toast('Erreur r\u00E9seau', 'error');
    }
  }

  /* ═══════════ SIGNATURE ═══════════ */
  async loadSignature() {
    const el = document.getElementById('emailSignaturePreview');
    // D'abord essayer le cache
    let sig = localStorage.getItem('rw_email_signature') || '';
    if (sig && el) { el.innerHTML = sig; return; }
    // Sinon charger depuis Gmail
    try {
      const r = await wfetch(`${WORKER_URL}/gmail/signature`);
      if (r.ok) {
        const data = await r.json();
        if (data.signatures?.length) {
          // Prendre la signature par d\u00E9faut ou la premi\u00E8re
          const s = data.signatures.find(x => x.isDefault) || data.signatures[0];
          sig = s.signature || '';
          localStorage.setItem('rw_email_signature', sig);
          localStorage.setItem('rw_email_signature_name', s.name || '');
        }
      }
    } catch (e) {}
    if (el) el.innerHTML = sig || '<span style="color:var(--aa)">Aucune signature trouv\u00E9e</span>';
  }

  async refreshSignature() {
    localStorage.removeItem('rw_email_signature');
    this.toast('Chargement de la signature Gmail...', '');
    await this.loadSignature();
    this.toast('Signature mise \u00E0 jour', 'success');
  }

  emailStageLabel(s) {
    return { envoi: 'Envoi', relance: 'Relance', accepte: 'Confirmation', refuse: 'Refus' }[s] || s || 'Envoi';
  }

  /* ═══════════ CLIENTS ═══════════ */
  renderClients() {
    const c = document.getElementById('clientsList'); if (!c) return;
    c.innerHTML = this.clients.map((cl, i) => {
      const clientDevis = this.devis.filter(d => d.client === cl.name);
      const cnt = clientDevis.length;
      const src = cl.source === 'shopify';
      const info = [cl.address, cl.email, cl.phone].filter(x => x && x !== 'null' && x.trim()).join(' — ') || 'Aucune info';
      const details = [cl.siret ? `SIRET: ${cl.siret}` : '', cl.tva ? `TVA: ${cl.tva}` : '', cl.contacts || ''].filter(Boolean).join(' — ');

      const devisRows = clientDevis.length ? clientDevis.map(d => `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid var(--f5)">
          <div>
            <span style="font-family:monospace;font-size:10px;font-weight:600;margin-right:8px">${d.id}</span>
            <span class="type-tag type-${d.type}">${this.typeLabel(d.type)}</span>
            <span style="font-size:11px;margin-left:8px">${d.objet}</span>
          </div>
          <div style="display:flex;align-items:center;gap:8px">
            <span style="font-weight:600;font-size:11px">${d.montant}</span>
            <span class="status status-${d.statut}" style="font-size:8px">${this.statusLabel(d.statut)}</span>
            <button class="btn btn-ghost btn-sm" onclick="event.stopPropagation();app.viewHtml('${d.id}')" style="padding:2px 6px;font-size:9px">Voir</button>
          </div>
        </div>
      `).join('') : '<div style="font-size:11px;color:var(--88);padding:8px 0">Aucun devis pour ce client</div>';

      const esc = (s) => (s || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      return `<div class="client-card" onclick="this.classList.toggle('expanded')" style="cursor:pointer">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <div class="client-name">${cl.name}</div>
          <div style="display:flex;align-items:center;gap:8px">
            <span class="source-tag ${src ? 'source-shopify' : 'source-local'}">${src ? 'Shopify' : 'Local'}</span>
            <span style="font-size:10px;color:var(--aa);transition:transform .2s" class="accordion-arrow">\u25BC</span>
          </div>
        </div>
        <div class="client-info">${info}</div>
        <div class="client-stats">
          <span>${cnt} devis</span>
          ${details ? `<span>${details}</span>` : ''}
        </div>
        <div class="client-accordion">
          <!-- \u00C9dition -->
          <div style="border-top:1px solid var(--ee);margin-top:10px;padding-top:10px">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
              <div style="font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:var(--88)">Informations</div>
              <div style="display:flex;gap:6px">
                <button class="btn btn-ghost btn-sm" onclick="event.stopPropagation();app.toggleEditClient(${i})" style="padding:2px 8px;font-size:9px">Modifier</button>
                <button class="btn btn-ghost btn-sm" onclick="event.stopPropagation();app.exportClientData('${this.escHtml(cl.name)}')" style="padding:2px 8px;font-size:9px">Exporter</button>
              </div>
            </div>
            <div id="clientView-${i}" class="client-info-grid">
              <div><span class="detail-label">Adresse</span><span class="detail-value">${cl.address || 'Non renseign\u00E9e'}</span></div>
              <div><span class="detail-label">Email</span><span class="detail-value">${cl.email || 'Non renseign\u00E9'}</span></div>
              <div><span class="detail-label">T\u00E9l\u00E9phone</span><span class="detail-value">${cl.phone || 'Non renseign\u00E9'}</span></div>
              <div><span class="detail-label">SIRET</span><span class="detail-value">${cl.siret || 'Non renseign\u00E9'}</span></div>
              <div><span class="detail-label">TVA</span><span class="detail-value">${cl.tva || 'N/A'}</span></div>
              <div><span class="detail-label">Contacts</span><span class="detail-value">${cl.contacts || 'Non renseign\u00E9'}</span></div>
            </div>
            <div id="clientEdit-${i}" style="display:none" onclick="event.stopPropagation()">
              <div class="client-edit-grid">
                <div class="chat-form-field"><label>Nom</label><input type="text" id="cedit_name_${i}" value="${esc(cl.name)}"></div>
                <div class="chat-form-field"><label>Adresse</label><input type="text" id="cedit_addr_${i}" value="${esc(cl.address)}"></div>
                <div class="chat-form-field"><label>Email</label><input type="email" id="cedit_email_${i}" value="${esc(cl.email)}"></div>
                <div class="chat-form-field"><label>T\u00E9l\u00E9phone</label><input type="text" id="cedit_phone_${i}" value="${esc(cl.phone)}"></div>
                <div class="chat-form-field"><label>SIRET</label><input type="text" id="cedit_siret_${i}" value="${esc(cl.siret)}"></div>
                <div class="chat-form-field"><label>TVA</label><input type="text" id="cedit_tva_${i}" value="${esc(cl.tva)}"></div>
                <div class="chat-form-field" style="grid-column:1/-1"><label>Contacts</label><input type="text" id="cedit_contacts_${i}" value="${esc(cl.contacts)}" placeholder="Noms des contacts"></div>
              </div>
              <div style="display:flex;gap:6px;margin-top:8px">
                <button class="btn btn-primary btn-sm" onclick="event.stopPropagation();app.saveEditClient(${i})">Sauvegarder</button>
                <button class="btn btn-ghost btn-sm" onclick="event.stopPropagation();app.toggleEditClient(${i})">Annuler</button>
              </div>
            </div>
          </div>
          <!-- Historique devis -->
          <div style="border-top:1px solid var(--ee);margin-top:10px;padding-top:10px">
            <div style="font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:var(--88);margin-bottom:6px">Historique devis</div>
            ${devisRows}
          </div>
        </div>
      </div>`;
    }).join('');
  }

  filterClients(s) { document.querySelectorAll('.client-card').forEach(c => { c.style.display = c.textContent.toLowerCase().includes(s.toLowerCase()) ? '' : 'none'; }); }

  addClient() {
    const newClient = { name: '', address: '', email: '', phone: '', siret: '', tva: '', contacts: '', source: 'local' };
    this.clients.unshift(newClient);
    this.renderClients();
    const card = document.querySelector('.client-card');
    if (card) {
      card.classList.add('expanded');
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.toggleEditClient(0);
      setTimeout(() => { const nameInput = document.getElementById('cedit_name_0'); if (nameInput) nameInput.focus(); }, 100);
    }
  }

  toggleEditClient(i) {
    const view = document.getElementById(`clientView-${i}`);
    const edit = document.getElementById(`clientEdit-${i}`);
    if (!view || !edit) return;
    const showing = edit.style.display !== 'none';
    view.style.display = showing ? '' : 'none';
    edit.style.display = showing ? 'none' : '';
  }

  async saveEditClient(i) {
    const cl = this.clients[i];
    if (!cl) return;

    const oldName = cl.name;
    cl.name = (document.getElementById(`cedit_name_${i}`)?.value || cl.name).trim();
    cl.address = (document.getElementById(`cedit_addr_${i}`)?.value || '').trim();
    cl.email = (document.getElementById(`cedit_email_${i}`)?.value || '').trim();
    cl.phone = (document.getElementById(`cedit_phone_${i}`)?.value || '').trim();
    cl.siret = (document.getElementById(`cedit_siret_${i}`)?.value || '').trim();
    cl.tva = (document.getElementById(`cedit_tva_${i}`)?.value || '').trim();
    cl.contacts = (document.getElementById(`cedit_contacts_${i}`)?.value || '').trim();

    // Si le nom a chang\u00E9, mettre \u00E0 jour les devis li\u00E9s
    if (oldName !== cl.name) {
      this.devis.forEach(d => { if (d.client === oldName) d.client = cl.name; });
    }

    // Sauvegarder dans le KV
    try {
      await wfetch(`${WORKER_URL}/clients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cl)
      });
    } catch (e) {}

    this.renderClients();
    this.renderDevis();
    this.toast(`Client ${cl.name} mis \u00E0 jour`, 'success');
  }

  /* ═══════════ EXPORT FICHE CLIENT ═══════════ */
  exportClientData(clientName) {
    const cl = this.clients.find(c => c.name === clientName);
    if (!cl) { this.toast('Client introuvable', 'error'); return; }

    const clientDevis = this.devis.filter(d => d.client.toUpperCase() === cl.name.toUpperCase()).sort((a, b) => b.date.localeCompare(a.date));
    const clientEmails = (this._emailLog || []).filter(e => {
      const d = this.devis.find(x => x.id === e.devisId);
      return d && d.client.toUpperCase() === cl.name.toUpperCase();
    });

    // Stats
    const nbDevis = clientDevis.length;
    const nbAcceptes = clientDevis.filter(d => d.statut === 'accepte').length;
    const caTotal = clientDevis.reduce((s, d) => s + this.getDevisAmount(d), 0);
    const caAccepte = clientDevis.filter(d => d.statut === 'accepte').reduce((s, d) => s + this.getDevisAmount(d), 0);
    const taux = nbDevis ? Math.round((nbAcceptes / nbDevis) * 100) : 0;
    const premierDevis = clientDevis.length ? clientDevis[clientDevis.length - 1].date : '';
    const dernierDevis = clientDevis.length ? clientDevis[0].date : '';

    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Fiche client \u2014 ${cl.name}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',-apple-system,sans-serif;font-size:10px;line-height:1.5;color:#111;padding:30px 35px;max-width:210mm;margin:0 auto}
h1{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:2px}
.sub{font-size:9px;color:#666;margin-bottom:20px}
.section{margin-bottom:18px}
.section-title{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;padding-bottom:6px;border-bottom:1px solid #ddd;margin-bottom:10px}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:18px}
.info-box{background:#f7f7f7;padding:10px 14px;border-radius:4px}
.info-label{font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:3px}
.info-value{font-size:11px;font-weight:600}
.stats-row{display:flex;gap:12px;margin-bottom:18px}
.stat-box{flex:1;background:#f7f7f7;padding:12px;text-align:center;border-radius:4px}
.stat-num{font-size:20px;font-weight:700}
.stat-label{font-size:7px;text-transform:uppercase;letter-spacing:0.5px;color:#888;margin-top:2px}
table{width:100%;border-collapse:collapse}
th{background:#111;color:#fff;font-size:7px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;padding:6px 8px;text-align:left}
th:last-child{text-align:right}
td{padding:6px 8px;border-bottom:1px solid #eee;font-size:9px}
td:last-child{text-align:right;font-weight:500}
.status{display:inline-block;font-size:7px;font-weight:600;padding:2px 6px;border-radius:3px;text-transform:uppercase}
.s-envoye{background:#f0f7ff;color:#0066cc}
.s-accepte{background:#e8f5e9;color:#2e7d32}
.s-refuse{background:#fff5f5;color:#c62828}
.s-brouillon{background:#f5f5f5;color:#888}
.footer{margin-top:25px;padding-top:10px;border-top:1px solid #ddd;font-size:7px;color:#888;text-align:center}
</style></head><body>

<h1>${cl.name}</h1>
<div class="sub">Fiche client export\u00E9e le ${new Date().toLocaleDateString('fr-FR')} \u2014 REWORKED</div>

<div class="info-grid">
  <div class="info-box"><div class="info-label">Adresse</div><div class="info-value">${cl.address || 'Non renseign\u00E9e'}</div></div>
  <div class="info-box"><div class="info-label">SIRET</div><div class="info-value">${cl.siret || 'Non renseign\u00E9'}</div></div>
  <div class="info-box"><div class="info-label">Email</div><div class="info-value">${cl.email || 'Non renseign\u00E9'}</div></div>
  <div class="info-box"><div class="info-label">T\u00E9l\u00E9phone</div><div class="info-value">${cl.phone || 'Non renseign\u00E9'}</div></div>
  ${cl.contacts ? `<div class="info-box"><div class="info-label">Contacts</div><div class="info-value">${cl.contacts}</div></div>` : ''}
  <div class="info-box"><div class="info-label">Source</div><div class="info-value">${cl.source === 'shopify' ? 'Shopify' : 'Local'}</div></div>
</div>

<div class="stats-row">
  <div class="stat-box"><div class="stat-num">${nbDevis}</div><div class="stat-label">Devis</div></div>
  <div class="stat-box"><div class="stat-num">${nbAcceptes}</div><div class="stat-label">Accept\u00E9s</div></div>
  <div class="stat-box"><div class="stat-num">${taux}%</div><div class="stat-label">Taux</div></div>
  <div class="stat-box"><div class="stat-num">${this.fmtEuro(caTotal)}</div><div class="stat-label">CA total</div></div>
  <div class="stat-box"><div class="stat-num">${this.fmtEuro(caAccepte)}</div><div class="stat-label">CA accept\u00E9</div></div>
</div>

${premierDevis ? `<div style="font-size:8px;color:#888;margin-bottom:18px">Premi\u00E8re collaboration : ${this.fmtDate(premierDevis)} \u2014 Dernier devis : ${this.fmtDate(dernierDevis)}</div>` : ''}

<div class="section">
  <h3 class="section-title">Historique des devis</h3>
  <table>
    <thead><tr><th>N\u00B0</th><th>Date</th><th>Type</th><th>Objet</th><th>Statut</th><th>Montant</th></tr></thead>
    <tbody>
      ${clientDevis.map(d => `<tr>
        <td style="font-weight:600;font-family:monospace">${d.id}</td>
        <td>${this.fmtDate(d.date)}</td>
        <td>${this.typeLabel(d.type)}</td>
        <td>${d.objet}</td>
        <td><span class="status s-${d.statut}">${this.statusLabel(d.statut)}</span></td>
        <td style="font-weight:600">${d.montant}</td>
      </tr>`).join('')}
      ${!clientDevis.length ? '<tr><td colspan="6" style="text-align:center;color:#888;padding:15px">Aucun devis</td></tr>' : ''}
    </tbody>
  </table>
</div>

${clientEmails.length ? `<div class="section">
  <h3 class="section-title">Historique des \u00E9changes email</h3>
  <table>
    <thead><tr><th>Devis</th><th>Date</th><th>Type</th><th>Objet</th></tr></thead>
    <tbody>
      ${clientEmails.map(e => `<tr>
        <td style="font-family:monospace;font-weight:600">${e.devisId}</td>
        <td>${this.fmtDate(e.date)}</td>
        <td>${this.emailStageLabel(e.stage)}</td>
        <td>${this.escHtml(e.subject || '')}</td>
      </tr>`).join('')}
    </tbody>
  </table>
</div>` : ''}

<div class="footer">
  REWORKED \u2014 18 Rue Calliet, 69001 Lyon \u2014 atelier@rework-ed.com<br>
  Document g\u00E9n\u00E9r\u00E9 automatiquement. Donn\u00E9es au ${new Date().toLocaleDateString('fr-FR')}.
</div>

</body></html>`;

    this._download(html, `fiche-client-${cl.name.toLowerCase().replace(/\s+/g, '-')}.html`, 'text/html');
    this.toast(`Fiche ${cl.name} export\u00E9e`, 'success');
  }

  /* ═══════════ TARIFS ═══════════ */
  renderTarifs() {
    const c = document.getElementById('tarifsGrid'); if (!c) return;
    const cats = {};
    Object.entries(this.tarifs).forEach(([k, v]) => { const cat = v.cat || 'Autre'; if (!cats[cat]) cats[cat] = []; cats[cat].push({ key: k, ...v }); });

    c.innerHTML = Object.entries(cats).map(([cat, items]) => `
      <div class="tarif-section"><h4>${cat}</h4>
        ${items.map(t => `<div class="tarif-row">
          <div class="tarif-label">${t.label}</div>
          <input class="tarif-input" type="number" step="0.01" value="${t.price}" data-key="${t.key}">
          <div class="tarif-unit">€ / ${t.unit}</div>
          <button class="tarif-delete" onclick="app.deleteTarif('${t.key}')" title="Supprimer">x</button>
        </div>`).join('')}
        <div class="tarif-add-row">
          <button class="btn btn-ghost btn-sm" onclick="app.showAddTarif('${this.escHtml(cat)}')">+ Ajouter dans ${cat}</button>
        </div>
      </div>
    `).join('');

    // Zone pour nouvelle categorie
    c.innerHTML += `
      <div class="tarif-section tarif-new-cat">
        <button class="btn btn-primary btn-sm" onclick="app.showAddTarif('')">+ Nouvelle cat\u00E9gorie</button>
      </div>
    `;
  }

  showAddTarif(cat) {
    // Retirer un formulaire existant
    const old = document.getElementById('addTarifForm');
    if (old) old.remove();

    const container = document.getElementById('tarifsGrid');
    const el = document.createElement('div');
    el.id = 'addTarifForm';
    el.className = 'tarif-add-form';
    el.innerHTML = `
      <h4 style="font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:10px">${cat ? `Ajouter dans "${cat}"` : 'Nouveau tarif (nouvelle cat\u00E9gorie)'}</h4>
      ${!cat ? `<div class="tarif-add-field"><label>Categorie</label><input type="text" id="newTarifCat" placeholder="Ex: Broderie"></div>` : ''}
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div class="tarif-add-field" style="flex:2"><label>Designation</label><input type="text" id="newTarifLabel" placeholder="Ex: Broderie logo poitrine"></div>
        <div class="tarif-add-field" style="flex:0 0 80px"><label>Prix</label><input type="number" step="0.01" id="newTarifPrice" placeholder="0.00"></div>
        <div class="tarif-add-field" style="flex:0 0 100px"><label>Unite</label><input type="text" id="newTarifUnit" placeholder="piece" value="piece"></div>
      </div>
      <div style="display:flex;gap:8px;margin-top:10px">
        <button class="btn btn-primary btn-sm" onclick="app.addTarif('${this.escHtml(cat)}')">Ajouter</button>
        <button class="btn btn-ghost btn-sm" onclick="document.getElementById('addTarifForm').remove()">Annuler</button>
      </div>
    `;
    container.appendChild(el);
    el.scrollIntoView({ behavior: 'smooth' });
    (document.getElementById(cat ? 'newTarifLabel' : 'newTarifCat') || {}).focus?.();
  }

  addTarif(existingCat) {
    const label = (document.getElementById('newTarifLabel').value || '').trim();
    const price = parseFloat(document.getElementById('newTarifPrice').value) || 0;
    const unit = (document.getElementById('newTarifUnit').value || 'piece').trim();
    const catInput = document.getElementById('newTarifCat');
    const cat = existingCat || (catInput ? catInput.value.trim() : 'Autre');

    if (!label) { this.toast('Renseignez la d\u00E9signation', 'error'); return; }
    if (!cat) { this.toast('Renseignez la cat\u00E9gorie', 'error'); return; }

    // Generer une cle unique
    const key = 'custom_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.tarifs[key] = { label, price, unit, cat };

    document.getElementById('addTarifForm')?.remove();
    this.renderTarifs();
    this.toast(`Tarif "${label}" ajout\u00E9 dans ${cat}`, 'success');
  }

  deleteTarif(key) {
    const t = this.tarifs[key];
    if (!t) return;
    if (!confirm(`Supprimer "${t.label}" ?`)) return;
    delete this.tarifs[key];
    this.renderTarifs();
    this.toast('Tarif supprim\u00E9');
  }

  async saveTarifs() {
    document.querySelectorAll('.tarif-input').forEach(el => { const k = el.dataset.key; if (this.tarifs[k]) this.tarifs[k].price = parseFloat(el.value) || 0; });
    try { await wfetch(`${WORKER_URL}/tarifs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.tarifs) }); this.toast('Tarifs sauvegard\u00E9s', 'success'); } catch (e) { this.toast('Erreur', 'error'); }
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 GRILLE TARIFAIRE D\u00C9VELOPPEMENT PRODUIT \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  renderTarifsDev() {
    const c = document.getElementById('tarifsDevGrid'); if (!c) return;
    const td = this.tarifsDev;
    const esc = s => this.escHtml(String(s == null ? '' : s));

    // V\u00EAtements group\u00E9s par cat\u00E9gorie
    const catsG = {};
    Object.entries(td.garments || {}).forEach(([k, v]) => { const cat = v.cat || 'Autre'; if (!catsG[cat]) catsG[cat] = []; catsG[cat].push({ key: k, ...v }); });

    let html = `<div class="tarif-section"><h4>V\u00EAtements (prix de base)</h4>`;
    html += Object.entries(catsG).map(([cat, items]) => `
      <div style="padding:5px 12px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:var(--88);background:var(--f9)">${esc(cat)}</div>
      ${items.map(g => `<div class="tarif-row">
        <div class="tarif-label">${esc(g.label)}</div>
        <input class="tarif-input" type="number" step="1" value="${g.basePrice}" data-garment-key="${g.key}">
        <div class="tarif-unit">\u20AC base</div>
        <button class="tarif-delete" onclick="app.deleteGarment('${g.key}')" title="Supprimer">\u2715</button>
      </div>`).join('')}
      <div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.showAddGarment('${esc(cat)}')">+ Ajouter dans ${esc(cat)}</button></div>
    `).join('');
    html += `<div class="tarif-add-row"><button class="btn btn-primary btn-sm" onclick="app.showAddGarment('')">+ Nouvelle cat\u00E9gorie de v\u00EAtement</button></div>`;
    html += `</div>`;

    // Coefficients de complexit\u00E9
    html += `<div class="tarif-section"><h4>Coefficients de complexit\u00E9</h4>`;
    html += Object.entries(td.coefficients || {}).map(([k, v]) => `<div class="tarif-row">
      <div class="tarif-label">${esc(v.label)}</div>
      <input class="tarif-input" type="number" step="0.01" value="${v.mult}" data-coef-key="${k}">
      <div class="tarif-unit">\u00D7 base</div>
      <button class="tarif-delete" onclick="app.deleteCoef('${k}')" title="Supprimer">\u2715</button>
    </div>`).join('');
    html += `<div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.showAddCoef()">+ Ajouter un coefficient</button></div>`;
    html += `</div>`;

    // Options, group\u00E9es par phase
    const phases = [['predev', 'Options pr\u00E9alables au d\u00E9veloppement'], ['dev', 'Options de d\u00E9veloppement'], ['postdev', 'Options post\u00E9rieures au d\u00E9veloppement']];
    phases.forEach(([phaseKey, phaseLabel]) => {
      const items = Object.entries(td.options || {}).filter(([, v]) => v.phase === phaseKey).map(([k, v]) => ({ key: k, ...v }));
      html += `<div class="tarif-section"><h4>${esc(phaseLabel)}</h4>`;
      html += items.map(o => `<div class="tarif-row" style="flex-wrap:wrap">
        <div class="tarif-label" style="min-width:180px;flex:2">${esc(o.label)}</div>
        <select onchange="app.onDevOptionModeChange('${o.key}', this.value)" style="font-size:11px;padding:5px 6px;border:1px solid var(--dd);border-radius:6px;font-family:inherit">
          <option value="flat" ${o.mode === 'flat' ? 'selected' : ''}>\u20AC fixe</option>
          <option value="flat_qty" ${o.mode === 'flat_qty' ? 'selected' : ''}>\u20AC fixe \u00D7 quantit\u00E9</option>
          <option value="pct" ${o.mode === 'pct' ? 'selected' : ''}>% du prix de base</option>
          <option value="pct_qty" ${o.mode === 'pct_qty' ? 'selected' : ''}>% du prix de base \u00D7 quantit\u00E9</option>
        </select>
        <input class="tarif-input" type="number" step="0.01" value="${o.value}" data-opt-key="${o.key}" style="width:70px">
        <div class="tarif-unit">${o.mode.startsWith('pct') ? '%' : '\u20AC'}${o.mode.endsWith('_qty') ? ' / unit\u00E9' : ''}</div>
        <input type="text" value="${esc(o.unitLabel || '')}" data-opt-unit="${o.key}" placeholder="unit\u00E9 (ex: textile)" style="width:120px;font-size:10px;padding:5px 6px;border:1px solid var(--dd);border-radius:6px;font-family:inherit">
        <button class="tarif-delete" onclick="app.deleteDevOption('${o.key}')" title="Supprimer">\u2715</button>
      </div>`).join('');
      html += `<div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.showAddDevOption('${phaseKey}')">+ Ajouter une option</button></div>`;
      html += `</div>`;
    });

    // Scope inclus d'office dans le prix de base
    html += `<div class="tarif-section"><h4>Compris dans le prix de base (par pi\u00E8ce)</h4>
      <div style="padding:12px">
        <textarea id="devIncludedList" rows="4" style="width:100%;font-family:inherit;font-size:12px;padding:8px;border:1px solid var(--dd);border-radius:6px">${esc((td.included || []).join('\n'))}</textarea>
        <p style="font-size:10px;color:var(--88);margin-top:6px">Un \u00E9l\u00E9ment par ligne. Affich\u00E9 tel quel dans chaque devis d\u00E9veloppement g\u00E9n\u00E9r\u00E9, non factur\u00E9 s\u00E9par\u00E9ment.</p>
      </div>
    </div>`;

    c.innerHTML = html;
  }

  showAddGarment(cat) {
    const old = document.getElementById('addTarifDevForm'); if (old) old.remove();
    const container = document.getElementById('tarifsDevGrid');
    const el = document.createElement('div');
    el.id = 'addTarifDevForm';
    el.className = 'tarif-add-form';
    el.innerHTML = `
      <h4 style="font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:10px">${cat ? `Ajouter dans "${cat}"` : 'Nouveau v\u00EAtement (nouvelle cat\u00E9gorie)'}</h4>
      ${!cat ? `<div class="tarif-add-field"><label>Cat\u00E9gorie</label><input type="text" id="newGarmentCat" placeholder="Ex: Accessoires"></div>` : ''}
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div class="tarif-add-field" style="flex:2"><label>D\u00E9signation</label><input type="text" id="newGarmentLabel" placeholder="Ex: Robe"></div>
        <div class="tarif-add-field" style="flex:0 0 100px"><label>Prix de base</label><input type="number" step="1" id="newGarmentPrice" placeholder="0"></div>
      </div>
      <div style="display:flex;gap:8px;margin-top:10px">
        <button class="btn btn-primary btn-sm" onclick="app.addGarment('${this.escHtml(cat)}')">Ajouter</button>
        <button class="btn btn-ghost btn-sm" onclick="document.getElementById('addTarifDevForm').remove()">Annuler</button>
      </div>
    `;
    container.appendChild(el);
    el.scrollIntoView({ behavior: 'smooth' });
    (document.getElementById(cat ? 'newGarmentLabel' : 'newGarmentCat') || {}).focus?.();
  }

  addGarment(existingCat) {
    const label = (document.getElementById('newGarmentLabel').value || '').trim();
    const basePrice = parseFloat(document.getElementById('newGarmentPrice').value) || 0;
    const catInput = document.getElementById('newGarmentCat');
    const cat = existingCat || (catInput ? catInput.value.trim() : 'Autre');
    if (!label) { this.toast('Renseignez la d\u00E9signation', 'error'); return; }
    if (!cat) { this.toast('Renseignez la cat\u00E9gorie', 'error'); return; }
    const key = 'garment_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.tarifsDev.garments[key] = { label, basePrice, cat };
    document.getElementById('addTarifDevForm')?.remove();
    this.renderTarifsDev();
    this.toast(`V\u00EAtement "${label}" ajout\u00E9 dans ${cat}`, 'success');
  }

  deleteGarment(key) {
    const g = this.tarifsDev.garments[key]; if (!g) return;
    if (!confirm(`Supprimer "${g.label}" ?`)) return;
    delete this.tarifsDev.garments[key];
    this.renderTarifsDev();
    this.toast('V\u00EAtement supprim\u00E9');
  }

  showAddCoef() {
    const old = document.getElementById('addTarifDevForm'); if (old) old.remove();
    const container = document.getElementById('tarifsDevGrid');
    const el = document.createElement('div');
    el.id = 'addTarifDevForm';
    el.className = 'tarif-add-form';
    el.innerHTML = `
      <h4 style="font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:10px">Nouveau coefficient</h4>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div class="tarif-add-field" style="flex:2"><label>D\u00E9signation</label><input type="text" id="newCoefLabel" placeholder="Ex: Coef 4 \u2014 Pi\u00E8ce sur-mesure"></div>
        <div class="tarif-add-field" style="flex:0 0 100px"><label>Multiplicateur</label><input type="number" step="0.01" id="newCoefMult" placeholder="1.00"></div>
      </div>
      <div style="display:flex;gap:8px;margin-top:10px">
        <button class="btn btn-primary btn-sm" onclick="app.addCoef()">Ajouter</button>
        <button class="btn btn-ghost btn-sm" onclick="document.getElementById('addTarifDevForm').remove()">Annuler</button>
      </div>
    `;
    container.appendChild(el);
    el.scrollIntoView({ behavior: 'smooth' });
    document.getElementById('newCoefLabel')?.focus();
  }

  addCoef() {
    const label = (document.getElementById('newCoefLabel').value || '').trim();
    const mult = parseFloat(document.getElementById('newCoefMult').value) || 1;
    if (!label) { this.toast('Renseignez la d\u00E9signation', 'error'); return; }
    const key = 'coef_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.tarifsDev.coefficients[key] = { label, mult };
    document.getElementById('addTarifDevForm')?.remove();
    this.renderTarifsDev();
    this.toast(`Coefficient "${label}" ajout\u00E9`, 'success');
  }

  deleteCoef(key) {
    const co = this.tarifsDev.coefficients[key]; if (!co) return;
    if (!confirm(`Supprimer "${co.label}" ?`)) return;
    delete this.tarifsDev.coefficients[key];
    this.renderTarifsDev();
    this.toast('Coefficient supprim\u00E9');
  }

  showAddDevOption(phase) {
    const old = document.getElementById('addTarifDevForm'); if (old) old.remove();
    const container = document.getElementById('tarifsDevGrid');
    const el = document.createElement('div');
    el.id = 'addTarifDevForm';
    el.className = 'tarif-add-form';
    const phaseLabels = { predev: 'pr\u00E9alable au d\u00E9veloppement', dev: 'de d\u00E9veloppement', postdev: 'post\u00E9rieure au d\u00E9veloppement' };
    el.innerHTML = `
      <h4 style="font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:10px">Nouvelle option ${phaseLabels[phase] || ''}</h4>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div class="tarif-add-field" style="flex:2"><label>D\u00E9signation</label><input type="text" id="newOptLabel" placeholder="Ex: Essayage suppl\u00E9mentaire"></div>
        <div class="tarif-add-field" style="flex:0 0 180px"><label>Mode de calcul</label>
          <select id="newOptMode">
            <option value="flat">\u20AC fixe</option>
            <option value="flat_qty">\u20AC fixe \u00D7 quantit\u00E9</option>
            <option value="pct">% du prix de base</option>
            <option value="pct_qty">% du prix de base \u00D7 quantit\u00E9</option>
          </select>
        </div>
        <div class="tarif-add-field" style="flex:0 0 90px"><label>Valeur</label><input type="number" step="0.01" id="newOptValue" placeholder="0"></div>
        <div class="tarif-add-field" style="flex:0 0 150px"><label>Unit\u00E9 (si quantit\u00E9)</label><input type="text" id="newOptUnit" placeholder="Ex: textile"></div>
      </div>
      <div style="display:flex;gap:8px;margin-top:10px">
        <button class="btn btn-primary btn-sm" onclick="app.addDevOption('${phase}')">Ajouter</button>
        <button class="btn btn-ghost btn-sm" onclick="document.getElementById('addTarifDevForm').remove()">Annuler</button>
      </div>
    `;
    container.appendChild(el);
    el.scrollIntoView({ behavior: 'smooth' });
    document.getElementById('newOptLabel')?.focus();
  }

  addDevOption(phase) {
    const label = (document.getElementById('newOptLabel').value || '').trim();
    const mode = document.getElementById('newOptMode').value || 'flat';
    const value = parseFloat(document.getElementById('newOptValue').value) || 0;
    const unitLabel = (document.getElementById('newOptUnit').value || '').trim();
    if (!label) { this.toast('Renseignez la d\u00E9signation', 'error'); return; }
    const key = 'opt_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.tarifsDev.options[key] = { label, phase, mode, value, unitLabel };
    document.getElementById('addTarifDevForm')?.remove();
    this.renderTarifsDev();
    this.toast(`Option "${label}" ajout\u00E9e`, 'success');
  }

  deleteDevOption(key) {
    const o = this.tarifsDev.options[key]; if (!o) return;
    if (!confirm(`Supprimer "${o.label}" ?`)) return;
    delete this.tarifsDev.options[key];
    this.renderTarifsDev();
    this.toast('Option supprim\u00E9e');
  }

  onDevOptionModeChange(key, mode) {
    if (this.tarifsDev.options[key]) this.tarifsDev.options[key].mode = mode;
    this.renderTarifsDev();
  }

  async saveTarifsDev() {
    document.querySelectorAll('[data-garment-key]').forEach(el => { const k = el.dataset.garmentKey; if (this.tarifsDev.garments[k]) this.tarifsDev.garments[k].basePrice = parseFloat(el.value) || 0; });
    document.querySelectorAll('[data-coef-key]').forEach(el => { const k = el.dataset.coefKey; if (this.tarifsDev.coefficients[k]) this.tarifsDev.coefficients[k].mult = parseFloat(el.value) || 1; });
    document.querySelectorAll('[data-opt-key]').forEach(el => { const k = el.dataset.optKey; if (this.tarifsDev.options[k]) this.tarifsDev.options[k].value = parseFloat(el.value) || 0; });
    document.querySelectorAll('[data-opt-unit]').forEach(el => { const k = el.dataset.optUnit; if (this.tarifsDev.options[k]) this.tarifsDev.options[k].unitLabel = el.value.trim(); });
    const includedRaw = document.getElementById('devIncludedList')?.value || '';
    this.tarifsDev.included = includedRaw.split('\n').map(s => s.trim()).filter(Boolean);
    try { await wfetch(`${WORKER_URL}/tarifs-dev`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.tarifsDev) }); this.toast('Grille d\u00E9veloppement sauvegard\u00E9e', 'success'); } catch (e) { this.toast('Erreur', 'error'); }
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 DEVIS D\u00C9VELOPPEMENT PRODUIT (questionnaire + calcul d\u00E9terministe) \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  openDevDevisBuilder() {
    if (!this.company) this.company = this._defaultCompany();
    if (!Object.keys(this.tarifsDev.garments || {}).length) this.tarifsDev = this._defaultTarifsDev();
    const firstGarment = Object.keys(this.tarifsDev.garments)[0] || '';
    const firstCoef = Object.keys(this.tarifsDev.coefficients)[0] || '';
    this._dev = {
      clientName: '', clientType: 'professionnel', clientAddress: '', clientSiret: '',
      objet: '', delai: '', paymentTerms: 'standard',
      lines: [{ garment: firstGarment, coef: firstCoef, opts: {}, note: '', matiere: { enabled: false, desc: '', amount: 0 }, baseIncluded: true }],
    };
    this._devEditingId = null;
    document.getElementById('devDevisOverlay').classList.add('active');
    this.renderDevDevisBuilder();
  }

  // Rouvre le builder pré-rempli à partir de l'état sauvegardé d'un devis développement existant
  openDevDevisBuilderFromSnapshot(devisId) {
    const d = this.devis.find(x => x.id === devisId);
    if (!d || !d.snap) return false;
    if (!this.company) this.company = this._defaultCompany();
    if (!Object.keys(this.tarifsDev.garments || {}).length) this.tarifsDev = this._defaultTarifsDev();
    this._dev = { ...d.snap, lines: (d.snap.lines || []).map(l => ({ ...l, opts: { ...(l.opts || {}) } })) };
    this._devEditingId = devisId;
    document.getElementById('devDevisOverlay').classList.add('active');
    this.renderDevDevisBuilder();
    return true;
  }

  closeDevDevisBuilder() { document.getElementById('devDevisOverlay').classList.remove('active'); this._dev = null; this._devEditingId = null; }

  _devHasContent() {
    if (!this._dev) return false;
    if ((this._dev.clientName || '').trim()) return true;
    return (this._dev.lines || []).some(l => l.garment && (Object.keys(l.opts || {}).length || (l.note || '').trim()));
  }

  attemptCloseDevDevis() {
    if (!this._dev) { this.closeDevDevisBuilder(); return; }
    this._devReadForm();
    if (!this._devHasContent()) { this.closeDevDevisBuilder(); return; }
    if (!confirm('Fermer sans enregistrer ce devis d\u00E9veloppement ?')) return;
    this.closeDevDevisBuilder();
  }

  _devReadForm() {
    if (!this._dev) return;
    const g = id => document.getElementById(id);
    this._dev.clientName = g('devClientName')?.value || '';
    this._dev.clientType = g('devClientType')?.value || 'professionnel';
    this._dev.clientAddress = g('devClientAddress')?.value || '';
    this._dev.clientSiret = g('devClientSiret')?.value || '';
    this._dev.objet = g('devObjet')?.value || '';
    this._dev.delai = g('devDelai')?.value || '';
    this._dev.paymentTerms = g('devPaymentTerms')?.value || 'standard';
    this._devReadLines();
  }

  _devReadLines() {
    if (!this._dev) return;
    const rows = document.querySelectorAll('#devLinesBody > .dev-line');
    if (!rows.length) return;
    const lines = [];
    rows.forEach(row => {
      const garment = row.querySelector('[data-dl="garment"]')?.value || '';
      const coef = row.querySelector('[data-dl="coef"]')?.value || '';
      const note = row.querySelector('[data-dl="note"]')?.value || '';
      const opts = {};
      row.querySelectorAll('[data-dl-opt]').forEach(chk => {
        if (!chk.checked) return;
        const key = chk.dataset.dlOpt;
        const qtyInput = row.querySelector(`[data-dl-qty="${key}"]`);
        opts[key] = qtyInput ? (parseFloat(qtyInput.value) || 1) : 1;
      });
      const matiere = {
        enabled: row.querySelector('[data-dl="matiereEnabled"]')?.checked || false,
        desc: row.querySelector('[data-dl="matiereDesc"]')?.value || '',
        amount: parseFloat(row.querySelector('[data-dl="matiereAmount"]')?.value) || 0,
      };
      const customLabel = row.querySelector('[data-dl="customLabel"]')?.value || '';
      const customBasePrice = parseFloat(row.querySelector('[data-dl="customBasePrice"]')?.value) || 0;
      const baseIncluded = row.querySelector('[data-dl="baseIncluded"]')?.checked !== false;
      lines.push({ garment, coef, opts, note, matiere, customLabel, customBasePrice, baseIncluded });
    });
    this._dev.lines = lines;
  }

  devAddLine() {
    this._devReadForm();
    const firstGarment = Object.keys(this.tarifsDev.garments)[0] || '';
    const firstCoef = Object.keys(this.tarifsDev.coefficients)[0] || '';
    this._dev.lines.push({ garment: firstGarment, coef: firstCoef, opts: {}, note: '', matiere: { enabled: false, desc: '', amount: 0 }, baseIncluded: true });
    this.renderDevDevisBuilder();
  }

  devRemoveLine(i) {
    this._devReadForm();
    this._dev.lines.splice(i, 1);
    if (!this._dev.lines.length) {
      const firstGarment = Object.keys(this.tarifsDev.garments)[0] || '';
      const firstCoef = Object.keys(this.tarifsDev.coefficients)[0] || '';
      this._dev.lines.push({ garment: firstGarment, coef: firstCoef, opts: {}, note: '', matiere: { enabled: false, desc: '', amount: 0 }, baseIncluded: true });
    }
    this.renderDevDevisBuilder();
  }

  devToggleOption(checkbox) {
    const qtyInput = checkbox.closest('.dev-opt-row')?.querySelector('[data-dl-qty]');
    if (qtyInput) qtyInput.style.display = checkbox.checked ? 'inline-block' : 'none';
    this.devRecalc();
  }

  devToggleMatiere(checkbox) {
    const fields = checkbox.closest('.dev-matiere-row')?.querySelector('.dev-matiere-fields');
    if (fields) fields.style.display = checkbox.checked ? 'flex' : 'none';
    this.devRecalc();
  }

  devOnGarmentChange(select) {
    const row = select.closest('.dev-line');
    const customFields = row?.querySelector('.dev-custom-fields');
    if (customFields) customFields.style.display = select.value === '__custom__' ? 'flex' : 'none';
    this.devRecalc();
  }

  // Ajoute une pièce "Autre (prix libre)" à la grille permanente, et bascule la ligne dessus
  async devSaveCustomGarmentToGrid(i) {
    this._devReadForm();
    const line = this._dev.lines[i];
    if (!line || line.garment !== '__custom__') return;
    const label = (line.customLabel || '').trim();
    const basePrice = this._num(line.customBasePrice);
    if (!label) { this.toast("Donnez un nom à la pièce avant de l'ajouter à la grille", 'error'); return; }
    const key = 'garment_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.tarifsDev.garments[key] = { label, basePrice, cat: 'Sur-mesure' };
    try { await wfetch(`${WORKER_URL}/tarifs-dev`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.tarifsDev) }); } catch (e) {}
    line.garment = key;
    this.renderDevDevisBuilder();
    this.toast(`"${label}" ajouté à la grille (catégorie Sur-mesure)`, 'success');
  }

  devRecalc() {
    if (!this._dev) return;
    this._devReadLines();
    const c = this._devCompute();
    document.querySelectorAll('#devLinesBody > .dev-line').forEach((row, i) => {
      const el = row.querySelector('[data-dl-total]');
      if (el && c.lines[i]) el.textContent = this._eur(c.lines[i].subtotal);
    });
    const totalEl = document.getElementById('devGrandTotal');
    if (totalEl) totalEl.textContent = this._eur(c.total);
  }

  renderDevDevisBuilder() {
    const body = document.getElementById('devDevisBuilderBody');
    if (!body || !this._dev) return;
    const dev = this._dev;
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const phaseGroups = [['predev', 'Options pr\u00E9alables'], ['dev', 'Options de d\u00E9veloppement'], ['postdev', 'Options post\u00E9rieures']];

    const hdr = document.querySelector('#devDevisOverlay .preview-header span');
    if (hdr) hdr.textContent = this._devEditingId ? `Modifier devis d\u00E9veloppement ${this._devEditingId}` : 'Nouveau devis d\u00E9veloppement';
    const genBtn = document.getElementById('devDevisGenerateBtn');
    if (genBtn) genBtn.textContent = this._devEditingId ? 'Mettre \u00E0 jour & aper\u00E7u' : 'G\u00E9n\u00E9rer & aper\u00E7u';

    body.innerHTML = `
      <div class="fb-section-title">Client</div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Nom du client</label>
          <input type="text" id="devClientName" list="clientSuggestions" value="${esc(dev.clientName)}" placeholder="Ex: RAWRUN">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>Type</label>
          <select id="devClientType">
            <option value="professionnel" ${dev.clientType === 'professionnel' ? 'selected' : ''}>Professionnel</option>
            <option value="particulier" ${dev.clientType === 'particulier' ? 'selected' : ''}>Particulier</option>
          </select>
        </div>
      </div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Adresse</label>
          <input type="text" id="devClientAddress" value="${esc(dev.clientAddress)}">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>SIRET</label>
          <input type="text" id="devClientSiret" value="${esc(dev.clientSiret)}">
        </div>
      </div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Objet du devis</label>
          <input type="text" id="devObjet" value="${esc(dev.objet)}" placeholder="Ex: D\u00E9veloppement collection SS27">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>D\u00E9lais souhait\u00E9s</label>
          <input type="text" id="devDelai" value="${esc(dev.delai)}" placeholder="Ex: Avant le 15 juin">
        </div>
      </div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Conditions de paiement</label>
          <select id="devPaymentTerms">
            <option value="standard" ${dev.paymentTerms !== 'staged' ? 'selected' : ''}>Acompte 40% / Solde 60%</option>
            <option value="staged" ${dev.paymentTerms === 'staged' ? 'selected' : ''}>3 \u00E9tapes : 30% commande / 30% validation toile / 40% livraison prototype</option>
          </select>
        </div>
      </div>

      <div class="fb-section-title">Pi\u00E8ces \u00E0 d\u00E9velopper</div>
      <div id="devLinesBody">
        ${dev.lines.map((l, i) => this._devLineHtml(l, i, phaseGroups)).join('')}
      </div>
      <button class="btn btn-ghost btn-sm" onclick="app.devAddLine()" style="margin-top:8px">+ Ajouter un v\u00EAtement</button>

      <div class="fb-totals" style="margin-top:16px">
        <div class="fb-total-net"><span>Total devis d\u00E9veloppement</span><strong id="devGrandTotal">\u2014</strong></div>
      </div>

      <div class="fb-section-title">Ajuster avec Claude (optionnel)</div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <input type="text" id="devClaudeInstruction" placeholder="Ex : ajoute une remise de 10 %, pr\u00E9cise le d\u00E9lai de gradation...">
        </div>
        <button class="btn btn-ghost btn-sm" onclick="app.adjustDevDevisWithClaude()" style="flex-shrink:0">Ajuster avec Claude</button>
      </div>
    `;
    this.devRecalc();
  }

  _devLineHtml(l, i, phaseGroups) {
    const td = this.tarifsDev;
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const isCustom = l.garment === '__custom__';
    const garmentOpts = `<option value="__custom__" ${isCustom ? 'selected' : ''}>\u270e Autre (prix libre)</option>`
      + Object.entries(td.garments || {}).map(([k, v]) => `<option value="${k}" ${l.garment === k ? 'selected' : ''}>${esc(v.label)} \u2014 ${this._eur(v.basePrice)}</option>`).join('');
    const coefOpts = Object.entries(td.coefficients || {}).map(([k, v]) => `<option value="${k}" ${l.coef === k ? 'selected' : ''}>${esc(v.label)}</option>`).join('');
    const optsHtml = phaseGroups.map(([phaseKey, phaseLabel]) => {
      const items = Object.entries(td.options || {}).filter(([, v]) => v.phase === phaseKey);
      if (!items.length) return '';
      return `<div class="dev-opt-group">
        <div class="dev-opt-group-title">${esc(phaseLabel)}</div>
        ${items.map(([key, o]) => {
          const checked = l.opts && Object.prototype.hasOwnProperty.call(l.opts, key);
          const qty = checked ? (l.opts[key] || 1) : 1;
          const isQty = o.mode.endsWith('_qty');
          const ruleText = o.mode === 'flat' ? `${this._eur(o.value)}`
            : o.mode === 'flat_qty' ? `${this._eur(o.value)} / ${o.unitLabel || 'unit\u00E9'}`
            : o.mode === 'pct' ? `${o.value}% du prix de base`
            : `${o.value}% du prix de base / ${o.unitLabel || 'unit\u00E9'}`;
          return `<label class="dev-opt-row">
            <input type="checkbox" data-dl-opt="${key}" ${checked ? 'checked' : ''} onchange="app.devToggleOption(this)">
            <span class="dev-opt-label">${esc(o.label)}</span>
            <input type="number" step="1" min="1" value="${qty}" data-dl-qty="${key}" class="dev-opt-qty" style="display:${(checked && isQty) ? 'inline-block' : 'none'}" oninput="app.devRecalc()" title="Quantit\u00E9 (${esc(o.unitLabel || 'unit\u00E9')})">
            <span class="dev-opt-price">${esc(ruleText)}</span>
          </label>`;
        }).join('')}
      </div>`;
    }).join('');

    return `<div class="dev-line" data-idx="${i}">
      <div class="dev-line-header">
        <select data-dl="garment" onchange="app.devOnGarmentChange(this)" style="flex:1">${garmentOpts}</select>
        <select data-dl="coef" onchange="app.devRecalc()" style="flex:1">${coefOpts}</select>
        <button class="tarif-delete" onclick="app.devRemoveLine(${i})" title="Supprimer cette pi\u00E8ce">\u2715</button>
      </div>
      <div class="dev-custom-fields" style="display:${isCustom ? 'flex' : 'none'}">
        <input type="text" data-dl="customLabel" value="${esc(l.customLabel || '')}" placeholder="Nom de la pi\u00E8ce (ex : Cagoule de sc\u00E8ne)" oninput="app.devRecalc()">
        <input type="number" step="1" min="0" data-dl="customBasePrice" value="${l.customBasePrice || 0}" placeholder="Prix de base \u20AC" oninput="app.devRecalc()">
        <button type="button" class="btn btn-ghost btn-sm" onclick="app.devSaveCustomGarmentToGrid(${i})" title="Ajouter cette pi\u00E8ce \u00E0 la grille permanente">+ Grille</button>
      </div>
      <label class="dev-base-toggle">
        <input type="checkbox" data-dl="baseIncluded" ${l.baseIncluded === false ? '' : 'checked'} onchange="app.devRecalc()">
        D\u00E9veloppement complet inclus (patronage, toile, modif l\u00E9g\u00E8re, prototype) \u2014 d\u00E9cocher pour une reprise sur une pi\u00E8ce d\u00E9j\u00E0 d\u00E9velopp\u00E9e (facture uniquement les options coch\u00E9es ci-dessous)
      </label>
      ${optsHtml}
      <div class="chat-form-field chat-form-field-full" style="margin-top:6px">
        <input type="text" data-dl="note" value="${esc(l.note || '')}" placeholder="Note / commentaire (ex : textiles \u00E0 sourcer, trims souhait\u00E9s...)">
      </div>
      <div class="dev-matiere-row">
        <label style="display:flex;align-items:center;gap:6px;font-size:11px;cursor:pointer">
          <input type="checkbox" data-dl="matiereEnabled" ${l.matiere?.enabled ? 'checked' : ''} onchange="app.devToggleMatiere(this)">
          Mati\u00E8re / fournitures avanc\u00E9es par REWORKED (factur\u00E9es en plus, au r\u00E9el)
        </label>
        <div class="dev-matiere-fields" style="display:${l.matiere?.enabled ? 'flex' : 'none'}">
          <input type="text" data-dl="matiereDesc" value="${esc(l.matiere?.desc || '')}" placeholder="Ex : 2m lainage bouclette fournisseur X" oninput="app.devRecalc()" style="flex:2">
          <input type="number" step="0.01" min="0" data-dl="matiereAmount" value="${l.matiere?.amount || 0}" placeholder="Montant \u20AC" oninput="app.devRecalc()" style="flex:0 0 100px">
        </div>
      </div>
      <div style="margin-top:8px;text-align:right">
        <button type="button" class="btn btn-ghost btn-sm" onclick="app.publishLineToCatalogue(${i})" title="Ajouter cette pi\u00E8ce au catalogue des produits d\u00E9velopp\u00E9s">+ Publier au catalogue</button>
      </div>
      <div class="dev-line-subtotal">Sous-total pi\u00E8ce <strong data-dl-total>\u2014</strong></div>
    </div>`;
  }

  _devComputeLine(line) {
    const g = line.garment === '__custom__'
      ? { label: (line.customLabel || '').trim() || 'Pièce sur-mesure', basePrice: this._num(line.customBasePrice), cat: 'Sur-mesure' }
      : this.tarifsDev.garments[line.garment];
    const coef = this.tarifsDev.coefficients[line.coef];
    if (!g || !coef) return null;
    const basePrice = g.basePrice * (this._num(coef.mult) || 1);
    const baseIncluded = line.baseIncluded !== false;
    const items = [];
    let subtotal = 0;
    if (baseIncluded) {
      // Pas de mention du coefficient num\u00E9rique c\u00F4t\u00E9 client, juste la description qualitative
      items.push({ label: `Base d\u00E9veloppement \u2014 ${g.label}`, detail: `${this._devCoefClientLabel(coef.label)} \u00B7 comprend : ${(this.tarifsDev.included || []).join(', ')}`, amount: basePrice });
      subtotal += basePrice;
    } else {
      items.push({ label: 'Pi\u00E8ce d\u00E9j\u00E0 d\u00E9velopp\u00E9e', detail: 'D\u00E9veloppement de base non refactur\u00E9 (patronage / toile / prototype d\u00E9j\u00E0 r\u00E9alis\u00E9s)', amount: 0 });
    }
    Object.entries(line.opts || {}).forEach(([key, qty]) => {
      const o = this.tarifsDev.options[key]; if (!o) return;
      const isQty = o.mode.endsWith('_qty');
      const q = isQty ? Math.max(1, this._num(qty) || 1) : 1;
      let amount = 0;
      if (o.mode === 'flat' || o.mode === 'flat_qty') amount = o.value * q;
      else amount = basePrice * (o.value / 100) * q;
      const qtyTag = isQty ? ` (\u00D7 ${q} ${o.unitLabel || 'unit\u00E9'}${q > 1 ? 's' : ''})` : '';
      // Pas de d\u00E9tail de calcul (%, \u20AC/unit\u00E9) sur le devis client : uniquement le libell\u00E9 et le montant.
      items.push({ label: o.label + qtyTag, detail: '', amount });
      subtotal += amount;
    });
    if (line.matiere && line.matiere.enabled) {
      const amt = this._num(line.matiere.amount);
      if (amt > 0) {
        items.push({ label: 'Matière / fournitures avancées', detail: line.matiere.desc || 'Facturé au réel', amount: amt });
        subtotal += amt;
      }
    }
    return { garment: g, coef, basePrice, items, subtotal, note: line.note || '', baseIncluded };
  }

  _devCompute() {
    const lines = (this._dev?.lines || []).map(l => this._devComputeLine(l)).filter(Boolean);
    const total = lines.reduce((s, l) => s + l.subtotal, 0);
    return { lines, total };
  }

  async generateDevDevis() {
    if (!this._dev) return;
    this._devReadForm();
    const dev = this._dev;
    if (!dev.clientName.trim()) { this.toast('Renseignez le nom du client', 'error'); return; }
    const c = this._devCompute();
    if (!c.lines.length) { this.toast('Ajoutez au moins une pi\u00E8ce \u00E0 d\u00E9velopper', 'error'); return; }

    const numero = this._devEditingId || await this._allocateDevisNumber();
    const data = this._devDevisData(numero, c);
    const html = this.buildDevDevisHtml(data);
    const wasEditing = !!this._devEditingId;
    await this._commitDevDevis(numero, html, c, dev);
    this.toast(wasEditing ? `Devis ${numero} mis \u00E0 jour` : `Devis ${numero} g\u00E9n\u00E9r\u00E9`, 'success');
  }

  async _allocateDevisNumber() {
    let numero = '';
    try { const r = await wfetch(`${WORKER_URL}/next-number`); if (r.ok) { const d = await r.json(); numero = d.number; } } catch (e) {}
    if (!numero) { const now = new Date(); numero = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(this.devis.length + 1).padStart(3, '0')}`; }
    return numero;
  }

  _devDevisData(numero, c) {
    const dev = this._dev;
    const profile = (this.company && this.company.reworked) || this._defaultCompany().reworked;
    return {
      numero, dateFr: this._fmtDateLong(new Date().toISOString().slice(0, 10)),
      company: profile,
      client: { name: dev.clientName.toUpperCase(), address: dev.clientAddress, siret: dev.clientSiret },
      objet: dev.objet || 'D\u00E9veloppement produit',
      delai: dev.delai,
      paymentTerms: dev.paymentTerms || 'standard',
      lines: c.lines, total: c.total,
      logoUrl: 'https://cdn.shopify.com/s/files/1/0798/6259/5851/t/17/assets/logo-red-studio.svg',
    };
  }

  async _commitDevDevis(numero, html, c, dev, montantOverride) {
    const client = (dev.clientName || '').toUpperCase();
    const existing = this.devis.find(d => d.id === numero);
    // Snapshot de l'\u00E9tat du questionnaire (garments/coefs/options choisis) pour pouvoir
    // rouvrir le calculateur plus tard via "Modifier", au lieu de ne garder que le HTML final.
    const snap = { ...dev, lines: (dev.lines || []).map(l => ({ ...l, opts: { ...(l.opts || {}) } })) };
    const newDevis = {
      id: numero, date: existing ? existing.date : new Date().toISOString().slice(0, 10), client,
      type: 'dev', objet: dev.objet || 'D\u00E9veloppement produit',
      montant: montantOverride || this._eur(c.total), statut: existing ? existing.statut : 'brouillon',
      snap,
    };
    const idx = this.devis.findIndex(d => d.id === numero);
    if (idx >= 0) this.devis[idx] = newDevis; else this.devis.push(newDevis);
    try {
      await wfetch(`${WORKER_URL}/devis`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newDevis) });
      await wfetch(`${WORKER_URL}/devis/${numero}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ html }) });
    } catch (e) {}
    if (!this.clients.find(cl => (cl.name || '').toUpperCase() === client)) {
      const nc = { name: client, address: dev.clientAddress || '', siret: dev.clientSiret || '', contacts: '', source: 'local' };
      this.clients.push(nc);
      try { await wfetch(`${WORKER_URL}/clients`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nc) }); } catch (e) {}
    }
    this.renderDevis();
    this.renderClients();
    this.closeDevDevisBuilder();
    this.showPreview(html, numero);
  }

  async adjustDevDevisWithClaude() {
    if (!this._dev) return;
    const instruction = document.getElementById('devClaudeInstruction')?.value.trim();
    if (!instruction) { this.toast('\u00C9crivez une demande pour Claude', 'error'); return; }
    this._devReadForm();
    const dev = this._dev;
    if (!dev.clientName.trim()) { this.toast('Renseignez le nom du client avant l\'ajustement', 'error'); return; }
    const c = this._devCompute();
    if (!c.lines.length) { this.toast('Ajoutez au moins une pi\u00E8ce \u00E0 d\u00E9velopper', 'error'); return; }

    const numero = this._devEditingId || await this._allocateDevisNumber();
    const baseHtml = this.buildDevDevisHtml(this._devDevisData(numero, c));
    this.toast('Claude ajuste le devis...', '');
    try {
      const r = await wfetch(`${WORKER_URL}/chat-dev`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction, html: baseHtml }),
      });
      const j = await r.json();
      if (j.error) { this.toast(j.error, 'error'); return; }
      const m = (j.text || '').match(/```html\n([\s\S]*?)```/) || (j.text || '').match(/(<!DOCTYPE html>[\s\S]*?<\/html>)/i);
      if (!m) { this.toast('Pas de HTML renvoy\u00E9 par Claude', 'error'); return; }
      const claudeHtml = m[1].trim();
      const totalFromHtml = this._extractDevDevisTotalFromHtml(claudeHtml);
      const montantStr = totalFromHtml != null ? this._eur(totalFromHtml) : undefined;
      await this._commitDevDevis(numero, claudeHtml, c, dev, montantStr);
      this.toast(`Devis ${numero} ajust\u00E9 par Claude et enregistr\u00E9`, 'success');
    } catch (e) { this.toast('Erreur r\u00E9seau Claude', 'error'); }
  }

  _extractDevDevisTotalFromHtml(html) {
    const m = html.match(/<strong>TOTAL<\/strong><\/td>\s*<td[^>]*><strong>([^<]+)<\/strong>/i);
    if (!m) return null;
    const v = this._num((m[1].match(/[\d\s.,]+/) || [''])[0]);
    return isNaN(v) ? null : v;
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 RENDU HTML DEVIS D\u00C9VELOPPEMENT (m\u00EAme DA que les devis/factures) \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  buildDevDevisHtml(data) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const co = data.company;

    const companyInfo = [
      esc(co.address), `SIRET : ${esc(co.siret)}`, co.numTva ? `N\u00B0 TVA : ${esc(co.numTva)}` : '', `${esc(co.tel)} \u00B7 ${esc(co.email)}`,
    ].filter(Boolean).join('<br>');

    const sectionsHtml = data.lines.map((l, i) => `
      <div class="section">
        <h3 class="section-title">${i + 1}. ${esc(l.garment.label)} \u2014 ${esc(this._devCoefClientLabel(l.coef.label))}</h3>
        <table><thead><tr><th>D\u00E9signation</th><th>Montant</th></tr></thead>
        <tbody>
          ${l.items.map(it => `<tr><td><strong>${esc(it.label)}</strong>${it.detail ? `<span class="detail">${esc(it.detail)}</span>` : ''}</td><td>${this._eur(it.amount)}</td></tr>`).join('')}
          <tr class="row-total"><td>Sous-total</td><td>${this._eur(l.subtotal)}</td></tr>
        </tbody></table>
        ${l.note ? `<div class="note">Note : ${esc(l.note)}</div>` : ''}
      </div>`).join('');

    const objetMeta = data.delai ? `<span><strong>D\u00E9lai :</strong> ${esc(data.delai)}</span>` : '';

    const reglementHtml = data.paymentTerms === 'staged'
      ? `<li><strong>30%</strong> \u00E0 la commande</li><li><strong>30%</strong> \u00E0 la validation de la toile</li><li><strong>40%</strong> \u00E0 la livraison du prototype</li>`
      : `<li><strong>Acompte 40%</strong> \u00E0 la validation</li><li><strong>Solde 60%</strong> apr\u00E8s prestation</li>`;

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Devis d\u00E9veloppement ${esc(data.numero)} - ${esc(data.client.name)}</title>
<style>
${this._factureCSS()}
</style>
</head>
<body>

<div class="header">
  <div>
    <img class="logo" src="${data.logoUrl}" alt="R/ED STUDIO">
    <div class="company-info"><strong>${esc(co.emetteur)}</strong><br>${companyInfo}</div>
  </div>
  <div class="devis-badge">
    <h2>Devis d\u00E9veloppement N\u00B0 ${esc(data.numero)}</h2>
    <p>\u00C9mis le ${esc(data.dateFr)}<br>Valide 30 jours</p>
  </div>
</div>

<div class="parties">
  <div class="partie">
    <div class="partie-label">Client</div>
    <div class="partie-content"><strong>${esc(data.client.name)}</strong>${data.client.address ? `<br>${esc(data.client.address)}` : ''}${data.client.siret ? `<br>SIRET : ${esc(data.client.siret)}` : ''}</div>
  </div>
  <div class="partie">
    <div class="partie-label">\u00C9metteur</div>
    <div class="partie-content"><strong>${esc(co.emetteur)}</strong><br>${esc(co.address)}</div>
  </div>
</div>

${data.objet || objetMeta ? `<div class="objet">
  ${data.objet ? `<div class="objet-title">${esc(data.objet)}</div>` : ''}
  ${objetMeta ? `<div class="objet-meta">${objetMeta}</div>` : ''}
</div>` : ''}

${sectionsHtml}

<div class="section"><table><tbody>
  <tr class="row-total"><td><strong>TOTAL</strong></td><td><strong>${this._eur(data.total)}</strong></td></tr>
</tbody></table></div>

<div class="conditions">
  <div class="condition-box"><h4>Compris par pi\u00E8ce</h4><ul>${(this.tarifsDev.included || []).map(x => `<li>${esc(x)}</li>`).join('')}</ul></div>
  <div class="condition-box"><h4>\u00C0 fournir par le client</h4><ul><li>Cahier des charges / r\u00E9f\u00E9rences visuelles</li><li>Validation des \u00E9tapes interm\u00E9diaires</li></ul></div>
</div>

<div class="note">Une modification est consid\u00E9r\u00E9e comme \u00AB l\u00E9g\u00E8re \u00BB si elle n'implique pas de reprise du patronage (ex : ajustement de longueur, d'aisance). Toute modification n\u00E9cessitant un nouveau patron ou une nouvelle toile est factur\u00E9e en tant que toile suppl\u00E9mentaire.</div>

<div class="condition-box border" style="margin-top:10px"><h4>R\u00E8glement</h4>
  <ul style="${data.paymentTerms === 'staged' ? '' : 'columns:2'}">${reglementHtml}</ul>
  <p style="font-size:8px;margin-top:6px;line-height:1.5">B\u00E9n\u00E9ficiaire : ${esc(co.beneficiaire)}<br>IBAN ${esc(co.iban)} \u00B7 BIC ${esc(co.bic)}</p>
</div>

<div class="signature-area">
  <div class="signature-box"><label>Date et signature du client</label><div class="signature-line"></div></div>
  <div class="signature-box"><label>Pour Reworked</label><div class="signature-line"></div></div>
</div>

<div class="footer">${esc(co.emetteur)} \u00B7 ${esc(co.mentionLegale)}<br>${esc(co.address)} \u00B7 ${esc(co.tel)} \u00B7 ${esc(co.email)}</div>

</body>
</html>`;
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 GRILLE TARIFAIRE CONFECTION \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  renderTarifsConfection() {
    const c = document.getElementById('tarifsConfectionGrid'); if (!c) return;
    const tc = this.tarifsConfection;
    const esc = s => this.escHtml(String(s == null ? '' : s));

    const catsG = {};
    Object.entries(tc.garments || {}).forEach(([k, v]) => { const cat = v.cat || 'Autre'; if (!catsG[cat]) catsG[cat] = []; catsG[cat].push({ key: k, ...v }); });

    let html = `<div class="tarif-section"><h4>V\u00EAtements de r\u00E9f\u00E9rence (pi\u00E8ces non d\u00E9velopp\u00E9es par nous)</h4>`;
    html += Object.entries(catsG).map(([cat, items]) => `
      <div style="padding:5px 12px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:var(--88);background:var(--f9)">${esc(cat)}</div>
      ${items.map(g => `<div class="tarif-row">
        <div class="tarif-label">${esc(g.label)}</div>
        <input class="tarif-input" type="number" step="1" value="${g.basePrice}" data-conf-garment-key="${g.key}">
        <div class="tarif-unit">\u20AC r\u00E9f.</div>
        <button class="tarif-delete" onclick="app.deleteConfGarment('${g.key}')" title="Supprimer">\u2715</button>
      </div>`).join('')}
      <div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.showAddConfGarment('${esc(cat)}')">+ Ajouter dans ${esc(cat)}</button></div>
    `).join('');
    html += `<div class="tarif-add-row"><button class="btn btn-primary btn-sm" onclick="app.showAddConfGarment('')">+ Nouvelle cat\u00E9gorie</button></div>`;
    html += `</div>`;

    html += `<div class="tarif-section"><h4>Coefficients de complexit\u00E9</h4>`;
    html += Object.entries(tc.coefficients || {}).map(([k, v]) => `<div class="tarif-row">
      <div class="tarif-label">${esc(v.label)}</div>
      <input class="tarif-input" type="number" step="0.01" value="${v.mult}" data-conf-coef-key="${k}">
      <div class="tarif-unit">\u00D7 r\u00E9f\u00E9rence</div>
      <button class="tarif-delete" onclick="app.deleteConfCoef('${k}')" title="Supprimer">\u2715</button>
    </div>`).join('');
    html += `<div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.showAddConfCoef()">+ Ajouter un coefficient</button></div>`;
    html += `</div>`;

    html += `<div class="tarif-section"><h4>Suggestion de coefficient selon le nombre de pi\u00E8ces \u00E0 d\u00E9couper</h4>`;
    html += (tc.pieceCountThresholds || []).map((t, i) => `<div class="tarif-row">
      <div class="tarif-label">Jusqu'\u00E0</div>
      <input class="tarif-input" type="number" step="1" value="${t.maxPieces}" data-conf-thresh-max="${i}" style="width:70px">
      <div class="tarif-unit">pi\u00E8ces \u2192</div>
      <select data-conf-thresh-coef="${i}" style="flex:1;padding:6px 8px;border:1px solid var(--dd);border-radius:6px;font-family:inherit;font-size:12px">
        ${Object.entries(tc.coefficients || {}).map(([k, v]) => `<option value="${k}" ${t.coefKey === k ? 'selected' : ''}>${esc(v.label)}</option>`).join('')}
      </select>
      <button class="tarif-delete" onclick="app.deleteConfThreshold(${i})" title="Supprimer">\u2715</button>
    </div>`).join('');
    html += `<div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.addConfThreshold()">+ Ajouter un seuil</button></div>`;
    html += `<p style="font-size:10px;color:var(--88);padding:0 12px 8px">Suggestion affich\u00E9e dans le questionnaire, non contraignante : le coefficient reste modifiable \u00E0 la main.</p>`;
    html += `</div>`;

    html += `<div class="tarif-section"><h4>Composantes incompressibles (comprises dans le prix de base)</h4>`;
    html += Object.entries(tc.operations || {}).filter(([, o]) => o.category === 'included').map(([k, o]) => `<div class="tarif-row">
      <div class="tarif-label">${esc(o.label)}</div>
      <input class="tarif-input" type="number" step="1" value="${o.pct}" data-conf-op-pct="${k}" style="width:70px">
      <div class="tarif-unit">% du prix de base</div>
      <button class="tarif-delete" onclick="app.deleteConfOperation('${k}')" title="Supprimer">\u2715</button>
    </div>`).join('');
    html += `<div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.showAddConfOperation('included')">+ Ajouter une composante incompressible</button></div>`;
    html += `<p style="font-size:10px;color:var(--88);padding:0 12px 8px">Comprises par d\u00E9faut dans chaque devis ; d\u00E9cochables ligne par ligne en cas exceptionnel (retire le % correspondant du prix de base). Ces % devraient id\u00E9alement totaliser 100%.</p>`;
    html += `</div>`;

    html += `<div class="tarif-section"><h4>Options en plus (facultatives)</h4>`;
    html += Object.entries(tc.operations || {}).filter(([, o]) => o.category !== 'included').map(([k, o]) => `<div class="tarif-row" style="flex-wrap:wrap">
      <div class="tarif-label" style="min-width:180px;flex:2">${esc(o.label)}</div>
      <select onchange="app.onConfOperationModeChange('${k}', this.value)" style="font-size:11px;padding:5px 6px;border:1px solid var(--dd);border-radius:6px;font-family:inherit">
        <option value="flat" ${o.mode === 'flat' ? 'selected' : ''}>\u20AC fixe</option>
        <option value="flat_qty" ${o.mode === 'flat_qty' ? 'selected' : ''}>\u20AC fixe / pi\u00E8ce</option>
        <option value="pct" ${o.mode === 'pct' ? 'selected' : ''}>% du prix de r\u00E9f\u00E9rence</option>
        <option value="pct_qty" ${o.mode === 'pct_qty' ? 'selected' : ''}>% du prix de r\u00E9f\u00E9rence \u00D7 quantit\u00E9</option>
      </select>
      <input class="tarif-input" type="number" step="0.01" value="${o.value}" data-conf-op-key="${k}" style="width:70px">
      <div class="tarif-unit">${o.mode.startsWith('pct') ? '%' : '\u20AC'}${o.mode.endsWith('_qty') ? ' / unit\u00E9' : ''}</div>
      <label style="display:flex;align-items:center;gap:4px;font-size:10px;color:var(--66)"><input type="checkbox" data-conf-op-default="${k}" ${o.defaultChecked ? 'checked' : ''}> par d\u00E9faut</label>
      <button class="tarif-delete" onclick="app.deleteConfOperation('${k}')" title="Supprimer">\u2715</button>
    </div>`).join('');
    html += `<div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.showAddConfOperation('addon')">+ Ajouter une option</button></div>`;
    html += `</div>`;

    html += `<div class="tarif-section"><h4>Paliers d\u00E9gressifs (selon la quantit\u00E9 de chaque ligne)</h4>`;
    html += (tc.quantityTiers || []).map((t, i) => `<div class="tarif-row">
      <div class="tarif-label">\u00C0 partir de</div>
      <input class="tarif-input" type="number" step="1" value="${t.minQty}" data-conf-tier-min="${i}" style="width:70px">
      <div class="tarif-unit">pi\u00E8ces</div>
      <input type="text" value="${esc(t.label || '')}" data-conf-tier-label="${i}" placeholder="Libell\u00E9 (ex: 10-24)" style="width:110px;font-size:11px;padding:6px 8px;border:1px solid var(--dd);border-radius:6px;font-family:inherit">
      <input class="tarif-input" type="number" step="0.01" value="${t.mult}" data-conf-tier-mult="${i}" style="width:70px">
      <div class="tarif-unit">\u00D7 prix fa\u00E7on</div>
      <button class="tarif-delete" onclick="app.deleteConfTier(${i})" title="Supprimer">\u2715</button>
    </div>`).join('');
    html += `<div class="tarif-add-row"><button class="btn btn-ghost btn-sm" onclick="app.addConfTier()">+ Ajouter un palier</button></div>`;
    html += `</div>`;

    html += `<div class="tarif-section"><h4>Ouverture de dossier (pi\u00E8ces non d\u00E9velopp\u00E9es par nous)</h4>
      <div class="tarif-row">
        <div class="tarif-label">Forfait analyse patron externe</div>
        <input class="tarif-input" type="number" step="1" value="${tc.ouvertureDossierPrice || 70}" id="confOuvertureDossierPrice">
        <div class="tarif-unit">\u20AC</div>
      </div>
    </div>`;

    c.innerHTML = html;
  }

  showAddConfGarment(cat) {
    const old = document.getElementById('addTarifConfForm'); if (old) old.remove();
    const container = document.getElementById('tarifsConfectionGrid');
    const el = document.createElement('div');
    el.id = 'addTarifConfForm';
    el.className = 'tarif-add-form';
    el.innerHTML = `
      <h4 style="font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:10px">${cat ? `Ajouter dans "${cat}"` : 'Nouveau v\u00EAtement (nouvelle cat\u00E9gorie)'}</h4>
      ${!cat ? `<div class="tarif-add-field"><label>Cat\u00E9gorie</label><input type="text" id="newConfGarmentCat" placeholder="Ex: Accessoires"></div>` : ''}
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div class="tarif-add-field" style="flex:2"><label>D\u00E9signation</label><input type="text" id="newConfGarmentLabel" placeholder="Ex: Robe"></div>
        <div class="tarif-add-field" style="flex:0 0 100px"><label>Prix de r\u00E9f\u00E9rence</label><input type="number" step="1" id="newConfGarmentPrice" placeholder="0"></div>
      </div>
      <div style="display:flex;gap:8px;margin-top:10px">
        <button class="btn btn-primary btn-sm" onclick="app.addConfGarment('${this.escHtml(cat)}')">Ajouter</button>
        <button class="btn btn-ghost btn-sm" onclick="document.getElementById('addTarifConfForm').remove()">Annuler</button>
      </div>
    `;
    container.appendChild(el);
    el.scrollIntoView({ behavior: 'smooth' });
    (document.getElementById(cat ? 'newConfGarmentLabel' : 'newConfGarmentCat') || {}).focus?.();
  }

  addConfGarment(existingCat) {
    const label = (document.getElementById('newConfGarmentLabel').value || '').trim();
    const basePrice = parseFloat(document.getElementById('newConfGarmentPrice').value) || 0;
    const catInput = document.getElementById('newConfGarmentCat');
    const cat = existingCat || (catInput ? catInput.value.trim() : 'Autre');
    if (!label) { this.toast('Renseignez la d\u00E9signation', 'error'); return; }
    if (!cat) { this.toast('Renseignez la cat\u00E9gorie', 'error'); return; }
    const key = 'garment_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.tarifsConfection.garments[key] = { label, basePrice, cat };
    document.getElementById('addTarifConfForm')?.remove();
    this.renderTarifsConfection();
    this.toast(`V\u00EAtement "${label}" ajout\u00E9 dans ${cat}`, 'success');
  }

  deleteConfGarment(key) {
    const g = this.tarifsConfection.garments[key]; if (!g) return;
    if (!confirm(`Supprimer "${g.label}" ?`)) return;
    delete this.tarifsConfection.garments[key];
    this.renderTarifsConfection();
    this.toast('V\u00EAtement supprim\u00E9');
  }

  showAddConfCoef() {
    const old = document.getElementById('addTarifConfForm'); if (old) old.remove();
    const container = document.getElementById('tarifsConfectionGrid');
    const el = document.createElement('div');
    el.id = 'addTarifConfForm';
    el.className = 'tarif-add-form';
    el.innerHTML = `
      <h4 style="font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:10px">Nouveau coefficient</h4>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div class="tarif-add-field" style="flex:2"><label>D\u00E9signation</label><input type="text" id="newConfCoefLabel" placeholder="Ex: Coef 4 \u2014 Pi\u00E8ce sur-mesure"></div>
        <div class="tarif-add-field" style="flex:0 0 100px"><label>Multiplicateur</label><input type="number" step="0.01" id="newConfCoefMult" placeholder="1.00"></div>
      </div>
      <div style="display:flex;gap:8px;margin-top:10px">
        <button class="btn btn-primary btn-sm" onclick="app.addConfCoef()">Ajouter</button>
        <button class="btn btn-ghost btn-sm" onclick="document.getElementById('addTarifConfForm').remove()">Annuler</button>
      </div>
    `;
    container.appendChild(el);
    el.scrollIntoView({ behavior: 'smooth' });
    document.getElementById('newConfCoefLabel')?.focus();
  }

  addConfCoef() {
    const label = (document.getElementById('newConfCoefLabel').value || '').trim();
    const mult = parseFloat(document.getElementById('newConfCoefMult').value) || 1;
    if (!label) { this.toast('Renseignez la d\u00E9signation', 'error'); return; }
    const key = 'coef_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.tarifsConfection.coefficients[key] = { label, mult };
    document.getElementById('addTarifConfForm')?.remove();
    this.renderTarifsConfection();
    this.toast(`Coefficient "${label}" ajout\u00E9`, 'success');
  }

  deleteConfCoef(key) {
    const co = this.tarifsConfection.coefficients[key]; if (!co) return;
    if (!confirm(`Supprimer "${co.label}" ?`)) return;
    delete this.tarifsConfection.coefficients[key];
    this.renderTarifsConfection();
    this.toast('Coefficient supprim\u00E9');
  }

  showAddConfOperation(category) {
    const old = document.getElementById('addTarifConfForm'); if (old) old.remove();
    const container = document.getElementById('tarifsConfectionGrid');
    const el = document.createElement('div');
    el.id = 'addTarifConfForm';
    el.className = 'tarif-add-form';
    if (category === 'included') {
      el.innerHTML = `
        <h4 style="font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:10px">Nouvelle composante incompressible</h4>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          <div class="tarif-add-field" style="flex:2"><label>D\u00E9signation</label><input type="text" id="newConfOpLabel" placeholder="Ex: Repassage final"></div>
          <div class="tarif-add-field" style="flex:0 0 100px"><label>% du prix de base</label><input type="number" step="1" id="newConfOpValue" placeholder="0"></div>
        </div>
        <div style="display:flex;gap:8px;margin-top:10px">
          <button class="btn btn-primary btn-sm" onclick="app.addConfOperation('included')">Ajouter</button>
          <button class="btn btn-ghost btn-sm" onclick="document.getElementById('addTarifConfForm').remove()">Annuler</button>
        </div>
      `;
    } else {
      el.innerHTML = `
        <h4 style="font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:10px">Nouvelle option facultative</h4>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          <div class="tarif-add-field" style="flex:2"><label>D\u00E9signation</label><input type="text" id="newConfOpLabel" placeholder="Ex: Broderie"></div>
          <div class="tarif-add-field" style="flex:0 0 180px"><label>Mode de calcul</label>
            <select id="newConfOpMode">
              <option value="flat">\u20AC fixe</option>
              <option value="flat_qty">\u20AC fixe / pi\u00E8ce</option>
              <option value="pct">% du prix de r\u00E9f\u00E9rence</option>
              <option value="pct_qty">% du prix de r\u00E9f\u00E9rence \u00D7 quantit\u00E9</option>
            </select>
          </div>
          <div class="tarif-add-field" style="flex:0 0 90px"><label>Valeur</label><input type="number" step="0.01" id="newConfOpValue" placeholder="0"></div>
        </div>
        <div style="display:flex;gap:8px;margin-top:10px">
          <button class="btn btn-primary btn-sm" onclick="app.addConfOperation('addon')">Ajouter</button>
          <button class="btn btn-ghost btn-sm" onclick="document.getElementById('addTarifConfForm').remove()">Annuler</button>
        </div>
      `;
    }
    container.appendChild(el);
    el.scrollIntoView({ behavior: 'smooth' });
    document.getElementById('newConfOpLabel')?.focus();
  }

  addConfOperation(category) {
    const label = (document.getElementById('newConfOpLabel').value || '').trim();
    const value = parseFloat(document.getElementById('newConfOpValue').value) || 0;
    if (!label) { this.toast('Renseignez la d\u00E9signation', 'error'); return; }
    const key = 'op_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    if (category === 'included') {
      this.tarifsConfection.operations[key] = { label, category: 'included', pct: value };
    } else {
      const mode = document.getElementById('newConfOpMode').value || 'flat';
      this.tarifsConfection.operations[key] = { label, category: 'addon', mode, value, unitLabel: 'pi\u00E8ce', defaultChecked: false };
    }
    document.getElementById('addTarifConfForm')?.remove();
    this.renderTarifsConfection();
    this.toast(`Op\u00E9ration "${label}" ajout\u00E9e`, 'success');
  }

  deleteConfOperation(key) {
    const o = this.tarifsConfection.operations[key]; if (!o) return;
    if (!confirm(`Supprimer "${o.label}" ?`)) return;
    delete this.tarifsConfection.operations[key];
    this.renderTarifsConfection();
    this.toast('Op\u00E9ration supprim\u00E9e');
  }

  onConfOperationModeChange(key, mode) {
    if (this.tarifsConfection.operations[key]) this.tarifsConfection.operations[key].mode = mode;
    this.renderTarifsConfection();
  }

  addConfThreshold() {
    this.tarifsConfection.pieceCountThresholds = this.tarifsConfection.pieceCountThresholds || [];
    const firstCoef = Object.keys(this.tarifsConfection.coefficients)[0] || '';
    this.tarifsConfection.pieceCountThresholds.push({ maxPieces: 10, coefKey: firstCoef });
    this.renderTarifsConfection();
  }
  deleteConfThreshold(i) {
    this.tarifsConfection.pieceCountThresholds.splice(i, 1);
    this.renderTarifsConfection();
  }

  addConfTier() {
    this.tarifsConfection.quantityTiers = this.tarifsConfection.quantityTiers || [];
    this.tarifsConfection.quantityTiers.push({ minQty: 1, mult: 1, label: '' });
    this.renderTarifsConfection();
  }
  deleteConfTier(i) {
    this.tarifsConfection.quantityTiers.splice(i, 1);
    this.renderTarifsConfection();
  }

  async saveTarifsConfection() {
    document.querySelectorAll('[data-conf-garment-key]').forEach(el => { const k = el.dataset.confGarmentKey; if (this.tarifsConfection.garments[k]) this.tarifsConfection.garments[k].basePrice = parseFloat(el.value) || 0; });
    document.querySelectorAll('[data-conf-coef-key]').forEach(el => { const k = el.dataset.confCoefKey; if (this.tarifsConfection.coefficients[k]) this.tarifsConfection.coefficients[k].mult = parseFloat(el.value) || 1; });
    document.querySelectorAll('[data-conf-op-key]').forEach(el => { const k = el.dataset.confOpKey; if (this.tarifsConfection.operations[k]) this.tarifsConfection.operations[k].value = parseFloat(el.value) || 0; });
    document.querySelectorAll('[data-conf-op-default]').forEach(el => { const k = el.dataset.confOpDefault; if (this.tarifsConfection.operations[k]) this.tarifsConfection.operations[k].defaultChecked = el.checked; });
    document.querySelectorAll('[data-conf-op-pct]').forEach(el => { const k = el.dataset.confOpPct; if (this.tarifsConfection.operations[k]) this.tarifsConfection.operations[k].pct = parseFloat(el.value) || 0; });
    document.querySelectorAll('[data-conf-thresh-max]').forEach(el => { const i = parseInt(el.dataset.confThreshMax); if (this.tarifsConfection.pieceCountThresholds[i]) this.tarifsConfection.pieceCountThresholds[i].maxPieces = parseFloat(el.value) || 0; });
    document.querySelectorAll('[data-conf-thresh-coef]').forEach(el => { const i = parseInt(el.dataset.confThreshCoef); if (this.tarifsConfection.pieceCountThresholds[i]) this.tarifsConfection.pieceCountThresholds[i].coefKey = el.value; });
    document.querySelectorAll('[data-conf-tier-min]').forEach(el => { const i = parseInt(el.dataset.confTierMin); if (this.tarifsConfection.quantityTiers[i]) this.tarifsConfection.quantityTiers[i].minQty = parseFloat(el.value) || 0; });
    document.querySelectorAll('[data-conf-tier-label]').forEach(el => { const i = parseInt(el.dataset.confTierLabel); if (this.tarifsConfection.quantityTiers[i]) this.tarifsConfection.quantityTiers[i].label = el.value; });
    document.querySelectorAll('[data-conf-tier-mult]').forEach(el => { const i = parseInt(el.dataset.confTierMult); if (this.tarifsConfection.quantityTiers[i]) this.tarifsConfection.quantityTiers[i].mult = parseFloat(el.value) || 1; });
    const dossierEl = document.getElementById('confOuvertureDossierPrice');
    if (dossierEl) this.tarifsConfection.ouvertureDossierPrice = parseFloat(dossierEl.value) || 0;
    try { await wfetch(`${WORKER_URL}/tarifs-confection`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.tarifsConfection) }); this.toast('Grille confection sauvegard\u00E9e', 'success'); } catch (e) { this.toast('Erreur', 'error'); }
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 DEVIS CONFECTION (questionnaire + calcul d\u00E9terministe) \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  openConfectionBuilder() {
    if (!this.company) this.company = this._defaultCompany();
    if (!Object.keys(this.tarifsConfection.garments || {}).length) this.tarifsConfection = this._defaultTarifsConfection();
    const firstGarment = Object.keys(this.tarifsConfection.garments)[0] || '';
    const firstCoef = Object.keys(this.tarifsConfection.coefficients)[0] || '';
    this._conf = {
      clientName: '', clientType: 'professionnel', clientAddress: '', clientSiret: '',
      objet: '', delai: '',
      lines: [this._confBlankLine(firstGarment, firstCoef)],
    };
    this._confEditingId = null;
    document.getElementById('confectionDevisOverlay').classList.add('active');
    this.renderConfectionBuilder();
  }

  _confBlankLine(garment, coef) {
    return {
      sourceType: 'catalog', garment, coef,
      customLabel: '', customBasePrice: 0,
      catalogProductId: '',
      sourceDevId: '', sourceDevLineIndex: null,
      importedLabel: '', importedBasePrice: 0, importedCoefLabel: '', importedCoefMult: 1,
      pieceCount: 0, quantity: 1,
      removedIncluded: [], // composantes incompressibles retirées (cas exceptionnel) : aucune par défaut
      opts: Object.fromEntries(Object.entries(this.tarifsConfection.operations || {}).filter(([, o]) => o.category !== 'included' && o.defaultChecked).map(([k]) => [k, 1])),
      matiere: { enabled: false, desc: '', amountPerPiece: 0 },
      dossier: { enabled: false, amount: this.tarifsConfection.ouvertureDossierPrice || 70 },
      note: '', addons: [],
    };
  }

  openConfectionBuilderFromSnapshot(devisId) {
    const d = this.devis.find(x => x.id === devisId);
    if (!d || !d.snap) return false;
    if (!this.company) this.company = this._defaultCompany();
    if (!Object.keys(this.tarifsConfection.garments || {}).length) this.tarifsConfection = this._defaultTarifsConfection();
    this._conf = {
      ...d.snap,
      lines: (d.snap.lines || []).map(l => ({
        ...l, opts: { ...(l.opts || {}) }, matiere: { ...(l.matiere || {}) }, dossier: { ...(l.dossier || {}) }, addons: (l.addons || []).map(a => ({ ...a })),
      })),
    };
    this._confEditingId = devisId;
    document.getElementById('confectionDevisOverlay').classList.add('active');
    this.renderConfectionBuilder();
    return true;
  }

  closeConfectionDevisBuilder() { document.getElementById('confectionDevisOverlay').classList.remove('active'); this._conf = null; this._confEditingId = null; }

  _confHasContent() {
    if (!this._conf) return false;
    if ((this._conf.clientName || '').trim()) return true;
    return (this._conf.lines || []).some(l => l.garment || l.customLabel || l.sourceDevId || l.catalogProductId);
  }

  attemptCloseConfectionDevis() {
    if (!this._conf) { this.closeConfectionDevisBuilder(); return; }
    this._confReadForm();
    if (!this._confHasContent()) { this.closeConfectionDevisBuilder(); return; }
    if (!confirm('Fermer sans enregistrer ce devis confection ?')) return;
    this.closeConfectionDevisBuilder();
  }

  _confReadForm() {
    if (!this._conf) return;
    const g = id => document.getElementById(id);
    this._conf.clientName = g('confClientName')?.value || '';
    this._conf.clientType = g('confClientType')?.value || 'professionnel';
    this._conf.clientAddress = g('confClientAddress')?.value || '';
    this._conf.clientSiret = g('confClientSiret')?.value || '';
    this._conf.objet = g('confObjet')?.value || '';
    this._conf.delai = g('confDelai')?.value || '';
    this._confReadLines();
  }

  _confReadLines() {
    if (!this._conf) return;
    const rows = document.querySelectorAll('#confLinesBody > .conf-line');
    if (!rows.length) return;
    const lines = [];
    rows.forEach(row => {
      const g = sel => row.querySelector(`[data-cl="${sel}"]`);
      const sourceType = g('sourceType')?.value || 'catalog';
      const garment = g('garment')?.value || '';
      const coef = g('coef')?.value || '';
      const customLabel = g('customLabel')?.value || '';
      const customBasePrice = parseFloat(g('customBasePrice')?.value) || 0;
      const catalogProductId = g('catalogProductId')?.value || '';
      const sourceDevId = g('sourceDevId')?.value || '';
      const sdli = g('sourceDevLineIndex')?.value;
      const sourceDevLineIndex = (sdli === undefined || sdli === '') ? null : parseInt(sdli);
      const importedLabel = g('importedLabel')?.value || '';
      const importedBasePrice = parseFloat(g('importedBasePrice')?.value) || 0;
      const importedCoefLabel = g('importedCoefLabel')?.value || '';
      const importedCoefMult = parseFloat(g('importedCoefMult')?.value) || 1;
      const pieceCount = parseFloat(g('pieceCount')?.value) || 0;
      const quantity = parseFloat(g('quantity')?.value) || 1;
      const note = g('note')?.value || '';
      const removedIncluded = [];
      row.querySelectorAll('[data-cl-included]').forEach(chk => {
        if (!chk.checked) removedIncluded.push(chk.dataset.clIncluded);
      });
      const opts = {};
      row.querySelectorAll('[data-cl-opt]').forEach(chk => {
        if (!chk.checked) return;
        const key = chk.dataset.clOpt;
        const qtyInput = row.querySelector(`[data-cl-qty="${key}"]`);
        opts[key] = qtyInput ? (parseFloat(qtyInput.value) || 1) : 1;
      });
      const matiere = {
        enabled: g('matiereEnabled')?.checked || false,
        desc: g('matiereDesc')?.value || '',
        amountPerPiece: parseFloat(g('matiereAmount')?.value) || 0,
      };
      const dossier = {
        enabled: g('dossierEnabled')?.checked || false,
        amount: parseFloat(g('dossierAmount')?.value) || 0,
      };
      const addons = [];
      row.querySelectorAll('[data-cl-addon]').forEach(chk => {
        if (!chk.checked) return;
        const key = chk.dataset.clAddon;
        const qtyInput = row.querySelector(`[data-cl-addon-qty="${key}"]`);
        addons.push({ key, qty: qtyInput ? (parseFloat(qtyInput.value) || 0) : 0 });
      });
      lines.push({ sourceType, garment, coef, customLabel, customBasePrice, catalogProductId, sourceDevId, sourceDevLineIndex, importedLabel, importedBasePrice, importedCoefLabel, importedCoefMult, pieceCount, quantity, note, removedIncluded, opts, matiere, dossier, addons });
    });
    this._conf.lines = lines;
  }

  confAddLine() {
    this._confReadForm();
    const firstGarment = Object.keys(this.tarifsConfection.garments)[0] || '';
    const firstCoef = Object.keys(this.tarifsConfection.coefficients)[0] || '';
    this._conf.lines.push(this._confBlankLine(firstGarment, firstCoef));
    this.renderConfectionBuilder();
  }

  confRemoveLine(i) {
    this._confReadForm();
    this._conf.lines.splice(i, 1);
    if (!this._conf.lines.length) {
      const firstGarment = Object.keys(this.tarifsConfection.garments)[0] || '';
      const firstCoef = Object.keys(this.tarifsConfection.coefficients)[0] || '';
      this._conf.lines.push(this._confBlankLine(firstGarment, firstCoef));
    }
    this.renderConfectionBuilder();
  }

  confOnSourceTypeChange(select) {
    this._confReadForm();
    const idx = parseInt(select.closest('.conf-line').dataset.idx);
    if (this._conf.lines[idx]) this._conf.lines[idx].sourceType = select.value;
    this.renderConfectionBuilder();
  }

  confOnDevSelect(select) {
    this._confReadForm();
    const idx = parseInt(select.closest('.conf-line').dataset.idx);
    const line = this._conf.lines[idx];
    if (!line) return;
    line.sourceDevId = select.value;
    line.sourceDevLineIndex = null;
    line.importedLabel = ''; line.importedBasePrice = 0; line.importedCoefLabel = ''; line.importedCoefMult = 1;
    this.renderConfectionBuilder();
  }

  confOnDevLineSelect(select) {
    this._confReadForm();
    const idx = parseInt(select.closest('.conf-line').dataset.idx);
    const line = this._conf.lines[idx];
    if (!line) return;
    const lineIdx = select.value === '' ? null : parseInt(select.value);
    const r = lineIdx != null ? this._confResolveDevLine(line.sourceDevId, lineIdx) : null;
    line.sourceDevLineIndex = lineIdx;
    line.importedLabel = r ? r.label : '';
    line.importedBasePrice = r ? r.basePrice : 0;
    line.importedCoefLabel = r ? r.coefLabel : '';
    line.importedCoefMult = r ? r.coefMult : 1;
    this.renderConfectionBuilder();
  }

  confOnCatalogueProductSelect(select) {
    this._confReadForm();
    const idx = parseInt(select.closest('.conf-line').dataset.idx);
    const line = this._conf.lines[idx];
    if (!line) return;
    line.catalogProductId = select.value;
    const p = this.catalogue.find(x => x.id === select.value);
    if (p) this._applyCatalogueDefaultsToLine(line, p);
    this.renderConfectionBuilder();
  }

  // R\u00E9sout une ligne d'un devis d\u00E9veloppement import\u00E9 (lecture seule, ne touche \u00E0 aucune fonction du module dev)
  _confResolveDevLine(devId, lineIndex) {
    const d = this.devis.find(x => x.id === devId);
    const line = d?.snap?.lines?.[lineIndex];
    if (!line) return null;
    let label, basePrice;
    if (line.garment === '__custom__') {
      label = (line.customLabel || '').trim() || 'Pi\u00E8ce sur-mesure';
      basePrice = this._num(line.customBasePrice);
    } else {
      const g = this.tarifsDev.garments[line.garment];
      if (!g) return null;
      label = g.label; basePrice = this._num(g.basePrice);
    }
    const coef = this.tarifsDev.coefficients[line.coef];
    if (!coef) return null;
    return { label, basePrice, coefLabel: coef.label, coefMult: this._num(coef.mult) || 1 };
  }

  confSuggestCoef(input) {
    const row = input.closest('.conf-line');
    const val = parseFloat(input.value) || 0;
    const suggestEl = row?.querySelector('.conf-coef-suggest');
    if (!suggestEl) return;
    if (val <= 0) { suggestEl.textContent = ''; return; }
    const thresholds = (this.tarifsConfection.pieceCountThresholds || []).slice().sort((a, b) => a.maxPieces - b.maxPieces);
    const match = thresholds.find(t => val <= t.maxPieces) || thresholds[thresholds.length - 1];
    const coef = match ? this.tarifsConfection.coefficients[match.coefKey] : null;
    suggestEl.textContent = coef ? `Suggestion : ${coef.label}` : '';
  }

  confToggleOption(checkbox) {
    const qtyInput = checkbox.closest('.dev-opt-row')?.querySelector('[data-cl-qty]');
    if (qtyInput) qtyInput.style.display = checkbox.checked ? 'inline-block' : 'none';
    this.confRecalc();
  }

  confToggleMatiere(checkbox) {
    const fields = checkbox.closest('.dev-matiere-row')?.querySelector('.dev-matiere-fields');
    if (fields) fields.style.display = checkbox.checked ? 'flex' : 'none';
    this.confRecalc();
  }

  confToggleDossier(checkbox) {
    const amountInput = checkbox.closest('.dev-base-toggle')?.querySelector('[data-cl="dossierAmount"]');
    if (amountInput) amountInput.style.display = checkbox.checked ? 'inline-block' : 'none';
    this.confRecalc();
  }

  confToggleAddon(checkbox) {
    const qtyInput = checkbox.closest('.conf-addon-row')?.querySelector('[data-cl-addon-qty]');
    if (qtyInput) qtyInput.style.display = checkbox.checked ? 'inline-block' : 'none';
    this.confRecalc();
  }

  confToggleAddonsPanel(i) {
    const panel = document.getElementById(`confAddonsPanel-${i}`);
    if (panel) panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
  }

  async confSaveCustomGarmentToGrid(i) {
    this._confReadForm();
    const line = this._conf.lines[i];
    if (!line || line.sourceType !== 'custom') return;
    const label = (line.customLabel || '').trim();
    const basePrice = this._num(line.customBasePrice);
    if (!label) { this.toast("Donnez un nom \u00E0 la pi\u00E8ce avant de l'ajouter \u00E0 la grille", 'error'); return; }
    const key = 'garment_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.tarifsConfection.garments[key] = { label, basePrice, cat: 'Sur-mesure' };
    try { await wfetch(`${WORKER_URL}/tarifs-confection`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.tarifsConfection) }); } catch (e) {}
    line.sourceType = 'catalog';
    line.garment = key;
    this.renderConfectionBuilder();
    this.toast(`"${label}" ajout\u00E9 \u00E0 la grille confection (cat\u00E9gorie Sur-mesure)`, 'success');
  }

  confRecalc() {
    if (!this._conf) return;
    this._confReadLines();
    const c = this._confCompute();
    document.querySelectorAll('#confLinesBody > .conf-line').forEach((row, i) => {
      const el = row.querySelector('[data-cl-total]');
      if (el && c.lines[i]) el.textContent = this._eur(c.lines[i].subtotal);
      const tierEl = row.querySelector('[data-conf-tier]');
      if (tierEl) tierEl.textContent = (c.lines[i] && c.lines[i].tier && c.lines[i].tier.label) ? `Palier : ${c.lines[i].tier.label}` : '';
    });
    const totalEl = document.getElementById('confGrandTotal');
    if (totalEl) totalEl.textContent = this._eur(c.total);
  }

  renderConfectionBuilder() {
    const body = document.getElementById('confectionDevisBuilderBody');
    if (!body || !this._conf) return;
    const conf = this._conf;
    const esc = s => this.escHtml(String(s == null ? '' : s));

    const hdr = document.querySelector('#confectionDevisOverlay .preview-header span');
    if (hdr) hdr.textContent = this._confEditingId ? `Modifier devis confection ${this._confEditingId}` : 'Nouveau devis confection';
    const genBtn = document.getElementById('confectionDevisGenerateBtn');
    if (genBtn) genBtn.textContent = this._confEditingId ? 'Mettre \u00E0 jour & aper\u00E7u' : 'G\u00E9n\u00E9rer & aper\u00E7u';

    body.innerHTML = `
      <div class="fb-section-title">Client</div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Nom du client</label>
          <input type="text" id="confClientName" list="clientSuggestions" value="${esc(conf.clientName)}" placeholder="Ex: RAWRUN">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>Type</label>
          <select id="confClientType">
            <option value="professionnel" ${conf.clientType === 'professionnel' ? 'selected' : ''}>Professionnel</option>
            <option value="particulier" ${conf.clientType === 'particulier' ? 'selected' : ''}>Particulier</option>
          </select>
        </div>
      </div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Adresse</label>
          <input type="text" id="confClientAddress" value="${esc(conf.clientAddress)}">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>SIRET</label>
          <input type="text" id="confClientSiret" value="${esc(conf.clientSiret)}">
        </div>
      </div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Objet du devis</label>
          <input type="text" id="confObjet" value="${esc(conf.objet)}" placeholder="Ex: Production collection SS27">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>D\u00E9lais souhait\u00E9s</label>
          <input type="text" id="confDelai" value="${esc(conf.delai)}" placeholder="Ex: Avant le 15 juin">
        </div>
      </div>

      <div class="fb-section-title">Pi\u00E8ces \u00E0 produire</div>
      <div id="confLinesBody">
        ${conf.lines.map((l, i) => this._confLineHtml(l, i)).join('')}
      </div>
      <button class="btn btn-ghost btn-sm" onclick="app.confAddLine()" style="margin-top:8px">+ Ajouter une pi\u00E8ce</button>

      <div class="fb-totals" style="margin-top:16px">
        <div class="fb-total-net"><span>Total devis confection</span><strong id="confGrandTotal">\u2014</strong></div>
      </div>

      <div class="fb-section-title">Ajuster avec Claude (optionnel)</div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <input type="text" id="confClaudeInstruction" placeholder="Ex : ajoute une remise de 5 %, pr\u00E9cise le d\u00E9lai de livraison...">
        </div>
        <button class="btn btn-ghost btn-sm" onclick="app.adjustConfectionDevisWithClaude()" style="flex-shrink:0">Ajuster avec Claude</button>
      </div>
    `;
    this.confRecalc();
  }

  _confLineHtml(l, i) {
    const tc = this.tarifsConfection;
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const sourceType = l.sourceType || 'catalog';

    const garmentOpts = Object.entries(tc.garments || {}).map(([k, v]) => `<option value="${k}" ${l.garment === k ? 'selected' : ''}>${esc(v.label)} \u2014 ${this._eur(v.basePrice)}</option>`).join('');
    const coefOpts = Object.entries(tc.coefficients || {}).map(([k, v]) => `<option value="${k}" ${l.coef === k ? 'selected' : ''}>${esc(v.label)}</option>`).join('');

    const devOptions = this.devis.filter(d => d.type === 'dev' && d.snap).slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    const devSelectOpts = ['<option value="">Choisir un devis...</option>'].concat(
      devOptions.map(d => `<option value="${d.id}" ${l.sourceDevId === d.id ? 'selected' : ''}>${esc(d.id)} \u00B7 ${esc(d.client)}</option>`)
    ).join('');

    let devLineSelectOpts = '<option value="">\u2014</option>';
    if (l.sourceDevId) {
      const d = this.devis.find(x => x.id === l.sourceDevId);
      const dlines = d?.snap?.lines || [];
      devLineSelectOpts = dlines.map((dl, idx) => {
        const r = this._confResolveDevLine(l.sourceDevId, idx);
        return `<option value="${idx}" ${l.sourceDevLineIndex === idx ? 'selected' : ''}>${esc(r ? `${r.label} \u2014 ${r.coefLabel}` : 'Pi\u00E8ce')}</option>`;
      }).join('') || '<option value="">Aucune pi\u00E8ce dans ce devis</option>';
    }

    const catalogueOpts = ['<option value="">Choisir un produit...</option>'].concat(
      this.catalogue.slice()
        .sort((a, b) => (a.client || '').localeCompare(b.client || '') || (a.label || '').localeCompare(b.label || ''))
        .map(p => `<option value="${p.id}" ${l.catalogProductId === p.id ? 'selected' : ''}>${esc(p.client)} \u2014 ${esc(p.label)} (${this._eur(p.basePrice)})</option>`)
    ).join('');

    // Composantes incompressibles du prix de base (coupe, montage, finitions, contr\u00F4le qualit\u00E9) :
    // comprises par d\u00E9faut, d\u00E9cochables ligne par ligne en cas exceptionnel (retire leur % du prix de base).
    const includedOpsHtml = Object.entries(tc.operations || {}).filter(([, o]) => o.category === 'included').map(([key, o]) => {
      const removed = (l.removedIncluded || []).includes(key);
      return `<label class="dev-opt-row">
        <input type="checkbox" data-cl-included="${key}" ${removed ? '' : 'checked'} onchange="app.confRecalc()">
        <span class="dev-opt-label">${esc(o.label)}</span>
        <span class="dev-opt-price">${removed ? `-${o.pct}% (retir\u00E9)` : `compris (${o.pct}% du prix de base)`}</span>
      </label>`;
    }).join('');

    // Vraies options facultatives, en plus du prix de base
    const addonOpsHtml = Object.entries(tc.operations || {}).filter(([, o]) => o.category !== 'included').map(([key, o]) => {
      const checked = l.opts && Object.prototype.hasOwnProperty.call(l.opts, key);
      const qty = checked ? (l.opts[key] || 1) : 1;
      const isQty = o.mode.endsWith('_qty');
      const ruleText = o.mode === 'flat' ? `${this._eur(o.value)}`
        : o.mode === 'flat_qty' ? `${this._eur(o.value)} / ${o.unitLabel || 'pi\u00E8ce'}`
        : o.mode === 'pct' ? `${o.value}% du prix de r\u00E9f\u00E9rence`
        : `${o.value}% du prix de r\u00E9f\u00E9rence / ${o.unitLabel || 'unit\u00E9'}`;
      return `<label class="dev-opt-row">
        <input type="checkbox" data-cl-opt="${key}" ${checked ? 'checked' : ''} onchange="app.confToggleOption(this)">
        <span class="dev-opt-label">${esc(o.label)}</span>
        <input type="number" step="1" min="1" value="${qty}" data-cl-qty="${key}" class="dev-opt-qty" style="display:${(checked && isQty) ? 'inline-block' : 'none'}" oninput="app.confRecalc()">
        <span class="dev-opt-price">${esc(ruleText)}</span>
      </label>`;
    }).join('');

    return `<div class="dev-line conf-line" data-idx="${i}">
      <div class="dev-line-header">
        <select data-cl="sourceType" onchange="app.confOnSourceTypeChange(this)" style="flex:1">
          <option value="catalog" ${sourceType === 'catalog' ? 'selected' : ''}>Grille confection</option>
          <option value="catalog_product" ${sourceType === 'catalog_product' ? 'selected' : ''}>S\u00E9lectionner un produit du catalogue</option>
          <option value="dev" ${sourceType === 'dev' ? 'selected' : ''}>Importer depuis un devis d\u00E9veloppement</option>
          <option value="custom" ${sourceType === 'custom' ? 'selected' : ''}>\u270E Autre (prix libre)</option>
        </select>
        <button class="tarif-delete" onclick="app.confRemoveLine(${i})" title="Supprimer cette pi\u00E8ce">\u2715</button>
      </div>

      <div class="conf-catalog-fields" style="display:${sourceType === 'catalog' ? 'flex' : 'none'};gap:8px;margin-top:8px">
        <select data-cl="garment" onchange="app.confRecalc()" style="flex:1">${garmentOpts}</select>
      </div>

      <div class="conf-source-row" style="display:${sourceType === 'catalog_product' ? 'flex' : 'none'}">
        <select data-cl="catalogProductId" onchange="app.confOnCatalogueProductSelect(this)" style="flex:1">${catalogueOpts}</select>
      </div>

      <div class="conf-source-row" style="display:${sourceType === 'dev' ? 'flex' : 'none'}">
        <select data-cl="sourceDevId" onchange="app.confOnDevSelect(this)">${devSelectOpts}</select>
        <select data-cl="sourceDevLineIndex" onchange="app.confOnDevLineSelect(this)">${devLineSelectOpts}</select>
      </div>
      <input type="hidden" data-cl="importedLabel" value="${esc(l.importedLabel || '')}">
      <input type="hidden" data-cl="importedBasePrice" value="${l.importedBasePrice || 0}">
      <input type="hidden" data-cl="importedCoefLabel" value="${esc(l.importedCoefLabel || '')}">
      <input type="hidden" data-cl="importedCoefMult" value="${l.importedCoefMult || 1}">

      <div class="dev-custom-fields" style="display:${sourceType === 'custom' ? 'flex' : 'none'}">
        <input type="text" data-cl="customLabel" value="${esc(l.customLabel || '')}" placeholder="Nom de la pi\u00E8ce" oninput="app.confRecalc()">
        <input type="number" step="1" min="0" data-cl="customBasePrice" value="${l.customBasePrice || 0}" placeholder="Prix de r\u00E9f\u00E9rence \u20AC" oninput="app.confRecalc()">
        <button type="button" class="btn btn-ghost btn-sm" onclick="app.confSaveCustomGarmentToGrid(${i})" title="Ajouter cette pi\u00E8ce \u00E0 la grille permanente">+ Grille</button>
      </div>

      <div class="conf-piece-count-row" style="display:${(sourceType === 'dev' || sourceType === 'catalog_product') ? 'none' : 'flex'}">
        <select data-cl="coef" onchange="app.confRecalc()" style="flex:1">${coefOpts}</select>
      </div>
      <div class="conf-piece-count-row" style="display:${(sourceType === 'dev' || sourceType === 'catalog_product') ? 'none' : 'flex'}">
        <input type="number" step="1" min="0" data-cl="pieceCount" value="${l.pieceCount || ''}" placeholder="Nb pi\u00E8ces \u00E0 d\u00E9couper">
        <span class="conf-coef-suggest"></span>
      </div>

      <div class="conf-piece-count-row">
        <label style="font-size:11px;color:var(--66)">Quantit\u00E9</label>
        <input type="number" step="1" min="1" data-cl="quantity" value="${l.quantity || 1}" oninput="app.confRecalc()">
        <span class="conf-tier-info" data-conf-tier></span>
      </div>

      <div class="dev-opt-group">
        <div class="dev-opt-group-title">Compris dans le prix de base (décocher = cas exceptionnel)</div>
        ${includedOpsHtml}
      </div>
      <div class="dev-opt-group">
        <div class="dev-opt-group-title">Options en plus (facultatives)</div>
        ${addonOpsHtml}
      </div>

      <div class="dev-matiere-row">
        <label style="display:flex;align-items:center;gap:6px;font-size:11px;cursor:pointer">
          <input type="checkbox" data-cl="matiereEnabled" ${l.matiere?.enabled ? 'checked' : ''} onchange="app.confToggleMatiere(this)">
          Mati\u00E8re / trims avanc\u00E9s par REWORKED (factur\u00E9s en plus, au r\u00E9el, par pi\u00E8ce)
        </label>
        <div class="dev-matiere-fields" style="display:${l.matiere?.enabled ? 'flex' : 'none'}">
          <input type="text" data-cl="matiereDesc" value="${esc(l.matiere?.desc || '')}" placeholder="Ex : coton bio fournisseur X" oninput="app.confRecalc()" style="flex:2">
          <input type="number" step="0.01" min="0" data-cl="matiereAmount" value="${l.matiere?.amountPerPiece || 0}" placeholder="\u20AC / pi\u00E8ce" oninput="app.confRecalc()" style="flex:0 0 100px">
        </div>
      </div>

      <label class="dev-base-toggle">
        <input type="checkbox" data-cl="dossierEnabled" ${l.dossier?.enabled ? 'checked' : ''} onchange="app.confToggleDossier(this)">
        Premi\u00E8re mise en production (analyse patron externe + ouverture de dossier) \u2014 forfait unique
        <input type="number" step="1" min="0" data-cl="dossierAmount" value="${l.dossier?.amount || this.tarifsConfection.ouvertureDossierPrice || 70}" oninput="app.confRecalc()" style="width:70px;margin-left:auto;display:${l.dossier?.enabled ? 'inline-block' : 'none'}">
      </label>

      <div class="chat-form-field chat-form-field-full" style="margin-top:6px">
        <input type="text" data-cl="note" value="${esc(l.note || '')}" placeholder="Note / commentaire">
      </div>

      <button type="button" class="btn btn-ghost btn-sm" onclick="app.confToggleAddonsPanel(${i})" style="margin-top:8px">+ Marquage textile (optionnel)</button>
      <div class="conf-addons-panel" id="confAddonsPanel-${i}" style="display:${(l.addons || []).length ? 'block' : 'none'}">
        ${this._confAddonsHtml(l)}
      </div>

      <div class="dev-line-subtotal">Sous-total pi\u00E8ce <strong data-cl-total>\u2014</strong></div>
    </div>`;
  }

  // Uniquement les catégories de marquage textile (sérigraphie, DTF) : pas de bases, forfaits,
  // développement, couture/étiquettes ni location dans les options confection.
  _confMarquageCats() { return ['Sérigraphie', 'DTF']; }

  _confAddonsHtml(l) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const allowed = this._confMarquageCats();
    const cats = {};
    Object.entries(this.tarifs || {}).forEach(([k, v]) => { const cat = v.cat || 'Autre'; if (!allowed.includes(cat)) return; if (!cats[cat]) cats[cat] = []; cats[cat].push({ key: k, ...v }); });
    const selected = {};
    (l.addons || []).forEach(a => { selected[a.key] = a.qty; });
    return Object.entries(cats).map(([cat, items]) => `
      <div class="conf-addon-group">
        <div class="conf-addon-group-title">${esc(cat)}</div>
        ${items.map(t => {
          const checked = Object.prototype.hasOwnProperty.call(selected, t.key);
          const qty = checked ? selected[t.key] : (l.quantity || 1);
          return `<label class="conf-addon-row">
            <input type="checkbox" data-cl-addon="${t.key}" ${checked ? 'checked' : ''} onchange="app.confToggleAddon(this)">
            <span class="conf-addon-label">${esc(t.label)}</span>
            <input type="number" step="1" min="0" value="${qty}" data-cl-addon-qty="${t.key}" class="conf-addon-qty" style="display:${checked ? 'inline-block' : 'none'}" oninput="app.confRecalc()">
            <span class="conf-addon-price">${this._eur(t.price)}/${esc(t.unit)}</span>
          </label>`;
        }).join('')}
      </div>`).join('');
  }

  _confComputeLine(line) {
    let refBasePrice, coefMult, coefLabel, pieceLabel;
    if (line.sourceType === 'dev') {
      if (!line.sourceDevId || line.sourceDevLineIndex == null) return null;
      refBasePrice = this._num(line.importedBasePrice);
      coefMult = this._num(line.importedCoefMult) || 1;
      coefLabel = line.importedCoefLabel || '';
      pieceLabel = line.importedLabel || 'Pi\u00E8ce import\u00E9e';
    } else if (line.sourceType === 'custom') {
      pieceLabel = (line.customLabel || '').trim() || 'Pi\u00E8ce sur-mesure';
      refBasePrice = this._num(line.customBasePrice);
      const coef = this.tarifsConfection.coefficients[line.coef];
      if (!coef) return null;
      coefMult = this._num(coef.mult) || 1;
      coefLabel = coef.label;
    } else if (line.sourceType === 'catalog_product') {
      const p = this.catalogue.find(x => x.id === line.catalogProductId);
      if (!p) return null;
      pieceLabel = p.label;
      refBasePrice = this._num(p.basePrice);
      const coef = this.tarifsConfection.coefficients[p.coefKey];
      if (!coef) return null;
      coefMult = this._num(coef.mult) || 1;
      coefLabel = coef.label;
    } else {
      const g = this.tarifsConfection.garments[line.garment];
      if (!g) return null;
      pieceLabel = g.label;
      refBasePrice = this._num(g.basePrice);
      const coef = this.tarifsConfection.coefficients[line.coef];
      if (!coef) return null;
      coefMult = this._num(coef.mult) || 1;
      coefLabel = coef.label;
    }
    const refPrice = refBasePrice * coefMult;
    const quantity = Math.max(1, this._num(line.quantity) || 1);

    // Le prix de base (refPrice) comprend d\u00E9j\u00E0 coupe/montage/finitions/contr\u00F4le qualit\u00E9.
    // Retirer une composante incompressible (cas exceptionnel) d\u00E9duit son % du prix de base.
    const removedIncluded = line.removedIncluded || [];
    let includedFraction = 1;
    const removedLabels = [];
    Object.entries(this.tarifsConfection.operations || {}).filter(([, o]) => o.category === 'included').forEach(([key, o]) => {
      if (removedIncluded.includes(key)) {
        includedFraction -= this._num(o.pct) / 100;
        removedLabels.push(o.label);
      }
    });
    includedFraction = Math.max(0, includedFraction);

    let faconUnit = refPrice * includedFraction;

    // Vraies options facultatives, ajout\u00E9es en plus du prix de base
    Object.entries(line.opts || {}).forEach(([key, qty]) => {
      const o = this.tarifsConfection.operations[key]; if (!o || o.category === 'included') return;
      const isQty = o.mode.endsWith('_qty');
      const q = isQty ? Math.max(1, this._num(qty) || 1) : 1;
      let amt = 0;
      if (o.mode === 'flat' || o.mode === 'flat_qty') amt = o.value * q;
      else amt = refPrice * (o.value / 100) * q;
      faconUnit += amt;
    });

    const tiers = (this.tarifsConfection.quantityTiers || []).slice().sort((a, b) => a.minQty - b.minQty);
    let tier = tiers.length ? tiers[0] : { mult: 1, label: '' };
    tiers.forEach(t => { if (quantity >= t.minQty) tier = t; });
    const faconUnitFinal = faconUnit * (this._num(tier.mult) || 1);
    const faconTotal = faconUnitFinal * quantity;

    const items = [];
    const faconDetail = `${quantity} pi\u00E8ce${quantity > 1 ? 's' : ''} \u00B7 ${this._eur(faconUnitFinal)}/pi\u00E8ce${tier.label ? ' \u00B7 palier ' + tier.label : ''}${removedLabels.length ? ' \u00B7 hors : ' + removedLabels.join(', ') : ''}`;
    items.push({ label: `Fa\u00E7on \u2014 ${pieceLabel}`, detail: faconDetail, amount: faconTotal });
    let subtotal = faconTotal;

    if (line.matiere?.enabled) {
      const pp = this._num(line.matiere.amountPerPiece);
      if (pp > 0) {
        const amt = pp * quantity;
        items.push({ label: 'Mati\u00E8re / trims avanc\u00E9s', detail: `${line.matiere.desc || 'Factur\u00E9 au r\u00E9el'} \u00B7 ${this._eur(pp)}/pi\u00E8ce \u00D7 ${quantity}`, amount: amt });
        subtotal += amt;
      }
    }
    if (line.dossier?.enabled) {
      const amt = this._num(line.dossier.amount);
      if (amt > 0) {
        items.push({ label: 'Ouverture de dossier / analyse patron', detail: 'Forfait unique, ind\u00E9pendant de la quantit\u00E9', amount: amt });
        subtotal += amt;
      }
    }
    (line.addons || []).forEach(a => {
      const t = this.tarifs[a.key]; if (!t) return;
      const q = Math.max(0, this._num(a.qty) || 0);
      if (q <= 0) return;
      const amt = t.price * q;
      items.push({ label: t.label, detail: `${q} \u00D7 ${this._eur(t.price)} / ${t.unit}`, amount: amt });
      subtotal += amt;
    });

    return { pieceLabel, coefLabel, quantity, tier, items, subtotal, note: line.note || '' };
  }

  _confCompute() {
    const lines = (this._conf?.lines || []).map(l => this._confComputeLine(l)).filter(Boolean);
    const total = lines.reduce((s, l) => s + l.subtotal, 0);
    return { lines, total };
  }

  async generateConfectionDevis() {
    if (!this._conf) return;
    this._confReadForm();
    const conf = this._conf;
    if (!conf.clientName.trim()) { this.toast('Renseignez le nom du client', 'error'); return; }
    const c = this._confCompute();
    if (!c.lines.length) { this.toast('Ajoutez au moins une pi\u00E8ce \u00E0 produire', 'error'); return; }

    const numero = this._confEditingId || await this._allocateDevisNumber();
    const data = this._confectionDevisData(numero, c);
    const html = this.buildConfectionDevisHtml(data);
    const wasEditing = !!this._confEditingId;
    await this._commitConfectionDevis(numero, html, c, conf);
    this.toast(wasEditing ? `Devis ${numero} mis \u00E0 jour` : `Devis ${numero} g\u00E9n\u00E9r\u00E9`, 'success');
  }

  _confectionDevisData(numero, c) {
    const conf = this._conf;
    const profile = (this.company && this.company.reworked) || this._defaultCompany().reworked;
    return {
      numero, dateFr: this._fmtDateLong(new Date().toISOString().slice(0, 10)),
      company: profile,
      client: { name: conf.clientName.toUpperCase(), address: conf.clientAddress, siret: conf.clientSiret },
      objet: conf.objet || 'Confection',
      delai: conf.delai,
      lines: c.lines, total: c.total,
      logoUrl: 'https://cdn.shopify.com/s/files/1/0798/6259/5851/t/17/assets/logo-red-studio.svg',
    };
  }

  async _commitConfectionDevis(numero, html, c, conf, montantOverride) {
    const client = (conf.clientName || '').toUpperCase();
    const existing = this.devis.find(d => d.id === numero);
    const snap = {
      ...conf,
      lines: (conf.lines || []).map(l => ({ ...l, opts: { ...(l.opts || {}) }, matiere: { ...(l.matiere || {}) }, dossier: { ...(l.dossier || {}) }, addons: (l.addons || []).map(a => ({ ...a })) })),
    };
    const newDevis = {
      id: numero, date: existing ? existing.date : new Date().toISOString().slice(0, 10), client,
      type: 'confection', objet: conf.objet || 'Confection',
      montant: montantOverride || this._eur(c.total), statut: existing ? existing.statut : 'brouillon',
      snap,
    };
    const idx = this.devis.findIndex(d => d.id === numero);
    if (idx >= 0) this.devis[idx] = newDevis; else this.devis.push(newDevis);
    try {
      await wfetch(`${WORKER_URL}/devis`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newDevis) });
      await wfetch(`${WORKER_URL}/devis/${numero}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ html }) });
    } catch (e) {}
    if (!this.clients.find(cl => (cl.name || '').toUpperCase() === client)) {
      const nc = { name: client, address: conf.clientAddress || '', siret: conf.clientSiret || '', contacts: '', source: 'local' };
      this.clients.push(nc);
      try { await wfetch(`${WORKER_URL}/clients`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nc) }); } catch (e) {}
    }
    await this._confUpdateTraceability(numero, conf);
    this.renderDevis();
    this.renderClients();
    this.closeConfectionDevisBuilder();
    this.showPreview(html, numero);
  }

  // R\u00E9f\u00E9rence ce devis confection sur le(s) devis d\u00E9veloppement source (tra\u00E7abilit\u00E9 bidirectionnelle).
  // Ne touche \u00E0 aucune fonction du module dev : lit/\u00E9crit uniquement des donn\u00E9es de registre d\u00E9j\u00E0 g\u00E9n\u00E9riques.
  async _confUpdateTraceability(confectionNumero, conf) {
    const devIds = [...new Set((conf.lines || []).filter(l => l.sourceType === 'dev' && l.sourceDevId).map(l => l.sourceDevId))];
    for (const devId of devIds) {
      const d = this.devis.find(x => x.id === devId);
      if (!d) continue;
      d.producedIn = d.producedIn || [];
      if (!d.producedIn.find(p => p.id === confectionNumero)) {
        d.producedIn.push({ id: confectionNumero, date: new Date().toISOString().slice(0, 10) });
        try { await wfetch(`${WORKER_URL}/devis`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(d) }); } catch (e) {}
      }
    }
  }

  async adjustConfectionDevisWithClaude() {
    if (!this._conf) return;
    const instruction = document.getElementById('confClaudeInstruction')?.value.trim();
    if (!instruction) { this.toast('\u00C9crivez une demande pour Claude', 'error'); return; }
    this._confReadForm();
    const conf = this._conf;
    if (!conf.clientName.trim()) { this.toast("Renseignez le nom du client avant l'ajustement", 'error'); return; }
    const c = this._confCompute();
    if (!c.lines.length) { this.toast('Ajoutez au moins une pi\u00E8ce \u00E0 produire', 'error'); return; }

    const numero = this._confEditingId || await this._allocateDevisNumber();
    const baseHtml = this.buildConfectionDevisHtml(this._confectionDevisData(numero, c));
    this.toast('Claude ajuste le devis...', '');
    try {
      const r = await wfetch(`${WORKER_URL}/chat-confection`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction, html: baseHtml }),
      });
      const j = await r.json();
      if (j.error) { this.toast(j.error, 'error'); return; }
      const m = (j.text || '').match(/```html\n([\s\S]*?)```/) || (j.text || '').match(/(<!DOCTYPE html>[\s\S]*?<\/html>)/i);
      if (!m) { this.toast('Pas de HTML renvoy\u00E9 par Claude', 'error'); return; }
      const claudeHtml = m[1].trim();
      const totalFromHtml = this._extractConfectionTotalFromHtml(claudeHtml);
      const montantStr = totalFromHtml != null ? this._eur(totalFromHtml) : undefined;
      await this._commitConfectionDevis(numero, claudeHtml, c, conf, montantStr);
      this.toast(`Devis ${numero} ajust\u00E9 par Claude et enregistr\u00E9`, 'success');
    } catch (e) { this.toast('Erreur r\u00E9seau Claude', 'error'); }
  }

  _extractConfectionTotalFromHtml(html) {
    const m = html.match(/<strong>TOTAL<\/strong><\/td>\s*<td[^>]*><strong>([^<]+)<\/strong>/i);
    if (!m) return null;
    const v = this._num((m[1].match(/[\d\s.,]+/) || [''])[0]);
    return isNaN(v) ? null : v;
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 RENDU HTML DEVIS CONFECTION (m\u00EAme DA que les devis/factures) \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  buildConfectionDevisHtml(data) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const co = data.company;

    const companyInfo = [
      esc(co.address), `SIRET : ${esc(co.siret)}`, co.numTva ? `N\u00B0 TVA : ${esc(co.numTva)}` : '', `${esc(co.tel)} \u00B7 ${esc(co.email)}`,
    ].filter(Boolean).join('<br>');

    const sectionsHtml = data.lines.map((l, i) => `
      <div class="section">
        <h3 class="section-title">${i + 1}. ${esc(l.pieceLabel)}${l.coefLabel ? ` \u2014 ${esc(this._devCoefClientLabel(l.coefLabel))}` : ''}</h3>
        <table><thead><tr><th>D\u00E9signation</th><th>Montant</th></tr></thead>
        <tbody>
          ${l.items.map(it => `<tr><td><strong>${esc(it.label)}</strong>${it.detail ? `<span class="detail">${esc(it.detail)}</span>` : ''}</td><td>${this._eur(it.amount)}</td></tr>`).join('')}
          <tr class="row-total"><td>Sous-total</td><td>${this._eur(l.subtotal)}</td></tr>
        </tbody></table>
        ${l.note ? `<div class="note">Note : ${esc(l.note)}</div>` : ''}
      </div>`).join('');

    const objetMeta = data.delai ? `<span><strong>D\u00E9lai :</strong> ${esc(data.delai)}</span>` : '';

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Devis confection ${esc(data.numero)} - ${esc(data.client.name)}</title>
<style>
${this._factureCSS()}
</style>
</head>
<body>

<div class="header">
  <div>
    <img class="logo" src="${data.logoUrl}" alt="R/ED STUDIO">
    <div class="company-info"><strong>${esc(co.emetteur)}</strong><br>${companyInfo}</div>
  </div>
  <div class="devis-badge">
    <h2>Devis confection N\u00B0 ${esc(data.numero)}</h2>
    <p>\u00C9mis le ${esc(data.dateFr)}<br>Valide 30 jours</p>
  </div>
</div>

<div class="parties">
  <div class="partie">
    <div class="partie-label">Client</div>
    <div class="partie-content"><strong>${esc(data.client.name)}</strong>${data.client.address ? `<br>${esc(data.client.address)}` : ''}${data.client.siret ? `<br>SIRET : ${esc(data.client.siret)}` : ''}</div>
  </div>
  <div class="partie">
    <div class="partie-label">\u00C9metteur</div>
    <div class="partie-content"><strong>${esc(co.emetteur)}</strong><br>${esc(co.address)}</div>
  </div>
</div>

${data.objet || objetMeta ? `<div class="objet">
  ${data.objet ? `<div class="objet-title">${esc(data.objet)}</div>` : ''}
  ${objetMeta ? `<div class="objet-meta">${objetMeta}</div>` : ''}
</div>` : ''}

${sectionsHtml}

<div class="section"><table><tbody>
  <tr class="row-total"><td><strong>TOTAL</strong></td><td><strong>${this._eur(data.total)}</strong></td></tr>
</tbody></table></div>

<div class="condition-box border" style="margin-top:10px"><h4>R\u00E8glement</h4>
  <ul style="columns:2"><li><strong>Acompte 40%</strong> \u00E0 la commande</li><li><strong>Solde 60%</strong> avant exp\u00E9dition</li></ul>
  <p style="font-size:8px;margin-top:6px;line-height:1.5">B\u00E9n\u00E9ficiaire : ${esc(co.beneficiaire)}<br>IBAN ${esc(co.iban)} \u00B7 BIC ${esc(co.bic)}</p>
</div>

<div class="signature-area">
  <div class="signature-box"><label>Date et signature du client</label><div class="signature-line"></div></div>
  <div class="signature-box"><label>Pour Reworked</label><div class="signature-line"></div></div>
</div>

<div class="footer">${esc(co.emetteur)} \u00B7 ${esc(co.mentionLegale)}<br>${esc(co.address)} \u00B7 ${esc(co.tel)} \u00B7 ${esc(co.email)}</div>

</body>
</html>`;
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 CATALOGUE DES PRODUITS D\u00C9VELOPP\u00C9S \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  renderCatalogue() {
    const tbody = document.getElementById('catalogueTableBody');
    if (!tbody) return;
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const q = (document.getElementById('catalogueSearch')?.value || '').toLowerCase();
    const clientFilter = document.getElementById('catalogueClientFilter')?.value || 'all';

    const filterEl = document.getElementById('catalogueClientFilter');
    if (filterEl) {
      const clients = [...new Set(this.catalogue.map(p => p.client))].filter(Boolean).sort();
      const current = filterEl.value || 'all';
      filterEl.innerHTML = '<option value="all">Tous les clients</option>' + clients.map(c => `<option value="${esc(c)}">${esc(c)}</option>`).join('');
      filterEl.value = clients.includes(current) ? current : 'all';
    }

    const list = this.catalogue
      .filter(p => (clientFilter === 'all' || p.client === clientFilter) && (!q || `${p.client} ${p.label} ${p.cat || ''}`.toLowerCase().includes(q)))
      .sort((a, b) => (a.client || '').localeCompare(b.client || '') || (a.label || '').localeCompare(b.label || ''));

    tbody.innerHTML = list.map(p => {
      const costs = this._catalogueComputeCosts(p);
      return `<tr onclick="app.toggleCatalogueDetail('${p.id}')" style="cursor:pointer">
      <td style="font-weight:600">${esc(p.client)}</td>
      <td>${esc(p.label)}</td>
      <td style="text-align:right;white-space:nowrap">${this._eur(p.basePrice)}</td>
      <td>${esc(this._catalogueCoefLabel(p.coefKey))}</td>
      <td style="text-align:right;white-space:nowrap">${this._eur(costs.total)}</td>
      <td class="td-actions"><div class="devis-actions">
        <button onclick="event.stopPropagation();app.viewCatalogueFiche('${p.id}')">Fiche</button>
        <button onclick="event.stopPropagation();app.produceCatalogueEntry('${p.id}')">Produire</button>
        <button onclick="event.stopPropagation();app.editCatalogueEntry('${p.id}')">Modifier</button>
        <button class="devis-action-delete" onclick="event.stopPropagation();app.deleteCatalogueEntry('${p.id}')">Suppr.</button>
      </div></td>
    </tr>
    <tr class="devis-detail-row" id="catalogue-detail-${p.id}" style="display:none">
      <td colspan="6" style="padding:0">
        <div class="devis-detail">
          ${p.description ? `<div style="font-size:11px;color:var(--55);margin-bottom:8px">${esc(p.description)}</div>` : ''}
          <div class="devis-detail-grid">
            <div><span class="detail-label">Mati\u00E8res</span><span class="detail-value">${(p.matieres || []).length ? (p.matieres.map(m => `${esc(m.denomination)} (${this._eur(m.pricePerMeter)}/m \u00D7 ${m.quantity || 0}m${m.fournisseur ? ' \u00B7 ' + esc(m.fournisseur) : ''})`).join(', ')) : '\u2014'}</span></div>
            <div><span class="detail-label">Trims</span><span class="detail-value">${(p.trims || []).length ? (p.trims.map(t => `${esc(t.denomination)} (${this._eur(t.pricePerUnit)} \u00D7 ${t.quantity || 0}${t.fournisseur ? ' \u00B7 ' + esc(t.fournisseur) : ''})`).join(', ')) : '\u2014'}</span></div>
            <div><span class="detail-label">Prints</span><span class="detail-value">${(p.prints || []).length ? (p.prints.map(pr => `${esc((this.tarifs[pr.tarifKey] || {}).label || '?')} \u00D7 ${pr.qty || 1}`).join(', ')) : '\u2014'}</span></div>
            <div><span class="detail-label">Co\u00FBt mati\u00E8re/pi\u00E8ce</span><span class="detail-value">${this._eur(costs.total)} <span style="font-weight:400;color:var(--88)">(mat. ${this._eur(costs.matiereCost)} \u00B7 trims ${this._eur(costs.trimsCost)} \u00B7 prints ${this._eur(costs.printsCost)})</span></span></div>
            <div><span class="detail-label">Patronage</span><span class="detail-value">${esc(p.patronageRef) || '\u2014'}</span></div>
            <div><span class="detail-label">Techpack</span><span class="detail-value">${p.techpackUrl ? `<a href="${esc(p.techpackUrl)}" target="_blank" rel="noopener">Lien</a>` : '\u2014'}</span></div>
          </div>
          ${p.notes ? `<div style="font-size:11px;color:var(--55);margin:8px 0">${esc(p.notes)}</div>` : ''}
          ${p.sourceDevId ? `<div style="font-size:10px;color:var(--66)">Publi\u00E9 depuis le devis d\u00E9veloppement <span class="conf-trace-link" onclick="event.stopPropagation();app.viewHtml('${esc(p.sourceDevId)}')">${esc(p.sourceDevId)}</span></div>` : ''}
          ${p.photoUrl ? `<div style="margin-top:8px"><img src="${esc(p.photoUrl)}" alt="" style="max-width:160px;max-height:160px;border-radius:6px;border:1px solid var(--ee);object-fit:cover"></div>` : ''}
        </div>
      </td>
    </tr>`;
    }).join('');

    const badge = document.getElementById('catalogueBadge');
    if (badge) badge.textContent = this.catalogue.length;
  }

  _catalogueCoefLabel(coefKey) {
    const coef = this.tarifsConfection.coefficients?.[coefKey];
    return coef ? coef.label : '';
  }

  toggleCatalogueDetail(id) {
    const row = document.getElementById(`catalogue-detail-${id}`);
    if (row) row.style.display = row.style.display === 'none' ? '' : 'none';
  }

  filterCatalogue() { this.renderCatalogue(); }

  addCatalogueEntry() { this.openCatalogueForm(null); }

  editCatalogueEntry(id) {
    const p = this.catalogue.find(x => x.id === id);
    if (!p) return;
    this.openCatalogueForm(p);
  }

  openCatalogueForm(prefill) {
    this._catalogueEditingId = (prefill && prefill.id) || null;
    this._catalogueDraft = prefill ? {
      ...prefill,
      matieres: (prefill.matieres || []).map(m => ({ ...m })),
      trims: (prefill.trims || []).map(t => ({ ...t })),
      prints: (prefill.prints || []).map(pr => ({ ...pr })),
    } : {
      id: null, client: '', label: '', cat: '', description: '', basePrice: 0, coefKey: Object.keys(this.tarifsConfection.coefficients || {})[0] || '',
      matieres: [], trims: [], prints: [],
      patronageRef: '', techpackUrl: '', photoUrl: '', notes: '',
      sourceDevId: '', sourceDevLineIndex: null,
    };
    document.getElementById('catalogueFicheOverlay').classList.add('active');
    this.renderCatalogueForm();
  }

  closeCatalogueForm() { document.getElementById('catalogueFicheOverlay').classList.remove('active'); this._catalogueDraft = null; this._catalogueEditingId = null; }

  // Co\u00FBt mati\u00E8re/trims/prints par pi\u00E8ce, \u00E0 partir d'une fiche (draft ou entr\u00E9e enregistr\u00E9e)
  _catalogueComputeCosts(p) {
    const matiereCost = (p.matieres || []).reduce((s, m) => s + (this._num(m.pricePerMeter) * this._num(m.quantity)), 0);
    const trimsCost = (p.trims || []).reduce((s, t) => s + (this._num(t.pricePerUnit) * this._num(t.quantity)), 0);
    const printsCost = (p.prints || []).reduce((s, pr) => {
      const t = this.tarifs[pr.tarifKey]; if (!t) return s;
      return s + (t.price * (this._num(pr.qty) || 1));
    }, 0);
    return { matiereCost, trimsCost, printsCost, total: matiereCost + trimsCost + printsCost };
  }

  // Options d'un menu "biblioth\u00E8que" (mati\u00E8res ou trims) : "+ Nouveau" en t\u00EAte, puis les entr\u00E9es enregistr\u00E9es
  _catalogueLibOpts(lib, selectedId) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    return ['<option value="">+ Nouveau (non enregistr\u00E9)</option>'].concat(
      (lib || []).map(m => `<option value="${m.id}" ${selectedId === m.id ? 'selected' : ''}>${esc(m.denomination)}</option>`)
    ).join('');
  }

  // Options de marquage textile (m\u00EAmes cat\u00E9gories que les add-ons confection)
  _catalogueMarquageOpts(selectedKey) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const allowed = this._confMarquageCats();
    return ['<option value="">Choisir...</option>'].concat(
      Object.entries(this.tarifs || {}).filter(([, v]) => allowed.includes(v.cat))
        .map(([k, v]) => `<option value="${k}" ${selectedKey === k ? 'selected' : ''}>${esc(v.label)} (${this._eur(v.price)}/${esc(v.unit)})</option>`)
    ).join('');
  }

  _catalogueMatiereLineHtml(m, i) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    return `<div class="cat-sub-line" data-idx="${i}">
      <div class="cat-sub-line-top">
        <select data-field="libId" onchange="app.catOnMatiereLibSelect(this)">${this._catalogueLibOpts(this.matieresLib, m.libId)}</select>
        <button type="button" class="btn btn-ghost btn-sm" onclick="app.catSaveMatiereToLib(${i})">+ Biblioth\u00E8que</button>
        <button class="tarif-delete" onclick="app.catRemoveMatiereLine(${i})" title="Supprimer">\u2715</button>
      </div>
      <div class="cat-sub-line-fields">
        <div style="flex:2"><label>D\u00E9nomination</label><input type="text" data-field="denomination" value="${esc(m.denomination)}" placeholder="Ex: Coton bio 220g" oninput="app.catRecalcUnitCost()"></div>
        <div><label>Prix / m</label><input type="number" step="0.01" data-field="pricePerMeter" value="${m.pricePerMeter || 0}" oninput="app.catRecalcUnitCost()"></div>
        <div><label>Qt\u00E9 (m) / pi\u00E8ce</label><input type="number" step="0.01" data-field="quantity" value="${m.quantity || 0}" oninput="app.catRecalcUnitCost()"></div>
        <div><label>Composition</label><input type="text" data-field="composition" value="${esc(m.composition)}" placeholder="Ex: 100% coton"></div>
        <div><label>Fournisseur (lien ou nom)</label><input type="text" data-field="fournisseur" value="${esc(m.fournisseur)}"></div>
      </div>
    </div>`;
  }

  _catalogueTrimLineHtml(t, i) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    return `<div class="cat-sub-line" data-idx="${i}">
      <div class="cat-sub-line-top">
        <select data-field="libId" onchange="app.catOnTrimLibSelect(this)">${this._catalogueLibOpts(this.trimsLib, t.libId)}</select>
        <button type="button" class="btn btn-ghost btn-sm" onclick="app.catSaveTrimToLib(${i})">+ Biblioth\u00E8que</button>
        <button class="tarif-delete" onclick="app.catRemoveTrimLine(${i})" title="Supprimer">\u2715</button>
      </div>
      <div class="cat-sub-line-fields">
        <div style="flex:2"><label>D\u00E9nomination</label><input type="text" data-field="denomination" value="${esc(t.denomination)}" placeholder="Ex: Boutons corne 15mm" oninput="app.catRecalcUnitCost()"></div>
        <div><label>Prix unitaire</label><input type="number" step="0.01" data-field="pricePerUnit" value="${t.pricePerUnit || 0}" oninput="app.catRecalcUnitCost()"></div>
        <div><label>Qt\u00E9 / pi\u00E8ce</label><input type="number" step="1" data-field="quantity" value="${t.quantity || 0}" oninput="app.catRecalcUnitCost()"></div>
        <div><label>Composition</label><input type="text" data-field="composition" value="${esc(t.composition)}"></div>
        <div><label>Fournisseur (lien ou nom)</label><input type="text" data-field="fournisseur" value="${esc(t.fournisseur)}"></div>
      </div>
    </div>`;
  }

  _cataloguePrintLineHtml(pr, i) {
    return `<div class="cat-sub-line" data-idx="${i}">
      <div class="cat-sub-line-top">
        <select data-field="tarifKey" onchange="app.catRecalcUnitCost()">${this._catalogueMarquageOpts(pr.tarifKey)}</select>
        <button class="tarif-delete" onclick="app.catRemovePrintLine(${i})" title="Supprimer">\u2715</button>
      </div>
      <div class="cat-sub-line-fields">
        <div><label>Quantit\u00E9</label><input type="number" step="1" min="1" data-field="qty" value="${pr.qty || 1}" oninput="app.catRecalcUnitCost()"></div>
      </div>
    </div>`;
  }

  renderCatalogueForm() {
    const body = document.getElementById('catalogueFicheBody');
    if (!body || !this._catalogueDraft) return;
    const p = this._catalogueDraft;
    const esc = s => this.escHtml(String(s == null ? '' : s));

    const hdr = document.querySelector('#catalogueFicheOverlay .preview-header span');
    if (hdr) hdr.textContent = this._catalogueEditingId ? 'Modifier la fiche produit' : 'Nouvelle fiche produit';

    const coefOpts = Object.entries(this.tarifsConfection.coefficients || {}).map(([k, v]) => `<option value="${k}" ${p.coefKey === k ? 'selected' : ''}>${esc(v.label)}</option>`).join('');
    const costs = this._catalogueComputeCosts(p);

    body.innerHTML = `
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Client</label>
          <input type="text" id="catClient" list="clientSuggestions" value="${esc(p.client)}" placeholder="Ex: RAWRUN">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>Cat\u00E9gorie (optionnel)</label>
          <input type="text" id="catCat" value="${esc(p.cat)}" placeholder="Ex: Accessoires">
        </div>
      </div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Nom de la pi\u00E8ce</label>
          <input type="text" id="catLabel" value="${esc(p.label)}" placeholder="Ex: Cagoule de sc\u00E8ne">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>Prix fa\u00E7on de r\u00E9f\u00E9rence</label>
          <input type="number" step="1" id="catBasePrice" value="${p.basePrice || 0}">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>Coefficient</label>
          <select id="catCoefKey">${coefOpts}</select>
        </div>
      </div>
      ${p.sourceDevId ? `<p style="font-size:10px;color:var(--88);margin-top:-6px">Prix fa\u00E7on diff\u00E9rent du prix de d\u00E9veloppement \u2014 \u00E0 d\u00E9finir pour la production.</p>` : ''}
      <div class="chat-form-field chat-form-field-full">
        <label>Description de la pi\u00E8ce</label>
        <textarea id="catDescription" rows="2" placeholder="D\u00E9crivez la pi\u00E8ce...">${esc(p.description)}</textarea>
      </div>

      <div class="fb-section-title">Mati\u00E8res</div>
      <div id="catMatieresBody">${(p.matieres || []).map((m, i) => this._catalogueMatiereLineHtml(m, i)).join('')}</div>
      <button type="button" class="btn btn-ghost btn-sm" onclick="app.catAddMatiereLine()">+ Ajouter une mati\u00E8re</button>

      <div class="fb-section-title">Trims</div>
      <div id="catTrimsBody">${(p.trims || []).map((t, i) => this._catalogueTrimLineHtml(t, i)).join('')}</div>
      <button type="button" class="btn btn-ghost btn-sm" onclick="app.catAddTrimLine()">+ Ajouter un trim</button>

      <div class="fb-section-title">Prints (marquage textile)</div>
      <div id="catPrintsBody">${(p.prints || []).map((pr, i) => this._cataloguePrintLineHtml(pr, i)).join('')}</div>
      <button type="button" class="btn btn-ghost btn-sm" onclick="app.catAddPrintLine()">+ Ajouter un print</button>

      <div class="cat-unit-cost-box">
        <span>Co\u00FBt mati\u00E8re/pi\u00E8ce estim\u00E9</span>
        <div style="text-align:right">
          <strong id="catUnitCostTotal">${this._eur(costs.total)}</strong>
          <div class="cat-cost-detail">mat. <span id="catUnitCostMatiere">${this._eur(costs.matiereCost)}</span> \u00B7 trims <span id="catUnitCostTrims">${this._eur(costs.trimsCost)}</span> \u00B7 prints <span id="catUnitCostPrints">${this._eur(costs.printsCost)}</span></div>
        </div>
      </div>

      <div class="fb-section-title">R\u00E9f\u00E9rences</div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Patronage (lien ou note)</label>
          <input type="text" id="catPatronageRef" value="${esc(p.patronageRef)}" placeholder="Ex: Dossier Drive / classeur atelier n\u00B03">
        </div>
        <div class="chat-form-field">
          <label>Lien techpack</label>
          <input type="text" id="catTechpackUrl" value="${esc(p.techpackUrl)}" placeholder="https://...">
        </div>
      </div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Photo (lien URL)</label>
          <input type="text" id="catPhotoUrl" value="${esc(p.photoUrl)}" placeholder="https://...">
        </div>
      </div>
      <div class="chat-form-field chat-form-field-full">
        <label>Notes</label>
        <textarea id="catNotes" rows="2">${esc(p.notes)}</textarea>
      </div>
      ${p.sourceDevId ? `<p style="font-size:10px;color:var(--88)">Publi\u00E9 depuis le devis d\u00E9veloppement ${esc(p.sourceDevId)}</p>` : ''}
    `;
  }

  _catalogueReadMatieres() {
    const rows = document.querySelectorAll('#catMatieresBody > .cat-sub-line');
    return Array.from(rows).map(row => {
      const g = f => row.querySelector(`[data-field="${f}"]`)?.value || '';
      return {
        libId: g('libId'), denomination: g('denomination'),
        pricePerMeter: parseFloat(g('pricePerMeter')) || 0,
        quantity: parseFloat(g('quantity')) || 0,
        composition: g('composition'), fournisseur: g('fournisseur'),
      };
    });
  }

  _catalogueReadTrims() {
    const rows = document.querySelectorAll('#catTrimsBody > .cat-sub-line');
    return Array.from(rows).map(row => {
      const g = f => row.querySelector(`[data-field="${f}"]`)?.value || '';
      return {
        libId: g('libId'), denomination: g('denomination'),
        pricePerUnit: parseFloat(g('pricePerUnit')) || 0,
        quantity: parseFloat(g('quantity')) || 0,
        composition: g('composition'), fournisseur: g('fournisseur'),
      };
    });
  }

  _catalogueReadPrints() {
    const rows = document.querySelectorAll('#catPrintsBody > .cat-sub-line');
    return Array.from(rows).map(row => {
      const g = f => row.querySelector(`[data-field="${f}"]`)?.value || '';
      return { tarifKey: g('tarifKey'), qty: parseFloat(g('qty')) || 1 };
    });
  }

  _catalogueSyncDraftFromForm() {
    const p = this._catalogueDraft;
    if (!p) return;
    const g = id => document.getElementById(id);
    p.client = (g('catClient')?.value || '').trim().toUpperCase();
    p.cat = g('catCat')?.value || '';
    p.label = (g('catLabel')?.value || '').trim();
    p.description = g('catDescription')?.value || '';
    p.basePrice = parseFloat(g('catBasePrice')?.value) || 0;
    p.coefKey = g('catCoefKey')?.value || '';
    p.matieres = this._catalogueReadMatieres();
    p.trims = this._catalogueReadTrims();
    p.prints = this._catalogueReadPrints();
    p.patronageRef = g('catPatronageRef')?.value || '';
    p.techpackUrl = g('catTechpackUrl')?.value || '';
    p.photoUrl = g('catPhotoUrl')?.value || '';
    p.notes = g('catNotes')?.value || '';
  }

  catAddMatiereLine() { this._catalogueSyncDraftFromForm(); this._catalogueDraft.matieres.push({ libId: '', denomination: '', pricePerMeter: 0, quantity: 0, composition: '', fournisseur: '' }); this.renderCatalogueForm(); }
  catRemoveMatiereLine(i) { this._catalogueSyncDraftFromForm(); this._catalogueDraft.matieres.splice(i, 1); this.renderCatalogueForm(); }
  catAddTrimLine() { this._catalogueSyncDraftFromForm(); this._catalogueDraft.trims.push({ libId: '', denomination: '', pricePerUnit: 0, quantity: 0, composition: '', fournisseur: '' }); this.renderCatalogueForm(); }
  catRemoveTrimLine(i) { this._catalogueSyncDraftFromForm(); this._catalogueDraft.trims.splice(i, 1); this.renderCatalogueForm(); }
  catAddPrintLine() { this._catalogueSyncDraftFromForm(); this._catalogueDraft.prints.push({ tarifKey: '', qty: 1 }); this.renderCatalogueForm(); }
  catRemovePrintLine(i) { this._catalogueSyncDraftFromForm(); this._catalogueDraft.prints.splice(i, 1); this.renderCatalogueForm(); }

  catOnMatiereLibSelect(select) {
    const libId = select.value;
    if (!libId) return;
    const m = this.matieresLib.find(x => x.id === libId);
    if (!m) return;
    const row = select.closest('.cat-sub-line');
    row.querySelector('[data-field="denomination"]').value = m.denomination || '';
    row.querySelector('[data-field="pricePerMeter"]').value = m.pricePerMeter || 0;
    row.querySelector('[data-field="composition"]').value = m.composition || '';
    row.querySelector('[data-field="fournisseur"]').value = m.fournisseur || '';
    this.catRecalcUnitCost();
  }

  catOnTrimLibSelect(select) {
    const libId = select.value;
    if (!libId) return;
    const t = this.trimsLib.find(x => x.id === libId);
    if (!t) return;
    const row = select.closest('.cat-sub-line');
    row.querySelector('[data-field="denomination"]').value = t.denomination || '';
    row.querySelector('[data-field="pricePerUnit"]').value = t.pricePerUnit || 0;
    row.querySelector('[data-field="composition"]').value = t.composition || '';
    row.querySelector('[data-field="fournisseur"]').value = t.fournisseur || '';
    this.catRecalcUnitCost();
  }

  async catSaveMatiereToLib(i) {
    const rows = document.querySelectorAll('#catMatieresBody > .cat-sub-line');
    const row = rows[i]; if (!row) return;
    const g = f => row.querySelector(`[data-field="${f}"]`)?.value || '';
    const denomination = g('denomination').trim();
    if (!denomination) { this.toast("Renseignez une d\u00E9nomination avant d'enregistrer", 'error'); return; }
    const libIdSelect = row.querySelector('[data-field="libId"]');
    const entry = {
      id: libIdSelect.value || ('mat_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6)),
      denomination, pricePerMeter: parseFloat(g('pricePerMeter')) || 0,
      composition: g('composition'), fournisseur: g('fournisseur'),
    };
    const idx = this.matieresLib.findIndex(x => x.id === entry.id);
    if (idx >= 0) this.matieresLib[idx] = entry; else this.matieresLib.push(entry);
    try { await wfetch(`${WORKER_URL}/matieres`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entry) }); } catch (e) {}
    libIdSelect.innerHTML = this._catalogueLibOpts(this.matieresLib, entry.id);
    this.toast(`Mati\u00E8re "${denomination}" enregistr\u00E9e dans la biblioth\u00E8que`, 'success');
  }

  async catSaveTrimToLib(i) {
    const rows = document.querySelectorAll('#catTrimsBody > .cat-sub-line');
    const row = rows[i]; if (!row) return;
    const g = f => row.querySelector(`[data-field="${f}"]`)?.value || '';
    const denomination = g('denomination').trim();
    if (!denomination) { this.toast("Renseignez une d\u00E9nomination avant d'enregistrer", 'error'); return; }
    const libIdSelect = row.querySelector('[data-field="libId"]');
    const entry = {
      id: libIdSelect.value || ('trim_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6)),
      denomination, pricePerUnit: parseFloat(g('pricePerUnit')) || 0,
      composition: g('composition'), fournisseur: g('fournisseur'),
    };
    const idx = this.trimsLib.findIndex(x => x.id === entry.id);
    if (idx >= 0) this.trimsLib[idx] = entry; else this.trimsLib.push(entry);
    try { await wfetch(`${WORKER_URL}/trims`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entry) }); } catch (e) {}
    libIdSelect.innerHTML = this._catalogueLibOpts(this.trimsLib, entry.id);
    this.toast(`Trim "${denomination}" enregistr\u00E9 dans la biblioth\u00E8que`, 'success');
  }

  catRecalcUnitCost() {
    if (!this._catalogueDraft) return;
    const tmp = { matieres: this._catalogueReadMatieres(), trims: this._catalogueReadTrims(), prints: this._catalogueReadPrints() };
    const c = this._catalogueComputeCosts(tmp);
    const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
    set('catUnitCostTotal', this._eur(c.total));
    set('catUnitCostMatiere', this._eur(c.matiereCost));
    set('catUnitCostTrims', this._eur(c.trimsCost));
    set('catUnitCostPrints', this._eur(c.printsCost));
  }

  async saveCatalogueEntry() {
    const p = this._catalogueDraft;
    if (!p) return;
    this._catalogueSyncDraftFromForm();

    if (!p.client) { this.toast('Renseignez le client', 'error'); return; }
    if (!p.label) { this.toast('Renseignez le nom de la pi\u00E8ce', 'error'); return; }

    if (!p.id) p.id = 'prod_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    const idx = this.catalogue.findIndex(x => x.id === p.id);
    if (idx >= 0) this.catalogue[idx] = p; else this.catalogue.push(p);
    try { await wfetch(`${WORKER_URL}/catalogue`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(p) }); } catch (e) {}
    this.closeCatalogueForm();
    this.renderCatalogue();
    this.toast(`Fiche "${p.label}" enregistr\u00E9e`, 'success');
  }

  async deleteCatalogueEntry(id) {
    const p = this.catalogue.find(x => x.id === id);
    if (!p) return;
    if (!confirm(`Supprimer la fiche "${p.label}" (${p.client}) ?`)) return;
    this.catalogue = this.catalogue.filter(x => x.id !== id);
    try { await wfetch(`${WORKER_URL}/catalogue/${encodeURIComponent(id)}`, { method: 'DELETE' }); } catch (e) {}
    this.renderCatalogue();
    this.toast('Fiche supprim\u00E9e');
  }

  // Aper\u00E7u propre d'une fiche d\u00E9j\u00E0 enregistr\u00E9e dans le catalogue
  viewCatalogueFiche(id) {
    const p = this.catalogue.find(x => x.id === id);
    if (!p) return;
    this.showPreview(this.buildCatalogueFicheHtml(p), `fiche-${id}`);
  }

  // Aper\u00E7u de la fiche en cours d'\u00E9dition (avant m\u00EAme de l'enregistrer)
  previewCatalogueDraft() {
    if (!this._catalogueDraft) return;
    this._catalogueSyncDraftFromForm();
    if (!this._catalogueDraft.label.trim()) { this.toast('Renseignez au moins le nom de la pi\u00E8ce', 'error'); return; }
    this.showPreview(this.buildCatalogueFicheHtml(this._catalogueDraft), 'apercu-fiche');
    this.renderCatalogueForm();
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 RENDU HTML FICHE PRODUIT (usage interne, m\u00EAme DA que les devis) \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  buildCatalogueFicheHtml(p) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const co = (this.company && this.company.reworked) || this._defaultCompany().reworked;
    const costs = this._catalogueComputeCosts(p);

    const matieresRows = (p.matieres || []).map(m => `<tr>
      <td><strong>${esc(m.denomination) || '\u2014'}</strong>${m.composition ? `<span class="detail">${esc(m.composition)}</span>` : ''}</td>
      <td>${m.fournisseur ? esc(m.fournisseur) : '\u2014'}</td>
      <td>${this._eur(m.pricePerMeter)}/m \u00D7 ${m.quantity || 0}m</td>
      <td>${this._eur(this._num(m.pricePerMeter) * this._num(m.quantity))}</td>
    </tr>`).join('');

    const trimsRows = (p.trims || []).map(t => `<tr>
      <td><strong>${esc(t.denomination) || '\u2014'}</strong>${t.composition ? `<span class="detail">${esc(t.composition)}</span>` : ''}</td>
      <td>${t.fournisseur ? esc(t.fournisseur) : '\u2014'}</td>
      <td>${this._eur(t.pricePerUnit)} \u00D7 ${t.quantity || 0}</td>
      <td>${this._eur(this._num(t.pricePerUnit) * this._num(t.quantity))}</td>
    </tr>`).join('');

    const printsRows = (p.prints || []).map(pr => {
      const t = this.tarifs[pr.tarifKey];
      return `<tr>
        <td><strong>${t ? esc(t.label) : '\u2014'}</strong></td>
        <td>\u2014</td>
        <td>${t ? this._eur(t.price) + '/' + esc(t.unit) : ''} \u00D7 ${pr.qty || 1}</td>
        <td>${t ? this._eur(t.price * (this._num(pr.qty) || 1)) : '\u2014'}</td>
      </tr>`;
    }).join('');

    const tableBlock = (title, rows) => rows ? `
      <div class="section">
        <h3 class="section-title">${esc(title)}</h3>
        <table><thead><tr><th>D\u00E9signation</th><th>Fournisseur</th><th>D\u00E9tail</th><th>Co\u00FBt</th></tr></thead>
        <tbody>${rows}</tbody></table>
      </div>` : '';

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Fiche produit \u2014 ${esc(p.label)} (${esc(p.client)})</title>
<style>
${this._factureCSS()}
</style>
</head>
<body>

<div class="header">
  <div>
    <img class="logo" src="https://cdn.shopify.com/s/files/1/0798/6259/5851/t/17/assets/logo-red-studio.svg" alt="R/ED STUDIO">
    <div class="company-info">Fiche produit \u2014 usage interne</div>
  </div>
  <div class="devis-badge">
    <h2>Fiche produit</h2>
    <p>G\u00E9n\u00E9r\u00E9e le ${esc(this._fmtDateLong(new Date().toISOString().slice(0, 10)))}</p>
  </div>
</div>

<div class="parties">
  <div class="partie">
    <div class="partie-label">Client</div>
    <div class="partie-content"><strong>${esc(p.client)}</strong></div>
  </div>
  <div class="partie">
    <div class="partie-label">Pi\u00E8ce</div>
    <div class="partie-content"><strong>${esc(p.label)}</strong>${p.cat ? `<br>${esc(p.cat)}` : ''}</div>
  </div>
</div>

${p.description ? `<div class="objet"><div class="objet-title">Description</div><div style="font-size:9px;line-height:1.5">${esc(p.description)}</div></div>` : ''}

${p.photoUrl ? `<div style="text-align:center;margin-bottom:18px"><img src="${esc(p.photoUrl)}" alt="" style="max-width:220px;max-height:220px;border-radius:6px;border:1px solid #ddd;object-fit:cover"></div>` : ''}

<div class="section">
  <h3 class="section-title">Prix de r\u00E9f\u00E9rence</h3>
  <table><tbody>
    <tr><td>Prix fa\u00E7on de r\u00E9f\u00E9rence</td><td>${this._eur(p.basePrice)}</td></tr>
    <tr><td>Coefficient de complexit\u00E9</td><td>${esc(this._catalogueCoefLabel(p.coefKey)) || '\u2014'}</td></tr>
  </tbody></table>
</div>

${tableBlock('Mati\u00E8res', matieresRows)}
${tableBlock('Trims', trimsRows)}
${tableBlock('Prints', printsRows)}

<div class="section"><table><tbody>
  <tr class="row-total"><td><strong>Co\u00FBt mati\u00E8re total / pi\u00E8ce</strong></td><td><strong>${this._eur(costs.total)}</strong></td></tr>
</tbody></table></div>

<div class="conditions">
  <div class="condition-box"><h4>Patronage</h4><p style="font-size:8px;line-height:1.6">${esc(p.patronageRef) || '\u2014'}</p></div>
  <div class="condition-box"><h4>Techpack</h4><p style="font-size:8px;line-height:1.6">${p.techpackUrl ? `<a href="${esc(p.techpackUrl)}" target="_blank" rel="noopener">${esc(p.techpackUrl)}</a>` : '\u2014'}</p></div>
</div>

${p.notes ? `<div class="note">Note : ${esc(p.notes)}</div>` : ''}
${p.sourceDevId ? `<div class="note">Publi\u00E9 depuis le devis d\u00E9veloppement ${esc(p.sourceDevId)}</div>` : ''}

<div class="footer">${esc(co.emetteur)} \u00B7 Document interne, non contractuel \u00B7 ${esc(co.address)}</div>

</body>
</html>`;
  }

  // Ouvre le builder confection avec une ligne pr\u00E9-remplie depuis une fiche du catalogue
  produceCatalogueEntry(id) {
    const p = this.catalogue.find(x => x.id === id);
    if (!p) return;
    this.switchTab('confection');
    this.openConfectionBuilder();
    const line = this._conf.lines[0];
    line.sourceType = 'catalog_product';
    line.catalogProductId = id;
    this._applyCatalogueDefaultsToLine(line, p);
    this.renderConfectionBuilder();
  }

  // Pr\u00E9-remplit coefficient et mati\u00E8re d'une ligne confection \u00E0 partir d'une fiche catalogue
  _applyCatalogueDefaultsToLine(line, p) {
    line.coef = p.coefKey;
    const costs = this._catalogueComputeCosts(p);
    const matiereTrimsCost = costs.matiereCost + costs.trimsCost;
    if (matiereTrimsCost > 0) {
      const parts = [...(p.matieres || []), ...(p.trims || [])].map(x => x.denomination).filter(Boolean);
      line.matiere = { enabled: true, desc: parts.join(', ') || 'Mati\u00E8re/trims (fiche catalogue)', amountPerPiece: matiereTrimsCost };
    }
    // Les prints de la fiche deviennent des lignes de marquage textile (add-ons) sur la ligne confection
    line.addons = (p.prints || []).filter(pr => pr.tarifKey && this.tarifs[pr.tarifKey]).map(pr => ({ key: pr.tarifKey, qty: pr.qty || 1 }));
  }

  // Publie une pi\u00E8ce d'un devis d\u00E9veloppement (en cours d'\u00E9dition) vers le catalogue.
  // N'\u00E9crit rien dans le devis dev lui-m\u00EAme, ne touche \u00E0 aucune fonction du module dev.
  publishLineToCatalogue(i) {
    this._devReadForm();
    const line = this._dev.lines[i];
    const computed = this._devComputeLine(line);
    if (!computed) { this.toast('Ligne invalide', 'error'); return; }
    if (!(this._dev.clientName || '').trim()) { this.toast('Renseignez le client avant de publier au catalogue', 'error'); return; }
    const prefill = {
      id: null,
      client: this._dev.clientName.toUpperCase(),
      label: computed.garment.label,
      cat: computed.garment.cat || '',
      description: '',
      basePrice: 0,
      coefKey: Object.keys(this.tarifsConfection.coefficients || {})[0] || '',
      matieres: line.matiere?.desc ? [{ libId: '', denomination: line.matiere.desc, pricePerMeter: 0, quantity: 0, composition: '', fournisseur: '' }] : [],
      trims: [], prints: [],
      patronageRef: '', techpackUrl: '', photoUrl: '', notes: '',
      sourceDevId: this._devEditingId || this._lastSavedDevisId || '',
      sourceDevLineIndex: i,
    };
    this.openCatalogueForm(prefill);
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 FICHE TARIFS CLIENT (brochure, r\u00E9g\u00E9n\u00E9r\u00E9e \u00E0 la demande) \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  openFicheTarifsBuilder() {
    document.getElementById('ficheTarifsOverlay').classList.add('active');
    this.renderFicheTarifsBuilder();
  }

  closeFicheTarifsBuilder() { document.getElementById('ficheTarifsOverlay').classList.remove('active'); }

  renderFicheTarifsBuilder() {
    const body = document.getElementById('ficheTarifsBuilderBody');
    if (!body) return;
    const esc = s => this.escHtml(String(s == null ? '' : s));
    body.innerHTML = `
      <p style="font-size:10px;color:var(--88);line-height:1.6;margin-bottom:14px">Cette fiche reprend vos tarifs actuels (marquage textile, d\u00E9veloppement, confection) pr\u00E9sent\u00E9s s\u00E9par\u00E9ment, sans d\u00E9tailler vos calculs internes (coefficients, pourcentages). R\u00E9g\u00E9n\u00E9rez-la \u00E0 chaque envoi pour qu'elle refl\u00E8te vos derniers tarifs.</p>
      <div class="chat-form-field chat-form-field-full">
        <label>Texte de pr\u00E9sentation (m\u00E9moris\u00E9, r\u00E9utilis\u00E9 \u00E0 chaque g\u00E9n\u00E9ration)</label>
        <textarea id="ficheTarifsIntro" rows="8">${esc(this.ficheTarifsIntro)}</textarea>
      </div>
    `;
  }

  async generateFicheTarifs() {
    const introEl = document.getElementById('ficheTarifsIntro');
    this.ficheTarifsIntro = introEl?.value || '';
    try { await wfetch(`${WORKER_URL}/fiche-tarifs-texte`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ intro: this.ficheTarifsIntro }) }); } catch (e) {}

    const html = this.buildFicheTarifsHtml();
    this.closeFicheTarifsBuilder();
    this.showPreview(html, 'fiche-tarifs');
    this.toast('Fiche tarifs g\u00E9n\u00E9r\u00E9e', 'success');
  }

  // Fiche tarifs client RED Studio : prix de la base et prix de l'impression s\u00E9par\u00E9s, rang\u00E9s par cat\u00E9gorie.
  // Tout est lu dans la grille Tarifs : une ligne \u00E0 0 \u20AC s'affiche \u00AB Sur devis \u00BB, une unit\u00E9 \u00AB \u00E0 partir de \u00BB s'affiche \u00AB \u00C0 partir de \u00BB.
  buildFicheTarifsHtml() {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const co = (this.company && this.company.reworked) || this._defaultCompany().reworked;
    const today = this._fmtDateLong(new Date().toISOString().slice(0, 10));
    const T = this.tarifs || {};
    const entries = Object.entries(T).map(([key, t]) => ({ key, ...t }));
    const cents = n => Math.round((parseFloat(n) || 0) * 100);
    const eurC = c => this._eur(c / 100);
    const has = k => T[k] && parseFloat(T[k].price) > 0;
    const priceTxt = t => {
      const p = parseFloat(t.price) || 0;
      if (!p) return 'Sur devis';
      if (/partir de/i.test(t.unit || '')) return `\u00C0 partir de ${this._eur(p)}`;
      return `${this._eur(p)} / ${esc(t.unit)}`;
    };
    const listTable = items => `<table><tbody>${items.map(t => `<tr><td>${esc(t.label)}</td><td>${priceTxt(t)}</td></tr>`).join('')}</tbody></table>`;
    const KNOWN = new Set(['cuisson', 'cadre_garde', 'dtf_small', 'dtf_medium', 'dtf_large', 'dtf_presse', 'base_rwrkd_presta']);

    // S\u00E9rigraphie : une impression par pi\u00E8ce = tirage remis\u00E9 selon la quantit\u00E9 + cuisson (m\u00EAmes paliers que l'assistant devis)
    const REMISES = [
      { label: '1 \u00E0 24 pi\u00E8ces', pct: 0 },
      { label: '25 \u00E0 49 pi\u00E8ces', pct: 10 },
      { label: '50 \u00E0 99 pi\u00E8ces', pct: 15 },
      { label: '100 \u00E0 249 pi\u00E8ces', pct: 20 },
      { label: '250 pi\u00E8ces et plus', pct: 25 },
    ];
    const remiseFor = q => q >= 250 ? 25 : q >= 100 ? 20 : q >= 50 ? 15 : q >= 25 ? 10 : 0;
    const cuisson = cents(T.cuisson ? T.cuisson.price : 1);
    const couleurs = [1, 2, 3, 4].filter(n => has(`seri_impression_${n}c`));
    couleurs.forEach(n => KNOWN.add(`seri_impression_${n}c`));
    const seriImp = (n, pct) => Math.round(cents(T[`seri_impression_${n}c`].price) * (100 - pct) / 100) + cuisson;
    const seriTable = couleurs.length ? `<table><thead><tr><th>Quantit\u00E9</th>${couleurs.map(n => `<th class="col-num">${n} couleur${n > 1 ? 's' : ''}</th>`).join('')}</tr></thead>
      <tbody>${REMISES.map(r => `<tr><td>${r.label}</td>${couleurs.map(n => `<td class="col-num">${eurC(seriImp(n, r.pct))}</td>`).join('')}</tr>`).join('')}</tbody></table>` : '';

    // Frais d'installation : les cadres insol\u00E9s de la cat\u00E9gorie S\u00E9rigraphie
    const cadres = entries.filter(t => t.cat === 'S\u00E9rigraphie' && /^cadre/i.test(t.label || '') && parseFloat(t.price) > 0);
    cadres.forEach(t => KNOWN.add(t.key));
    const cadreMin = cadres.length ? Math.min(...cadres.map(t => cents(t.price))) : 0;
    const instTable = cadres.length ? `<table><thead><tr><th>Cadre</th><th>Prix</th></tr></thead>
      <tbody>${cadres.map(t => `<tr><td>${esc(t.label)}</td><td>${this._eur(parseFloat(t.price))} par couleur</td></tr>`).join('')}</tbody></table>` : '';
    const garde = has('cadre_garde') ? `, puis ${this._eur(parseFloat(T.cadre_garde.price))} par mois et par commande, 4 mois au maximum` : '';

    // DTF : une impression par pi\u00E8ce = format + presse, sans remise ni frais d'installation
    const presse = cents(T.dtf_presse ? T.dtf_presse.price : 0);
    const formats = [['dtf_small', 'Logo'], ['dtf_medium', 'A4'], ['dtf_large', 'A3']].filter(([k]) => has(k));
    const dtfImp = k => cents(T[k].price) + presse;
    const dtfTable = formats.length ? `<table><thead><tr><th>Quantit\u00E9</th>${formats.map(([, l]) => `<th class="col-num">${l}</th>`).join('')}</tr></thead>
      <tbody><tr><td>Toutes quantit\u00E9s</td>${formats.map(([k]) => `<td class="col-num">${eurC(dtfImp(k))}</td>`).join('')}</tr></tbody></table>` : '';

    // T-shirt RWRKD : paliers lus dans les libell\u00E9s \u00AB RWRKD Blanks (1-4 pcs) \u00BB, prix avec impression = ligne base_rwrkd_presta
    const paliers = entries.filter(t => t.cat === 'Bases textiles' && /^RWRKD Blanks/i.test(t.label || '')).map(t => {
      const m = /\((\d+)\s*(?:-\s*(\d+)|\+)\s*pcs\)/i.exec(t.label);
      return m ? { key: t.key, min: +m[1], max: m[2] ? +m[2] : null, price: cents(t.price) } : null;
    }).filter(Boolean).sort((a, b) => a.min - b.min);
    paliers.forEach(p => KNOWN.add(p.key));
    const presta = has('base_rwrkd_presta') ? cents(T.base_rwrkd_presta.price) : null;
    const prestaMax = presta != null ? +((/-\s*(\d+)\s*pcs/i.exec(T.base_rwrkd_presta.label || '') || [])[1] || 49) : 0;
    const baseFor = q => {
      if (presta != null && q <= prestaMax) return presta;
      const p = paliers.filter(x => q >= x.min).pop();
      return p ? p.price : 0;
    };
    const baseTable = paliers.length ? `<table><thead><tr><th>Quantit\u00E9</th><th class="col-num">Avec impression</th><th class="col-num">Sans impression</th></tr></thead>
      <tbody>${paliers.map(p => `<tr><td>${p.max ? `${p.min} \u00E0 ${p.max} pi\u00E8ces` : `${p.min} pi\u00E8ces et plus`}</td><td class="col-num">${eurC(presta != null && p.min <= prestaMax ? presta : p.price)}</td><td class="col-num">${eurC(p.price)}</td></tr>`).join('')}</tbody></table>` : '';

    const IMP_CATS = ['S\u00E9rigraphie', 'DTF', 'Impression'];
    const extrasImp = entries.filter(t => IMP_CATS.includes(t.cat) && !KNOWN.has(t.key));
    const extrasBase = entries.filter(t => t.cat === 'Bases textiles' && !KNOWN.has(t.key));

    // Prix d'appel en t\u00EAte de fiche
    const imps = [...formats.map(([k]) => dtfImp(k)), ...couleurs.map(n => seriImp(n, 25))];
    const bases = [...paliers.map(p => p.price), ...(presta != null ? [presta] : [])];
    const hook = [
      imps.length ? `<span>Impression d\u00E8s <b>${eurC(Math.min(...imps))}</b> la pi\u00E8ce</span>` : '',
      bases.length ? `<span>T-shirt RWRKD d\u00E8s <b>${eurC(Math.min(...bases))}</b></span>` : '',
      '<span>Maquette et BAT <b>offerts</b></span>',
    ].join('');

    // Exemples : t-shirts + impressions + frais d'installation
    const exemple = (titre, q, imp, nbCadres) => {
      const b = q * baseFor(q), im = q * imp.reduce((a, x) => a + x, 0), ins = nbCadres * cadreMin;
      const impTxt = imp.length > 1 ? `(${imp.map(eurC).join(' + ')})` : eurC(imp[0]);
      return `<div class="condition-box border"><h4>${titre}</h4><div class="ex-total">${eurC(b + im + ins)}</div><ul>
        <li>T-shirts : ${q} \u00D7 ${eurC(baseFor(q))} = ${eurC(b)}</li>
        <li>${imp.length > 1 ? 'Impressions' : 'Impression'} : ${q} \u00D7 ${impTxt} = ${eurC(im)}</li>
        <li>Installation : ${nbCadres ? `${nbCadres} cadre${nbCadres > 1 ? 's' : ''} = ${eurC(ins)}` : 'aucune'}</li></ul></div>`;
    };
    const ex = [];
    if (paliers.length && couleurs.includes(1) && cadres.length) ex.push(exemple('50 t-shirts, logo 1 couleur au c\u0153ur', 50, [seriImp(1, remiseFor(50))], 1));
    if (paliers.length && couleurs.includes(2) && cadres.length) ex.push(exemple('10 t-shirts, visuel 2 couleurs', 10, [seriImp(2, remiseFor(10))], 2));
    if (paliers.length && has('dtf_medium')) ex.push(exemple('20 t-shirts, visuel A4 en DTF', 20, [dtfImp('dtf_medium')], 0));
    if (paliers.length && couleurs.includes(1) && couleurs.includes(2) && cadres.length) ex.push(exemple('100 t-shirts, logo 1 couleur au c\u0153ur et visuel 2 couleurs au dos', 100, [seriImp(1, remiseFor(100)), seriImp(2, remiseFor(100))], 3));

    // Autres cat\u00E9gories de la grille, dans cet ordre, puis toute nouvelle cat\u00E9gorie ajout\u00E9e dans l'onglet Tarifs
    const ORDRE = ['Couture et finitions', '\u00C9v\u00E9nementiel et location', 'Cr\u00E9ation graphique', 'D\u00E9veloppement et confection'];
    const autres = {};
    entries.forEach(t => {
      const cat = t.cat || 'Autre';
      if (IMP_CATS.includes(cat) || cat === 'Bases textiles') return;
      (autres[cat] = autres[cat] || []).push(t);
    });
    const cats = [...ORDRE.filter(c => autres[c]), ...Object.keys(autres).filter(c => !ORDRE.includes(c))];
    const autresHtml = cats.map(cat => `<div class="section"><h3 class="section-title">${esc(cat)}</h3>${listTable(autres[cat])}</div>`).join('');

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Grille tarifaire RED Studio</title>
<style>
${this._factureCSS()}
.hook{display:flex;flex-wrap:wrap;gap:6px 28px;margin:-10px 0 18px;font-size:9px;color:#444}
.hook b{font-size:13px;color:#111;font-weight:700}
.sub{font-size:8.5px;font-weight:600;margin:12px 0 6px}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:0 24px}
.grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-top:6px}
.ex-total{font-size:14px;font-weight:700;margin:2px 0 4px}
</style>
</head>
<body>

<div class="header">
  <div>
    <img class="logo" src="https://cdn.shopify.com/s/files/1/0798/6259/5851/t/17/assets/logo-red-studio.svg" alt="R/ED STUDIO">
    <div class="company-info">Atelier de marquage textile \u00B7 Lyon<br>${esc(co.tel)} \u00B7 ${esc(co.email)}</div>
  </div>
  <div class="devis-badge">
    <h2>Grille tarifaire</h2>
    <p>Tarifs en vigueur au ${esc(today)}</p>
  </div>
</div>
<div class="hook">${hook}</div>

<div class="section">
  <p style="font-size:9px;line-height:1.7;white-space:pre-line">${esc(this.ficheTarifsIntro)}</p>
</div>

<div class="section">
  <h3 class="section-title">Impression textile</h3>
  <div class="sub">S\u00E9rigraphie \u00B7 prix de l'impression, par pi\u00E8ce</div>
  ${seriTable}
  <div class="note">Cuisson comprise, t-shirt en plus. Chaque impression de la pi\u00E8ce est compt\u00E9e : un logo au c\u0153ur et un visuel au dos font deux impressions. Remise sur le tirage d\u00E8s 25 pi\u00E8ces.</div>
  <div class="sub">Frais d'installation \u00B7 s\u00E9rigraphie uniquement</div>
  ${instTable}
  <div class="note">Un cadre par couleur et par design, pay\u00E9 une fois par commande. Gard\u00E9 1 mois pour un r\u00E9assort sans frais${garde}.</div>
  <div class="sub">DTF \u00B7 prix de l'impression, par pi\u00E8ce</div>
  ${dtfTable}
  <div class="note">Pose \u00E0 la presse comprise, t-shirt en plus. Aucun frais d'installation, quelle que soit la quantit\u00E9.</div>
  ${extrasImp.length ? `<div class="sub">Sur devis</div>${listTable(extrasImp)}` : ''}
</div>

<div class="section">
  <h3 class="section-title">Bases textiles</h3>
  <div class="sub">T-shirt RWRKD \u00B7 prix par pi\u00E8ce</div>
  ${baseTable}
  <div class="note">Le prix \u00AB avec impression \u00BB s'applique d\u00E8s qu'une impression est command\u00E9e sur la pi\u00E8ce. V\u00EAtements fournis par le client : possible, seule l'impression est factur\u00E9e. Pr\u00E9voir quelques pi\u00E8ces en plus pour les essais.</div>
  ${extrasBase.length ? listTable(extrasBase) : ''}
</div>

${ex.length ? `<div class="section"><h3 class="section-title">Exemples</h3><div class="conditions" style="margin-top:0">${ex.join('')}</div></div>` : ''}

<div class="grid2">${autresHtml}</div>

<div class="grid3">
  <div class="condition-box"><h4>Toujours inclus</h4><ul><li>Maquette offerte</li><li>BAT offert : le visuel final, valid\u00E9 avant l'impression</li><li>Devis et maquette sous 24 h</li><li>Aucun minimum de commande</li></ul></div>
  <div class="condition-box"><h4>D\u00E9lais et livraison</h4><ul><li>Production en 7 jours ouvr\u00E9s apr\u00E8s BAT valid\u00E9 et acompte re\u00E7u, jusqu'\u00E0 250 impressions. Au-del\u00E0, sur devis.</li><li>Retrait \u00E0 l'atelier gratuit, sur rendez-vous</li><li>Envoi en France m\u00E9tropolitaine \u00E0 partir de 9,45 \u20AC, selon le poids</li></ul></div>
  <div class="condition-box"><h4>Conditions</h4><ul><li>Acompte de 40 % \u00E0 la validation, solde \u00E0 la livraison</li><li>Prix nets. ${esc(co.mentionLegale)}</li></ul></div>
</div>

<div class="footer">${esc(co.emetteur)} \u00B7 ${esc(co.mentionLegale)}<br>${esc(co.address)} \u00B7 ${esc(co.tel)} \u00B7 ${esc(co.email)}</div>

</body>
</html>`;
  }

  /* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 INFOS ENTREPRISE (profils \u00E9metteurs) \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */
  async loadSettings() {
    try { const r = await wfetch(`${WORKER_URL}/settings`); if (r.ok) this.company = await r.json(); } catch (e) {}
    if (!this.company || !this.company.reworked) this.company = this._defaultCompany();
    if (!this.company.blanks) this.company.blanks = this._defaultCompany().blanks;
  }

  _settingsFields() {
    return [
      ['emetteur', 'Nom / \u00E9metteur'],
      ['address', 'Adresse (en-t\u00EAte du devis)'],
      ['siret', 'SIRET'],
      ['numTva', 'N\u00B0 TVA (optionnel)'],
      ['tel', 'T\u00E9l\u00E9phone'],
      ['email', 'Email'],
      ['beneficiaire', 'B\u00E9n\u00E9ficiaire du paiement'],
      ['iban', 'IBAN'],
      ['bic', 'BIC / banque'],
      ['mentionLegale', 'Mention l\u00E9gale (pied de page)'],
    ];
  }

  renderSettings() {
    const c = document.getElementById('settingsForm'); if (!c) return;
    if (!this.company) this.company = this._defaultCompany();
    const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    const profiles = [
      ['reworked', 'Profil Prestations (REWORKED)', 'S\u00E9rigraphie, DTF, production, cr\u00E9ation, location, sourcing, d\u00E9veloppement'],
      ['blanks', 'Profil Blanks (SANS C\u0152UR)', 'Vente de blanks vierges seuls, hors prestation'],
    ];
    c.innerHTML = profiles.map(([key, title, sub]) => `
      <div class="tarif-section">
        <h4>${title}</h4>
        <p style="font-size:9px;color:var(--88);margin:-4px 0 12px">${sub}</p>
        ${this._settingsFields().map(([f, label]) => `
          <div class="chat-form-field" style="margin-bottom:10px">
            <label>${label}</label>
            <input type="text" data-profile="${key}" data-field="${f}" value="${esc(this.company[key] && this.company[key][f])}">
          </div>`).join('')}
      </div>
    `).join('');
  }

  async saveSettings() {
    if (!this.company) this.company = this._defaultCompany();
    document.querySelectorAll('#settingsForm input').forEach(el => {
      const p = el.dataset.profile, f = el.dataset.field;
      if (!p || !f) return;
      if (!this.company[p]) this.company[p] = {};
      this.company[p][f] = el.value.trim();
    });
    try {
      await wfetch(`${WORKER_URL}/settings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.company) });
      this.toast('Infos entreprise sauvegard\u00E9es', 'success');
    } catch (e) { this.toast('Erreur', 'error'); }
  }

  _defaultCompany() {
    return {
      reworked: {
        emetteur: 'REWORKED',
        address: '56 Rue Pierre S\u00E9mard, 69600 Oullins-Pierre-B\u00E9nite',
        siret: '938 614 773 00019',
        numTva: '',
        tel: '06 01 42 71 70',
        email: 'atelier@rework-ed.com',
        beneficiaire: 'Guillaume Masson',
        iban: 'FR76 2823 3000 0115 1790 9471 865',
        bic: 'REVOFRP2 (Revolut Bank UAB)',
        mentionLegale: 'Auto-entrepreneur, TVA non applicable (art. 293 B du CGI)',
      },
      blanks: {
        emetteur: 'DEROBERT VALLA LOUIS (SANS C\u0152UR)',
        address: '56 Rue Pierre S\u00E9mard, 69600 Oullins-Pierre-B\u00E9nite',
        siret: '993 646 660 00015',
        numTva: 'FR12993646660',
        tel: '06 01 42 71 70',
        email: 'atelier@rework-ed.com',
        beneficiaire: 'M. Derobert Valla Louis (SANS C\u0152UR EI)',
        iban: 'FR76 1780 6005 9904 2208 6934 891',
        bic: 'AGRIFRPP878 (Cr\u00E9dit Agricole)',
        mentionLegale: 'Entrepreneur individuel (micro-entreprise), TVA non applicable (art. 293 B du CGI), RCS Lyon 993 646 660',
      },
    };
  }

  /* ═══════════ STATS ═══════════ */

  // Parser un montant string en nombre(s) : "2 560 - 4 128 €" -> [2560, 4128], "50 €" -> [50], "40 €/proto" -> [40]
  parseAmount(str) {
    if (!str || str === 'Voir devis') return [];
    // Extraire tous les nombres (avec espaces comme separateurs de milliers)
    const matches = str.match(/[\d][\d\s]*[\d]|[\d]+/g);
    if (!matches) return [];
    return matches.map(m => parseInt(m.replace(/\s/g, ''), 10)).filter(n => !isNaN(n) && n > 0);
  }

  // Retourne le montant "representatif" d'un devis (max de la fourchette, ou le seul montant)
  getDevisAmount(d) {
    const amounts = this.parseAmount(d.montant);
    if (!amounts.length) return 0;
    return Math.max(...amounts);
  }

  // Retourne le montant min d'un devis (pour fourchettes)
  getDevisAmountMin(d) {
    const amounts = this.parseAmount(d.montant);
    if (!amounts.length) return 0;
    return Math.min(...amounts);
  }

  fmtEuro(n) {
    if (n === 0) return '0 €';
    return n.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €';
  }

  renderStats() {
    const total = this.devis.length;
    const devisAcceptes = this.devis.filter(d => d.statut === 'accepte');
    const devisEnvoyes = this.devis.filter(d => d.statut === 'envoye');
    const acc = devisAcceptes.length;
    const env = devisEnvoyes.length;
    const taux = total ? Math.round((acc / total) * 100) : 0;

    // CA calculs
    const caAccepte = devisAcceptes.reduce((s, d) => s + this.getDevisAmount(d), 0);
    const caEnAttente = devisEnvoyes.reduce((s, d) => s + this.getDevisAmount(d), 0);
    const caTotal = this.devis.reduce((s, d) => s + this.getDevisAmount(d), 0);
    const panierMoyen = total ? Math.round(caTotal / total) : 0;

    document.getElementById('statsGrid').innerHTML = `
      <div class="stat-card"><div class="stat-number">${total}</div><div class="stat-label">Devis total</div></div>
      <div class="stat-card"><div class="stat-number" style="color:var(--green)">${acc}</div><div class="stat-label">Accept\u00E9s</div></div>
      <div class="stat-card"><div class="stat-number" style="color:var(--blue)">${env}</div><div class="stat-label">En attente</div></div>
      <div class="stat-card"><div class="stat-number">${taux}%</div><div class="stat-label">Taux acceptation</div></div>
      <div class="stat-card stat-card-ca"><div class="stat-number" style="color:var(--green)">${this.fmtEuro(caAccepte)}</div><div class="stat-label">CA accept\u00E9</div></div>
      <div class="stat-card stat-card-ca"><div class="stat-number" style="color:var(--blue)">${this.fmtEuro(caEnAttente)}</div><div class="stat-label">CA en attente</div></div>
      <div class="stat-card stat-card-ca"><div class="stat-number">${this.fmtEuro(caTotal)}</div><div class="stat-label">CA total</div></div>
      <div class="stat-card stat-card-ca"><div class="stat-number">${this.fmtEuro(panierMoyen)}</div><div class="stat-label">Panier moyen</div></div>
    `;

    // Barres de repartition (par nombre)
    const bar = (data, max, suffix = '') => Object.entries(data).sort((a, b) => b[1] - a[1]).map(([n, c]) =>
      `<div class="stats-bar-row"><div class="stats-bar-label">${n}</div><div class="stats-bar"><div class="stats-bar-fill" style="width:${(c / max) * 100}%"></div></div><div class="stats-bar-value">${typeof c === 'number' && suffix === ' €' ? this.fmtEuro(c) : c + suffix}</div></div>`
    ).join('');

    const byClient = {}; this.devis.forEach(d => byClient[d.client] = (byClient[d.client] || 0) + 1);
    const byType = {}; this.devis.forEach(d => byType[this.typeLabel(d.type)] = (byType[this.typeLabel(d.type)] || 0) + 1);
    const byStatut = {}; this.devis.forEach(d => byStatut[this.statusLabel(d.statut)] = (byStatut[this.statusLabel(d.statut)] || 0) + 1);

    // Top clients par CA
    const caByClient = {};
    this.devis.forEach(d => { caByClient[d.client] = (caByClient[d.client] || 0) + this.getDevisAmount(d); });

    // CA mensuel (tous statuts)
    const caByMonth = {};
    const countByMonth = {};
    this.devis.forEach(d => {
      const m = d.date.slice(0, 7);
      const label = this.fmtMonth(m);
      caByMonth[label] = (caByMonth[label] || 0) + this.getDevisAmount(d);
      countByMonth[label] = (countByMonth[label] || 0) + 1;
    });

    // CA mensuel accepte seulement
    const caAccepteByMonth = {};
    devisAcceptes.forEach(d => {
      const m = d.date.slice(0, 7);
      const label = this.fmtMonth(m);
      caAccepteByMonth[label] = (caAccepteByMonth[label] || 0) + this.getDevisAmount(d);
    });

    const det = document.getElementById('statsDetails'); if (!det) return;
    det.innerHTML = `
      <div class="stats-section" style="margin-bottom:12px">
        <h4>Tendance mensuelle (CA total vs CA accept\u00E9)</h4>
        <div class="stats-monthly-chart">
          ${Object.entries(caByMonth).map(([month, ca]) => {
            const caAcc = caAccepteByMonth[month] || 0;
            const maxCa = Math.max(...Object.values(caByMonth), 1);
            const cnt = countByMonth[month] || 0;
            return `<div class="stats-month-col">
              <div class="stats-month-bars">
                <div class="stats-month-bar stats-month-bar-total" style="height:${(ca / maxCa) * 100}%" title="CA total: ${this.fmtEuro(ca)}"></div>
                <div class="stats-month-bar stats-month-bar-accepte" style="height:${(caAcc / maxCa) * 100}%" title="CA accepte: ${this.fmtEuro(caAcc)}"></div>
              </div>
              <div class="stats-month-label">${month}</div>
              <div class="stats-month-value">${this.fmtEuro(ca)}</div>
              <div class="stats-month-count">${cnt} devis</div>
            </div>`;
          }).join('')}
        </div>
        <div style="display:flex;gap:16px;margin-top:10px;font-size:10px;color:var(--88)">
          <span><span style="display:inline-block;width:10px;height:10px;background:var(--11);border-radius:2px;margin-right:4px;vertical-align:middle"></span>CA total</span>
          <span><span style="display:inline-block;width:10px;height:10px;background:var(--green);border-radius:2px;margin-right:4px;vertical-align:middle"></span>CA accept\u00E9</span>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
        <div class="stats-section"><h4>Top clients par CA</h4>${bar(caByClient, Math.max(...Object.values(caByClient), 1), ' €')}</div>
        <div class="stats-section"><h4>Par type</h4>${bar(byType, Math.max(...Object.values(byType), 1))}</div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:12px">
        <div class="stats-section"><h4>Par client (nombre)</h4>${bar(byClient, Math.max(...Object.values(byClient), 1))}</div>
        <div class="stats-section"><h4>Par statut</h4>${bar(byStatut, Math.max(...Object.values(byStatut), 1))}</div>
      </div>
    `;
  }

  /* ══════════════════════════════════════════════════════════════
     FACTURES
  ══════════════════════════════════════════════════════════════ */

  _facEntiteLabel(e) { return { reworked: 'REWORKED', blanks: 'SANS CŒUR' }[e] || e; }
  _facEntiteCode(e) { return e === 'blanks' ? 'SC' : 'RWK'; }
  _facTypeLabel(t) { return { acompte: 'Acompte', solde: 'Solde', totale: 'Complète', avoir: 'Avoir', perso: 'Libre' }[t] || t; }
  _fbTypeHint(t) {
    return {
      acompte: "Premier versement à la commande (souvent 40 %). Le reste se facture ensuite en « Solde ».",
      solde: "Facture finale après la prestation : reprend le total, déduit l'acompte déjà réglé, affiche le reste dû.",
      totale: "Un seul paiement pour la totalité, sans acompte séparé. Le cas le plus simple.",
      avoir: "Document à montants négatifs pour rembourser ou annuler une facture déjà émise.",
      perso: "Aucun préréglage : vous construisez la facture ligne par ligne comme vous voulez.",
    }[t] || '';
  }
  _facStatutLabel(s) { return { brouillon: 'Brouillon', emise: 'Émise', payee: 'Payée' }[s] || s; }

  _num(v) { const n = parseFloat(String(v == null ? '' : v).replace(/\s/g, '').replace(',', '.')); return isNaN(n) ? 0 : n; }
  _devCoefClientLabel(label) { return (label || '').replace(/^Coef\s*\d+\s*[—-]\s*/i, ''); }
  _eur(n) { const neg = n < 0; const v = Math.abs(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); return (neg ? '-' : '') + v + ' €'; }
  _fmtDateLong(d) {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    const mois = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${parseInt(day)} ${mois[parseInt(m)]} ${y}`;
  }

  /* ═══════════ REGISTRE FACTURES ═══════════ */
  filterFactures() { this.renderFactures(); }

  renderFactures() {
    const tbody = document.getElementById('facturesTableBody');
    if (!tbody) return;
    const q = (document.getElementById('facturesSearch')?.value || '').toLowerCase();
    const fEnt = document.getElementById('facturesFilterEntite')?.value || 'all';
    const fSt = document.getElementById('facturesFilterStatut')?.value || 'all';

    const list = this.factures
      .filter(f => (fEnt === 'all' || f.entite === fEnt) && (fSt === 'all' || f.statut === fSt)
        && (!q || `${f.id} ${f.client} ${f.objet} ${f.montant} ${f.devisId || ''}`.toLowerCase().includes(q)))
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

    const badge = document.getElementById('facturesBadge');
    if (badge) badge.textContent = this.factures.length;

    if (!list.length) {
      tbody.innerHTML = `<tr><td colspan="8" style="text-align:center;color:var(--88);padding:24px;font-size:12px">Aucune facture. Cliquez sur « + Nouvelle facture ».</td></tr>`;
      return;
    }

    tbody.innerHTML = list.map(f => {
      if (f.isDraft) {
        return `<tr style="cursor:default;background:var(--f9)">
          <td class="td-id" style="font-weight:600;font-size:10px;color:var(--88)">BROUILLON</td>
          <td class="td-date" style="font-size:11px;color:var(--66)">${this.fmtDate(f.date)}</td>
          <td class="td-client" style="font-weight:600">${this.escHtml(f.client || '')}</td>
          <td><span class="source-tag ${f.entite === 'blanks' ? 'source-shopify' : 'source-local'}">${this._facEntiteLabel(f.entite)}</span></td>
          <td><span class="type-tag type-production">${this._facTypeLabel(f.type)}</span></td>
          <td class="td-montant" style="font-weight:600;text-align:right;white-space:nowrap">${this.escHtml(f.montant || '')}</td>
          <td class="td-statut"><span class="status status-brouillon">Brouillon</span></td>
          <td class="td-actions"><div class="devis-actions">
            <button onclick="app.viewFacture('${f.id}')">Voir</button>
            <button onclick="app.editFacture('${f.id}')">Reprendre</button>
            <button class="devis-action-delete" onclick="app.deleteFacture('${f.id}')">Suppr.</button>
          </div></td>
        </tr>`;
      }
      return `<tr style="cursor:default">
      <td class="td-id" style="font-weight:600;font-family:monospace;font-size:11px">${f.id}</td>
      <td class="td-date" style="font-size:11px;color:var(--66)">${this.fmtDate(f.date)}</td>
      <td class="td-client" style="font-weight:600">${this.escHtml(f.client || '')}</td>
      <td><span class="source-tag ${f.entite === 'blanks' ? 'source-shopify' : 'source-local'}">${this._facEntiteLabel(f.entite)}</span></td>
      <td><span class="type-tag type-production">${this._facTypeLabel(f.type)}</span></td>
      <td class="td-montant" style="font-weight:600;text-align:right;white-space:nowrap">${this.escHtml(f.montant || '')}</td>
      <td class="td-statut"><select class="status status-${f.statut === 'payee' ? 'accepte' : f.statut === 'emise' ? 'envoye' : 'brouillon'}" onchange="app.changeFactureStatus('${f.id}',this.value)" style="border:none;cursor:pointer;font-family:inherit;font-size:9px;font-weight:600;padding:3px 6px;border-radius:4px;background:inherit;color:inherit">
        ${['brouillon', 'emise', 'payee'].map(s => `<option value="${s}" ${f.statut === s ? 'selected' : ''}>${this._facStatutLabel(s)}</option>`).join('')}
      </select></td>
      <td class="td-actions"><div class="devis-actions">
        <button onclick="app.viewFacture('${f.id}')">Voir</button>
        <button onclick="app.editFacture('${f.id}')">Modifier</button>
        <button onclick="app.showEmailModal('${f.id}')">Email</button>
        <button onclick="app.duplicateFacture('${f.id}')">Dupliquer</button>
        <button class="devis-action-delete" onclick="app.deleteFacture('${f.id}')">Suppr.</button>
      </div></td>
    </tr>`; }).join('');
  }

  async viewFacture(id) {
    try {
      const r = await wfetch(`${WORKER_URL}/factures/${encodeURIComponent(id)}`);
      if (r.ok) { const d = await r.json(); if (d.html) { this.showPreview(d.html, id); return; } }
    } catch (e) {}
    this.toast('Pas de HTML pour cette facture', 'error');
  }

  async changeFactureStatus(id, s) {
    const f = this.factures.find(x => x.id === id);
    if (!f) return;
    f.statut = s; this.renderFactures();
    try { await wfetch(`${WORKER_URL}/factures/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ statut: s }) }); } catch (e) {}
    this.toast(`${id} : ${this._facStatutLabel(s)}`);
  }

  async deleteFacture(id) {
    if (!confirm(`Supprimer la facture ${id} ? Action irréversible.`)) return;
    this.factures = this.factures.filter(f => f.id !== id);
    this.renderFactures();
    try { await wfetch(`${WORKER_URL}/factures/${encodeURIComponent(id)}`, { method: 'DELETE' }); } catch (e) {}
    this.toast(`Facture ${id} supprimée`);
  }

  async duplicateFacture(id) {
    const f = this.factures.find(x => x.id === id);
    if (!f) return;
    if (!this.company) this.company = this._defaultCompany();
    const today = new Date().toISOString().slice(0, 10);
    if (f.snap) {
      this._fb = JSON.parse(JSON.stringify(f.snap));
    } else {
      this._fb = {
        entite: f.entite || 'reworked', devisId: f.devisId || '', num: '',
        dateEmission: today, dateVente: today, type: f.type || 'perso', statut: 'emise',
        clientName: f.client || '', clientAddress: '', clientSiret: '', contact: '', objet: f.objet || '',
        lines: [{ designation: f.objet || 'Prestation', detail: '', qte: 1, pu: 0 }],
        tvaActive: false, tvaRate: 20, acompteVerse: 0, acompteLabel: 'Acompte déjà réglé',
        paid: false, paidDate: today, paidMethod: 'Virement bancaire', netOverride: '', reglement: '', notes: '',
      };
    }
    this._fb.num = '';
    this._fbEditingId = null;
    this._fbDevisTotal = 0;
    this._fbDevisLines = null;
    document.getElementById('factureOverlay').classList.add('active');
    this.renderFactureBuilder();
    this.toast('Facture dupliquée, ajustez puis générez');
  }

  /* ═══════════ BUILDER ═══════════ */
  async openFactureBuilder(devisId, skipDraft) {
    if (!this.company) this.company = this._defaultCompany();
    this._fbEditingId = null;
    const today = new Date().toISOString().slice(0, 10);
    this._fb = {
      entite: 'reworked', devisId: devisId || '', num: '',
      dateEmission: today, dateVente: today,
      type: 'acompte', statut: 'emise',
      clientName: '', clientAddress: '', clientSiret: '', contact: '',
      objet: '',
      lines: [{ designation: '', detail: '', qte: 1, pu: 0 }],
      tvaActive: false, tvaRate: 20,
      acompteVerse: 0, acompteLabel: 'Acompte déjà réglé',
      paid: false, paidDate: today, paidMethod: 'Virement bancaire',
      netOverride: '', reglement: '', notes: '',
    };
    this._fbDevisTotal = 0;
    this._fbDevisLines = null;
    document.getElementById('factureOverlay').classList.add('active');
    if (devisId) { await this._fbLoadDevis(devisId); }
    this._fbApplyType();
    this.renderFactureBuilder();
  }

  closeFactureBuilder() { document.getElementById('factureOverlay').classList.remove('active'); this._fb = null; this._fbEditingId = null; }

  // Y a-t-il du contenu qui vaut la peine d'etre sauvegarde ?
  _fbHasContent(fb) {
    fb = fb || this._fb;
    if (!fb) return false;
    if ((fb.clientName || '').trim()) return true;
    if ((fb.objet || '').trim()) return true;
    return (fb.lines || []).some(l => (l.designation || '').trim() || this._num(l.pu));
  }

  // Tentative de fermeture : confirme, et enregistre un brouillon partagé si progression
  async attemptCloseFacture() {
    if (!this._fb) { this.closeFactureBuilder(); return; }
    this._fbReadForm();
    if (!this._fbHasContent()) { this.closeFactureBuilder(); return; }
    // Édition d'une facture déjà finalisée : on ne crée pas de brouillon, on confirme juste
    const editEntry = this._fbEditingId ? this.factures.find(f => f.id === this._fbEditingId) : null;
    if (editEntry && !editEntry.isDraft) {
      if (confirm('Quitter sans enregistrer les modifications de cette facture ?')) this.closeFactureBuilder();
      return;
    }
    if (!confirm('Quitter la création de facture ?\n\nUn brouillon sera enregistré dans la liste des factures (accessible à tous les utilisateurs de l\'app), vous pourrez le reprendre.')) return;
    await this._saveFactureDraft();
    this.closeFactureBuilder();
  }

  // Enregistre / met à jour un brouillon dans le registre partagé (KV), sans numéro de facture
  async _saveFactureDraft() {
    const fb = this._fb;
    const c = this._fbCompute();
    const existing = this._fbEditingId ? this.factures.find(f => f.id === this._fbEditingId) : null;
    const id = (existing && existing.isDraft) ? existing.id : `BR-${Date.now()}`;
    const profile = (this.company && this.company[fb.entite]) || this._defaultCompany()[fb.entite];
    const data = {
      num: 'Brouillon', entite: fb.entite, company: profile, typeTitle: 'Brouillon de facture',
      dateEmissionFr: this._fmtDateLong(fb.dateEmission), dateVenteFr: this._fmtDateLong(fb.dateVente),
      client: { name: (fb.clientName || '(brouillon)').toUpperCase(), address: fb.clientAddress, siret: fb.clientSiret },
      contact: fb.contact, objet: fb.objet, devisId: fb.devisId,
      lines: c.lines, sousTotal: c.sousTotal, tvaActive: c.tvaActive, tvaRate: c.tvaRate, tvaAmount: c.tvaAmount, totalTTC: c.totalTTC,
      acompte: c.acompte, acompteLabel: fb.acompteLabel, net: c.net,
      paid: c.paid, paidNow: c.paidNow, paidDateFr: this._fmtDateLong(fb.paidDate), paidMethod: fb.paidMethod,
      isAvoir: fb.type === 'avoir', reglement: fb.reglement, notes: fb.notes,
      logoUrl: 'https://cdn.shopify.com/s/files/1/0798/6259/5851/t/17/assets/logo-red-studio.svg',
    };
    const html = this.buildFactureHtml(data);
    const entry = {
      id, num: '', isDraft: true, entite: fb.entite, type: fb.type, devisId: fb.devisId || '',
      client: (fb.clientName || '(brouillon)').toUpperCase(), objet: fb.objet || this._facTypeLabel(fb.type),
      date: fb.dateEmission, montant: this._eur(c.paid ? c.totalTTC : c.net), statut: 'brouillon',
      snap: { ...fb, num: '', lines: fb.lines.map(l => ({ ...l })) },
    };
    const idx = this.factures.findIndex(x => x.id === id);
    if (idx >= 0) this.factures[idx] = entry; else this.factures.push(entry);
    try {
      await wfetch(`${WORKER_URL}/factures`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entry) });
      await wfetch(`${WORKER_URL}/factures/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ html }) });
    } catch (e) {}
    this.renderFactures();
    this.toast('Brouillon enregistré dans les factures', 'success');
  }

  // Alloue un numéro de facture (garde le numéro existant si déjà défini)
  async _allocateFactureNumber() {
    const fb = this._fb;
    if (fb.num) return fb.num;
    let num = '';
    try {
      const r = await wfetch(`${WORKER_URL}/next-facture-number?entite=${this._facEntiteCode(fb.entite)}`);
      if (r.ok) num = (await r.json()).number;
    } catch (e) {}
    if (!num) { const y = new Date().getFullYear(); num = `FAC-${this._facEntiteCode(fb.entite)}-${y}-${String(this.factures.length + 1).padStart(3, '0')}`; }
    return num;
  }

  // Enregistre une facture finalisée (retire l'ancien brouillon le cas échéant), aperçu
  async _commitFacture(num, html, c, montantStr) {
    const fb = this._fb;
    const editingEntry = this._fbEditingId ? this.factures.find(f => f.id === this._fbEditingId) : null;
    const entry = {
      id: num, num, entite: fb.entite, type: fb.type, devisId: fb.devisId || '',
      client: (fb.clientName || '').toUpperCase(), objet: fb.objet || this._facTypeLabel(fb.type),
      date: fb.dateEmission, montant: montantStr || this._eur(c.paid ? c.totalTTC : c.net),
      statut: c.paid ? 'payee' : fb.statut,
      snap: { ...fb, num, lines: fb.lines.map(l => ({ ...l })) },
    };
    // Si on finalise depuis un brouillon (id différent), supprimer l'ancien brouillon
    if (editingEntry && editingEntry.id !== num) {
      this.factures = this.factures.filter(f => f.id !== editingEntry.id);
      try { await wfetch(`${WORKER_URL}/factures/${encodeURIComponent(editingEntry.id)}`, { method: 'DELETE' }); } catch (e) {}
    }
    const idx = this.factures.findIndex(f => f.id === num);
    if (idx >= 0) this.factures[idx] = entry; else this.factures.push(entry);
    this._fbEditingId = null; fb.num = num;
    try {
      await wfetch(`${WORKER_URL}/factures`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entry) });
      await wfetch(`${WORKER_URL}/factures/${encodeURIComponent(num)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ html }) });
    } catch (e) {}
    if (!this.clients.find(cl => (cl.name || '').toUpperCase() === entry.client)) {
      const nc = { name: entry.client, address: fb.clientAddress, siret: fb.clientSiret, email: '', contacts: fb.contact, source: 'local' };
      this.clients.push(nc);
      try { await wfetch(`${WORKER_URL}/clients`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(nc) }); } catch (e) {}
    }
    this.renderFactures();
    this.closeFactureBuilder();
    this._lastFactureHtml = html;
    this.showPreview(html, num);
  }

  _extractNetFromFactureHtml(html) {
    const m = html.match(/class="row-net"[\s\S]*?class="col-num"[^>]*>([^<]+)</i);
    if (!m) return null;
    const v = this._num((m[1].match(/-?[\d\s.,]+/) || [''])[0]);
    return isNaN(v) ? null : v;
  }

  // Rouvre le builder pré-rempli depuis une facture existante (même numéro)
  async editFacture(id) {
    const f = this.factures.find(x => x.id === id);
    if (!f) return;
    if (!this.company) this.company = this._defaultCompany();
    const today = new Date().toISOString().slice(0, 10);
    if (f.snap) {
      this._fb = JSON.parse(JSON.stringify(f.snap));
    } else {
      // Ancienne facture sans snapshot : builder minimal reconstruit depuis le registre
      this._fb = {
        entite: f.entite || 'reworked', devisId: f.devisId || '', num: f.id,
        dateEmission: f.date || today, dateVente: f.date || today, type: f.type || 'perso', statut: f.statut || 'emise',
        clientName: f.client || '', clientAddress: '', clientSiret: '', contact: '', objet: f.objet || '',
        lines: [{ designation: f.objet || 'Prestation', detail: '', qte: 1, pu: this._num((f.montant || '').replace(/[^\d.,-]/g, '')) }],
        tvaActive: false, tvaRate: 20, acompteVerse: 0, acompteLabel: 'Acompte déjà réglé',
        paid: f.statut === 'payee', paidDate: f.date || today, paidMethod: 'Virement bancaire',
        netOverride: '', reglement: 'Facture payable à réception.', notes: '',
      };
    }
    this._fb.num = f.isDraft ? '' : f.id;
    this._fbEditingId = id;
    this._fbDevisTotal = 0;
    this._fbDevisLines = null;
    document.getElementById('factureOverlay').classList.add('active');
    this.renderFactureBuilder();
    this.toast(f.isDraft ? `Reprise du brouillon` : `Modification de ${id}`);
  }

  async _fbLoadDevis(devisId) {
    const d = this.devis.find(x => x.id === devisId);
    if (!d) return;
    this._fb.devisId = devisId;
    this._fb.clientName = d.client || '';
    this._fb.objet = d.objet || '';
    this._fbDevisTotal = this.getDevisAmount(d);
    // Compléter client depuis la fiche
    const cl = this.clients.find(c => (c.name || '').toUpperCase() === (d.client || '').toUpperCase());
    if (cl) { this._fb.clientAddress = cl.address || ''; this._fb.clientSiret = cl.siret || ''; }
    // Extraire les lignes du HTML du devis (best effort)
    try {
      const r = await wfetch(`${WORKER_URL}/devis/${encodeURIComponent(devisId)}`);
      if (r.ok) { const j = await r.json(); if (j.html) this._fbDevisLines = this._extractLinesFromDevisHtml(j.html); }
    } catch (e) {}
  }

  // Applique les valeurs par défaut selon le type choisi
  _fbApplyType() {
    const t = this._fb.type;
    const total = this._fbDevisTotal || 0;
    const devLines = (this._fbDevisLines && this._fbDevisLines.length) ? this._fbDevisLines.map(l => ({ ...l })) : null;
    const ref = this._fb.devisId ? `Sur devis N° ${this._fb.devisId}` : '';
    const round2 = n => Math.round(n * 100) / 100;

    if (t === 'acompte') {
      const pu = total ? round2(total * 0.4) : 0;
      this._fb.lines = [{ designation: 'Acompte de 40 % à la commande', detail: ref, qte: 1, pu }];
      this._fb.acompteVerse = 0;
      this._fb.reglement = "Acompte payable à la validation de la commande. Le solde fera l'objet d'une facture séparée après réalisation de la prestation.";
    } else if (t === 'solde') {
      this._fb.lines = devLines || [{ designation: 'Prestation (solde)', detail: ref, qte: 1, pu: total }];
      this._fb.acompteVerse = total ? round2(total * 0.4) : 0;
      this._fb.acompteLabel = 'Acompte de 40 % déjà réglé';
      this._fb.reglement = 'Solde payable à réception de la facture.';
    } else if (t === 'totale') {
      this._fb.lines = devLines || [{ designation: this._fb.objet || 'Prestation', detail: ref, qte: 1, pu: total }];
      this._fb.acompteVerse = 0;
      this._fb.reglement = 'Facture payable à réception.';
    } else if (t === 'avoir') {
      this._fb.lines = [{ designation: 'Avoir', detail: ref, qte: 1, pu: total ? -Math.abs(round2(total)) : 0 }];
      this._fb.acompteVerse = 0;
      this._fb.reglement = "Avoir à valoir sur une prochaine commande ou remboursement selon accord.";
    } else { // perso
      if (!this._fb.lines || !this._fb.lines.length) this._fb.lines = [{ designation: '', detail: '', qte: 1, pu: 0 }];
      this._fb.reglement = this._fb.reglement || 'Facture payable à réception.';
    }
  }

  // Lit les champs scalaires du DOM vers this._fb (hors lignes, gérées à part)
  _fbReadForm() {
    if (!this._fb) return;
    const g = id => document.getElementById(id);
    this._fb.entite = g('fbEntite')?.value || 'reworked';
    this._fb.devisId = g('fbDevis')?.value || '';
    this._fb.num = g('fbNum')?.value.trim() || '';
    this._fb.dateEmission = g('fbDateEmission')?.value || this._fb.dateEmission;
    this._fb.dateVente = g('fbDateVente')?.value || this._fb.dateVente;
    this._fb.type = g('fbType')?.value || 'perso';
    this._fb.statut = g('fbStatut')?.value || 'emise';
    this._fb.clientName = g('fbClientName')?.value || '';
    this._fb.clientAddress = g('fbClientAddress')?.value || '';
    this._fb.clientSiret = g('fbClientSiret')?.value || '';
    this._fb.contact = g('fbContact')?.value || '';
    this._fb.objet = g('fbObjet')?.value || '';
    this._fb.tvaActive = g('fbTvaActive')?.checked || false;
    this._fb.tvaRate = this._num(g('fbTvaRate')?.value) || 20;
    this._fb.acompteVerse = this._num(g('fbAcompteVerse')?.value);
    this._fb.acompteLabel = g('fbAcompteLabel')?.value || 'Acompte déjà réglé';
    this._fb.paid = g('fbPaid')?.checked || false;
    this._fb.paidDate = g('fbPaidDate')?.value || this._fb.paidDate;
    this._fb.paidMethod = g('fbPaidMethod')?.value || 'Virement bancaire';
    this._fb.netOverride = g('fbNetOverride')?.value.trim() || '';
    this._fb.reglement = g('fbReglement')?.value || '';
    this._fb.notes = g('fbNotes')?.value || '';
    this._fbReadLines();
  }

  _fbReadLines() {
    if (!this._fb) return;
    const rows = document.querySelectorAll('#fbLinesBody tr');
    const lines = [];
    rows.forEach(tr => {
      const desig = tr.querySelector('[data-l="designation"]')?.value || '';
      const detail = tr.querySelector('[data-l="detail"]')?.value || '';
      const qte = this._num(tr.querySelector('[data-l="qte"]')?.value);
      const pu = this._num(tr.querySelector('[data-l="pu"]')?.value);
      lines.push({ designation: desig, detail, qte, pu });
    });
    if (lines.length) this._fb.lines = lines;
  }

  // Handlers de changement (relisent le formulaire puis re-rendent ce qu'il faut)
  onFbEntiteChange() { this._fbReadForm(); this.renderFactureBuilder(); }
  onFbDevisChange() { this._fbReadForm(); const id = this._fb.devisId; if (id) { this.openFactureBuilderKeepType(id); } }
  async openFactureBuilderKeepType(id) {
    const keepType = this._fb.type;
    await this._fbLoadDevis(id);
    this._fb.type = keepType;
    this._fbApplyType();
    this.renderFactureBuilder();
  }
  onFbTypeChange() { this._fbReadForm(); this._fbApplyType(); this.renderFactureBuilder(); }
  onFbPaidChange() { this._fbReadForm(); this.fbRecalc(); }

  fbAddLine() { this._fbReadForm(); this._fb.lines.push({ designation: '', detail: '', qte: 1, pu: 0 }); this._fbRenderLines(); }
  fbRemoveLine(i) { this._fbReadForm(); this._fb.lines.splice(i, 1); if (!this._fb.lines.length) this._fb.lines.push({ designation: '', detail: '', qte: 1, pu: 0 }); this._fbRenderLines(); }

  _fbCompute() {
    const lines = this._fb.lines.map(l => ({ ...l, total: this._num(l.qte) * this._num(l.pu) }));
    const sousTotal = lines.reduce((s, l) => s + l.total, 0);
    const tvaActive = this._fb.tvaActive;
    const tvaAmount = tvaActive ? sousTotal * (this._num(this._fb.tvaRate) / 100) : 0;
    const totalTTC = sousTotal + tvaAmount;
    const acompte = this._num(this._fb.acompteVerse);
    // Facture acquittée si case cochée OU statut "payée"
    const paid = this._fb.paid || this._fb.statut === 'payee';
    let reste = totalTTC - acompte;              // ce qui resterait à payer hors acquittement
    const paidNow = paid ? reste : 0;            // montant réglé maintenant (mention acquittée)
    let net = paid ? 0 : reste;
    if (!paid && this._fb.netOverride !== '' && this._fb.netOverride != null) net = this._num(this._fb.netOverride);
    return { lines, sousTotal, tvaActive, tvaRate: this._num(this._fb.tvaRate), tvaAmount, totalTTC, acompte, paid, paidNow, net };
  }

  fbRecalc() {
    this._fbReadLines();
    const c = this._fbCompute();
    // Mettre à jour les totaux de lignes
    document.querySelectorAll('#fbLinesBody tr').forEach((tr, i) => {
      const cell = tr.querySelector('[data-l="total"]');
      if (cell && c.lines[i]) cell.textContent = this._eur(c.lines[i].total);
    });
    const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
    set('fbSousTotal', this._eur(c.sousTotal));
    set('fbTvaLine', c.tvaActive ? `${this._eur(c.tvaAmount)} (TVA ${c.tvaRate} %)` : '—');
    set('fbNetLabel', c.paid ? 'Net à payer (acquittée)' : 'Net à payer');
    set('fbNet', this._eur(c.net));
  }

  renderFactureBuilder() {
    const body = document.getElementById('factureBuilderBody');
    if (!body || !this._fb) return;
    const hdr = document.querySelector('#factureOverlay .preview-header span');
    if (hdr) hdr.textContent = this._fbEditingId ? `Modifier facture ${this._fbEditingId}` : 'Nouvelle facture';
    const fb = this._fb;
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const devisOptions = ['<option value="">Aucun (facture libre)</option>']
      .concat(this.devis.slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''))
        .map(d => `<option value="${d.id}" ${fb.devisId === d.id ? 'selected' : ''}>${d.id} · ${esc(d.client)} · ${esc(d.montant || '')}</option>`)).join('');

    const sel = (v, cur) => v === cur ? 'selected' : '';
    body.innerHTML = `
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Entité émettrice</label>
          <select id="fbEntite" onchange="app.onFbEntiteChange()">
            <option value="reworked" ${sel('reworked', fb.entite)}>REWORKED (prestations)</option>
            <option value="blanks" ${sel('blanks', fb.entite)}>SANS CŒUR (blanks)</option>
          </select>
        </div>
        <div class="chat-form-field">
          <label>Depuis un devis</label>
          <select id="fbDevis" onchange="app.onFbDevisChange()">${devisOptions}</select>
        </div>
      </div>

      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Type de facture</label>
          <select id="fbType" onchange="app.onFbTypeChange()">
            <option value="acompte" ${sel('acompte', fb.type)}>Acompte — 1er versement</option>
            <option value="solde" ${sel('solde', fb.type)}>Solde — reste à payer</option>
            <option value="totale" ${sel('totale', fb.type)}>Facture complète — payée en une fois</option>
            <option value="avoir" ${sel('avoir', fb.type)}>Avoir — remboursement / annulation</option>
            <option value="perso" ${sel('perso', fb.type)}>Libre — je remplis tout</option>
          </select>
          <div style="font-size:10px;color:var(--88);margin-top:5px;line-height:1.45">${esc(this._fbTypeHint(fb.type))}</div>
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>Numéro (auto si vide)</label>
          <input type="text" id="fbNum" value="${esc(fb.num)}" placeholder="FAC-${this._facEntiteCode(fb.entite)}-...">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>Statut</label>
          <select id="fbStatut">
            <option value="brouillon" ${sel('brouillon', fb.statut)}>Brouillon</option>
            <option value="emise" ${sel('emise', fb.statut)}>Émise</option>
            <option value="payee" ${sel('payee', fb.statut)}>Payée</option>
          </select>
        </div>
      </div>

      <div class="chat-form-row">
        <div class="chat-form-field chat-form-field-sm">
          <label>Date d'émission</label>
          <input type="date" id="fbDateEmission" value="${esc(fb.dateEmission)}">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>Date de vente / prestation</label>
          <input type="date" id="fbDateVente" value="${esc(fb.dateVente)}">
        </div>
        <div class="chat-form-field">
          <label>Objet</label>
          <input type="text" id="fbObjet" value="${esc(fb.objet)}" placeholder="Ex : Production 50 t-shirts DTF">
        </div>
      </div>

      <div class="fb-section-title">Client</div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Nom</label>
          <input type="text" id="fbClientName" list="clientSuggestions" value="${esc(fb.clientName)}">
        </div>
        <div class="chat-form-field chat-form-field-sm">
          <label>SIRET</label>
          <input type="text" id="fbClientSiret" value="${esc(fb.clientSiret)}">
        </div>
      </div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <label>Adresse</label>
          <input type="text" id="fbClientAddress" value="${esc(fb.clientAddress)}">
        </div>
        <div class="chat-form-field">
          <label>Contact (optionnel)</label>
          <input type="text" id="fbContact" value="${esc(fb.contact)}">
        </div>
      </div>

      <div class="fb-section-title">Lignes</div>
      <table class="fb-lines"><thead><tr>
        <th style="text-align:left">Désignation</th><th style="width:60px">Qté</th><th style="width:90px">P.U.</th><th style="width:90px;text-align:right">Total</th><th style="width:30px"></th>
      </tr></thead><tbody id="fbLinesBody"></tbody></table>
      <button class="btn btn-ghost btn-sm" onclick="app.fbAddLine()" style="margin-top:8px">+ Ligne</button>

      <div class="fb-section-title">Montants</div>
      <div style="display:flex;flex-wrap:wrap;gap:16px;align-items:flex-end">
        <label style="display:flex;align-items:center;gap:6px;font-size:11px;color:var(--66)">
          <input type="checkbox" id="fbTvaActive" ${fb.tvaActive ? 'checked' : ''} onchange="app.fbRecalc()"> Appliquer une TVA
        </label>
        <div class="chat-form-field chat-form-field-sm" style="flex:0 0 90px">
          <label>Taux TVA %</label>
          <input type="number" id="fbTvaRate" value="${esc(fb.tvaRate)}" oninput="app.fbRecalc()">
        </div>
        <div class="chat-form-field chat-form-field-sm" style="flex:0 0 150px">
          <label>Acompte déjà réglé (€)</label>
          <input type="number" step="0.01" id="fbAcompteVerse" value="${fb.acompteVerse || 0}" oninput="app.fbRecalc()">
        </div>
        <div class="chat-form-field" style="flex:1">
          <label>Libellé acompte</label>
          <input type="text" id="fbAcompteLabel" value="${esc(fb.acompteLabel)}">
        </div>
        <div class="chat-form-field chat-form-field-sm" style="flex:0 0 150px">
          <label>Net à payer (forcer, €)</label>
          <input type="text" id="fbNetOverride" value="${esc(fb.netOverride)}" oninput="app.fbRecalc()" placeholder="auto">
        </div>
      </div>
      <div class="fb-totals">
        <div><span>Sous-total</span><strong id="fbSousTotal">—</strong></div>
        <div><span>TVA</span><strong id="fbTvaLine">—</strong></div>
        <div class="fb-total-net"><span id="fbNetLabel">Net à payer</span><strong id="fbNet">—</strong></div>
      </div>

      <div class="fb-section-title">Paiement reçu</div>
      <div style="display:flex;flex-wrap:wrap;gap:16px;align-items:flex-end">
        <label style="display:flex;align-items:center;gap:6px;font-size:11px;color:var(--66)">
          <input type="checkbox" id="fbPaid" ${(fb.paid || fb.statut === 'payee') ? 'checked' : ''} onchange="app.onFbPaidChange()"> Facture déjà réglée (acquittée)
        </label>
        <div class="chat-form-field chat-form-field-sm" style="flex:0 0 160px">
          <label>Date de paiement</label>
          <input type="date" id="fbPaidDate" value="${esc(fb.paidDate)}">
        </div>
        <div class="chat-form-field" style="flex:1;min-width:180px">
          <label>Moyen de paiement</label>
          <input type="text" id="fbPaidMethod" value="${esc(fb.paidMethod)}" placeholder="Virement, CB, espèces...">
        </div>
      </div>
      <p style="font-size:10px;color:var(--88);margin-top:6px">Si coché (ou statut « Payée »), la facture affichera la mention « acquittée », le montant réglé et un net à payer de 0 €.</p>

      <div class="fb-section-title">Conditions & notes</div>
      <div class="chat-form-field chat-form-field-full">
        <label>Conditions de règlement</label>
        <textarea id="fbReglement" rows="2">${esc(fb.reglement)}</textarea>
      </div>
      <div class="chat-form-field chat-form-field-full">
        <label>Notes (optionnel)</label>
        <textarea id="fbNotes" rows="2">${esc(fb.notes)}</textarea>
      </div>

      <div class="fb-section-title">Ajuster avec Claude (optionnel)</div>
      <div class="chat-form-row">
        <div class="chat-form-field">
          <input type="text" id="fbClaudeInstruction" placeholder="Ex : ajoute une remise de 10 %, reformule l'objet...">
        </div>
        <button class="btn btn-ghost btn-sm" onclick="app.adjustFactureWithClaude()" style="flex-shrink:0">Ajuster avec Claude</button>
      </div>
    `;
    this._fbRenderLines();
    this._fbSyncClientList();
  }

  _fbSyncClientList() {
    // Réutilise la datalist clientSuggestions déjà peuplée
    if (!document.getElementById('clientSuggestions')) return;
  }

  _fbRenderLines() {
    const tb = document.getElementById('fbLinesBody');
    if (!tb || !this._fb) return;
    const esc = s => this.escHtml(String(s == null ? '' : s));
    tb.innerHTML = this._fb.lines.map((l, i) => `<tr>
      <td>
        <input type="text" data-l="designation" value="${esc(l.designation)}" placeholder="Désignation" style="width:100%">
        <input type="text" data-l="detail" value="${esc(l.detail)}" placeholder="détail (optionnel)" style="width:100%;margin-top:3px;font-size:10px;color:var(--66)">
      </td>
      <td><input type="number" step="0.01" data-l="qte" value="${l.qte}" oninput="app.fbRecalc()" style="width:100%"></td>
      <td><input type="number" step="0.01" data-l="pu" value="${l.pu}" oninput="app.fbRecalc()" style="width:100%"></td>
      <td data-l="total" style="text-align:right;font-weight:600;white-space:nowrap">${this._eur(this._num(l.qte) * this._num(l.pu))}</td>
      <td><button class="tarif-delete" onclick="app.fbRemoveLine(${i})" title="Supprimer">✕</button></td>
    </tr>`).join('');
    this.fbRecalc();
  }

  /* ═══════════ GÉNÉRATION ═══════════ */
  async generateFacture() {
    if (!this._fb) return;
    this._fbReadForm();
    const fb = this._fb;
    if (!fb.clientName.trim()) { this.toast('Renseignez le nom du client', 'error'); return; }

    const num = await this._allocateFactureNumber();
    if (!this._fbEditingId && this.factures.find(f => f.id === num)) { this.toast(`La facture ${num} existe déjà`, 'error'); return; }

    const c = this._fbCompute();
    const data = this._factureData(num, c);
    const html = this.buildFactureHtml(data);
    await this._commitFacture(num, html, c);
    this.toast(`Facture ${num} générée`, 'success');
  }

  // Construit l'objet de données pour buildFactureHtml à partir de l'état du builder
  _factureData(num, c) {
    const fb = this._fb;
    const profile = (this.company && this.company[fb.entite]) || this._defaultCompany()[fb.entite];
    const titles = { acompte: "Facture d'acompte", solde: 'Facture de solde', totale: 'Facture', avoir: 'Avoir', perso: 'Facture' };
    return {
      num, entite: fb.entite, company: profile,
      typeTitle: titles[fb.type] || 'Facture',
      dateEmissionFr: this._fmtDateLong(fb.dateEmission),
      dateVenteFr: this._fmtDateLong(fb.dateVente),
      client: { name: (fb.clientName || '').toUpperCase(), address: fb.clientAddress, siret: fb.clientSiret },
      contact: fb.contact, objet: fb.objet, devisId: fb.devisId,
      lines: c.lines, sousTotal: c.sousTotal,
      tvaActive: c.tvaActive, tvaRate: c.tvaRate, tvaAmount: c.tvaAmount, totalTTC: c.totalTTC,
      acompte: c.acompte, acompteLabel: fb.acompteLabel, net: c.net,
      paid: c.paid, paidNow: c.paidNow, paidDateFr: this._fmtDateLong(fb.paidDate), paidMethod: fb.paidMethod,
      isAvoir: fb.type === 'avoir',
      reglement: fb.reglement, notes: fb.notes,
      logoUrl: 'https://cdn.shopify.com/s/files/1/0798/6259/5851/t/17/assets/logo-red-studio.svg',
    };
  }

  async adjustFactureWithClaude() {
    if (!this._fb) return;
    const instruction = document.getElementById('fbClaudeInstruction')?.value.trim();
    if (!instruction) { this.toast('Écrivez une demande pour Claude', 'error'); return; }
    this._fbReadForm();
    const fb = this._fb;
    if (!fb.clientName.trim()) { this.toast('Renseignez le nom du client avant l\'ajustement', 'error'); return; }

    // Numéro réel attribué AVANT l'appel pour que Claude produise une facture définitive
    const num = await this._allocateFactureNumber();
    if (!this._fbEditingId && this.factures.find(f => f.id === num)) { this.toast(`La facture ${num} existe déjà`, 'error'); return; }

    const c = this._fbCompute();
    const baseHtml = this.buildFactureHtml(this._factureData(num, c));
    this.toast('Claude ajuste la facture...', '');
    try {
      const r = await wfetch(`${WORKER_URL}/chat-facture`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction, html: baseHtml }),
      });
      const j = await r.json();
      if (j.error) { this.toast(j.error, 'error'); return; }
      const m = (j.text || '').match(/```html\n([\s\S]*?)```/) || (j.text || '').match(/(<!DOCTYPE html>[\s\S]*?<\/html>)/i);
      if (!m) { this.toast('Pas de HTML renvoyé par Claude', 'error'); return; }
      const claudeHtml = m[1].trim();
      const netFromHtml = this._extractNetFromFactureHtml(claudeHtml);
      const montantStr = netFromHtml != null ? this._eur(netFromHtml) : undefined;
      await this._commitFacture(num, claudeHtml, c, montantStr);
      this.toast(`Facture ${num} ajustée par Claude et enregistrée`, 'success');
    } catch (e) { this.toast('Erreur réseau Claude', 'error'); }
  }

  /* ═══════════ RENDU HTML FACTURE (même DA que les devis) ═══════════ */
  _factureCSS() {
    return `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',-apple-system,sans-serif;font-size:10px;line-height:1.5;color:#111;padding:30px 35px;max-width:210mm;margin:0 auto}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:25px;padding-bottom:18px;border-bottom:1.5px solid #111}
.logo{height:10px;width:auto}
.company-info{font-size:8px;color:#444;margin-top:5px;line-height:1.5}
.devis-badge{text-align:right}
.devis-badge h2{font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase}
.devis-badge p{font-size:8px;color:#666;margin-top:3px}
.parties{display:flex;gap:30px;margin-bottom:20px}
.partie{flex:1}
.partie-label{font-size:7px;text-transform:uppercase;letter-spacing:1.5px;color:#888;margin-bottom:5px}
.partie-content{font-size:9px;line-height:1.5}
.partie-content strong{font-size:10px;font-weight:600}
.objet{background:#f7f7f7;padding:12px 16px;margin-bottom:20px}
.objet-title{font-size:10px;font-weight:600;margin-bottom:6px}
.objet-meta{display:flex;gap:20px;font-size:9px;flex-wrap:wrap}
.section{margin-bottom:18px}
.section-title{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;padding-bottom:6px;border-bottom:1px solid #ddd;margin-bottom:10px}
table{width:100%;border-collapse:collapse}
th{background:#111;color:white;font-size:7px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;padding:6px 8px;text-align:left}
th:last-child{text-align:right}
td{padding:7px 8px;border-bottom:1px solid #eee;font-size:9px}
td:last-child{text-align:right;font-weight:500}
.col-num{text-align:right;width:80px}
.row-total td{background:#f5f5f5;font-weight:600;border-bottom:none}
.row-paid td{background:#e8f5e9;color:#2a7d2a;font-weight:700;border-bottom:none}
.row-net td{background:#111;color:#fff;font-weight:700;border-bottom:none;font-size:10px}
.paid-stamp{display:inline-block;background:#2a7d2a;color:#fff;font-size:8px;font-weight:700;padding:2px 8px;border-radius:3px;letter-spacing:1px;margin-left:8px;vertical-align:middle}
.acquittee{margin-top:12px;background:#e8f5e9;border:1px solid #b9e4c4;color:#2a7d2a;padding:10px 14px;font-size:9px;font-weight:600;border-radius:4px}
.detail{font-size:7px;color:#666;font-weight:400;display:block;margin-top:1px}
.conditions{margin-top:15px;display:grid;grid-template-columns:1fr 1fr;gap:10px}
.condition-box{padding:10px 12px;background:#f9f9f9}
.condition-box.border{background:white;border:1px solid #ddd}
.condition-box h4{font-size:8px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px}
.condition-box ul{list-style:none;font-size:8px;line-height:1.6}
.condition-box li{padding-left:8px;position:relative}
.condition-box li:before{content:"•";position:absolute;left:0;color:#999}
.mentions{margin-top:15px;font-size:7.5px;color:#555;line-height:1.6;border-top:1px solid #eee;padding-top:10px}
.signature-area{margin-top:20px;display:flex;gap:25px}
.signature-box{flex:1}
.signature-box label{display:block;font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:6px}
.signature-line{border-bottom:1px solid #999;height:40px}
.footer{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:7px;color:#888;text-align:center}
.note{font-size:7px;color:#666;font-style:italic;margin-top:6px;line-height:1.5}
.page-break{page-break-before:always}
div[class]{page-break-inside:avoid}
table{page-break-inside:avoid}
tr{page-break-inside:avoid}
thead{display:table-header-group}
h1,h2,h3,h4{page-break-after:avoid}
ul,ol{page-break-inside:avoid}
p{orphans:3;widows:3}
body{orphans:3;widows:3}`;
  }

  buildFactureHtml(f) {
    const esc = s => this.escHtml(String(s == null ? '' : s));
    const co = f.company || {};
    const puLabel = f.tvaActive ? 'P.U. HT' : 'P.U.';
    const totLabel = f.tvaActive ? 'Total HT' : 'Total';

    const companyInfo = [
      esc(co.address),
      `SIRET : ${esc(co.siret)}`,
      co.numTva ? `N° TVA : ${esc(co.numTva)}` : '',
      `${esc(co.tel)} · ${esc(co.email)}`,
    ].filter(Boolean).join('<br>');

    const linesHtml = f.lines.map(l => `<tr>
      <td><strong>${esc(l.designation)}</strong>${l.detail ? `<span class="detail">${esc(l.detail)}</span>` : ''}</td>
      <td class="col-num">${this._num(l.qte) === 1 && !f.tvaActive ? '1' : this._num(l.qte).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</td>
      <td class="col-num">${this._eur(this._num(l.pu))}</td>
      <td class="col-num">${this._eur(l.total)}</td>
    </tr>`).join('');

    let totauxRows = `<tr class="row-total"><td colspan="3">${f.tvaActive ? 'Total HT' : 'Sous-total'}</td><td class="col-num">${this._eur(f.sousTotal)}</td></tr>`;
    if (f.tvaActive) {
      totauxRows += `<tr class="row-total"><td colspan="3">TVA ${f.tvaRate} %</td><td class="col-num">${this._eur(f.tvaAmount)}</td></tr>`;
      totauxRows += `<tr class="row-total"><td colspan="3">Total TTC</td><td class="col-num">${this._eur(f.totalTTC)}</td></tr>`;
    }
    if (f.acompte && f.acompte !== 0) {
      totauxRows += `<tr class="row-total"><td colspan="3">${esc(f.acompteLabel)}</td><td class="col-num">${this._eur(-Math.abs(f.acompte))}</td></tr>`;
    }
    if (f.paid && f.paidNow) {
      totauxRows += `<tr class="row-paid"><td colspan="3">Réglé le ${esc(f.paidDateFr)}${f.paidMethod ? ` (${esc(f.paidMethod)})` : ''}</td><td class="col-num">${this._eur(-Math.abs(f.paidNow))}</td></tr>`;
    }
    totauxRows += `<tr class="row-net"><td colspan="3">${f.isAvoir ? "MONTANT DE L'AVOIR" : 'NET À PAYER'}</td><td class="col-num">${this._eur(f.net)}</td></tr>`;

    const objetMeta = [
      f.devisId ? `<span><strong>Réf. devis :</strong> N° ${esc(f.devisId)}</span>` : '',
      `<span><strong>Date de vente / prestation :</strong> ${esc(f.dateVenteFr)}</span>`,
    ].filter(Boolean).join('');

    const tvaMention = f.tvaActive ? '' : 'TVA non applicable, art. 293 B du CGI.';

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>${esc(f.typeTitle)} ${esc(f.num)} - ${esc(f.client.name)}</title>
<style>
${this._factureCSS()}
</style>
</head>
<body>

<div class="header">
  <div>
    <img class="logo" src="${f.logoUrl}" alt="R/ED STUDIO">
    <div class="company-info"><strong>${esc(co.emetteur)}</strong><br>${companyInfo}</div>
  </div>
  <div class="devis-badge">
    <h2>${esc(f.typeTitle)} N° ${esc(f.num)}${f.paid ? '<span class="paid-stamp">PAYÉE</span>' : ''}</h2>
    <p>Émise le ${esc(f.dateEmissionFr)}${f.paid ? `<br>Acquittée le ${esc(f.paidDateFr)}` : ''}</p>
  </div>
</div>

<div class="parties">
  <div class="partie">
    <div class="partie-label">Émetteur</div>
    <div class="partie-content"><strong>${esc(co.emetteur)}</strong><br>${esc(co.address)}<br>SIRET : ${esc(co.siret)}${co.numTva ? `<br>N° TVA : ${esc(co.numTva)}` : ''}</div>
  </div>
  <div class="partie">
    <div class="partie-label">Client</div>
    <div class="partie-content"><strong>${esc(f.client.name)}</strong>${f.client.address ? `<br>${esc(f.client.address)}` : ''}${f.client.siret ? `<br>SIRET : ${esc(f.client.siret)}` : ''}${f.contact ? `<br>Contact : ${esc(f.contact)}` : ''}</div>
  </div>
</div>

${f.objet ? `<div class="objet">
  <div class="objet-title">${esc(f.objet)}</div>
  <div class="objet-meta">${objetMeta}</div>
</div>` : (objetMeta ? `<div class="objet"><div class="objet-meta">${objetMeta}</div></div>` : '')}

<div class="section">
  <table>
    <thead><tr><th>Désignation</th><th class="col-num">Qté</th><th class="col-num">${puLabel}</th><th class="col-num">${totLabel}</th></tr></thead>
    <tbody>
      ${linesHtml}
      ${totauxRows}
    </tbody>
  </table>
  ${f.paid ? `<div class="acquittee">Facture acquittée le ${esc(f.paidDateFr)}${f.paidMethod ? `, réglée par ${esc(f.paidMethod)}` : ''}. Bon pour solde de tout compte.</div>` : ''}
</div>

<div class="conditions">
  <div class="condition-box border"><h4>Règlement</h4>
    <p style="font-size:8px;line-height:1.6">${esc(f.reglement)}</p>
    <p style="font-size:8px;margin-top:6px;line-height:1.5">Bénéficiaire : ${esc(co.beneficiaire)}<br>IBAN ${esc(co.iban)} · BIC ${esc(co.bic)}</p>
  </div>
  <div class="condition-box"><h4>Informations</h4>
    <ul>
      <li>Date d'émission : ${esc(f.dateEmissionFr)}</li>
      <li>Date de vente / prestation : ${esc(f.dateVenteFr)}</li>
      ${f.devisId ? `<li>Référence devis : N° ${esc(f.devisId)}</li>` : ''}
    </ul>
  </div>
</div>

${f.notes ? `<div class="note">${esc(f.notes)}</div>` : ''}

<div class="mentions">
  ${tvaMention ? tvaMention + '<br>' : ''}
  En cas de retard de paiement, application de pénalités de retard au taux de trois fois le taux d'intérêt légal, ainsi qu'une indemnité forfaitaire de 40 EUR pour frais de recouvrement (art. L441-10 et D441-5 du Code de commerce). Pas d'escompte pour paiement anticipé.
</div>

<div class="footer">${esc(co.emetteur)} · ${esc(co.mentionLegale)}<br>${esc(co.address)} · ${esc(co.tel)} · ${esc(co.email)}</div>

</body>
</html>`;
  }

  /* Extraction best-effort des lignes d'un devis HTML */
  _extractLinesFromDevisHtml(html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const trs = doc.querySelectorAll('table tr');
      const lines = [];
      trs.forEach(tr => {
        if (tr.classList.contains('row-total')) return;
        if (tr.querySelector('th')) return;
        const tds = tr.querySelectorAll('td');
        if (tds.length < 2) return;
        const first = tds[0];
        const detailEl = first.querySelector('.detail, .devis-detail, span');
        const detail = detailEl ? detailEl.textContent.trim() : '';
        let designation = first.textContent.trim();
        if (detail && designation.endsWith(detail)) designation = designation.slice(0, -detail.length).trim();
        const nums = [...tds].map(td => td.textContent.trim());
        // Dernière cellule = total
        const totalStr = nums[nums.length - 1];
        const total = this._num((totalStr.match(/[\d\s.,]+/) || [''])[0]);
        if (!designation || (!total && tds.length < 3)) return;
        // 4 colonnes : desig, qte, pu, total
        if (tds.length >= 4) {
          const qte = this._num((nums[1].match(/[\d\s.,]+/) || ['1'])[0]) || 1;
          const pu = this._num((nums[2].match(/[\d\s.,]+/) || [''])[0]);
          lines.push({ designation, detail, qte, pu: pu || (qte ? total / qte : total) });
        } else {
          lines.push({ designation, detail, qte: 1, pu: total });
        }
      });
      return lines.filter(l => l.designation && (l.pu || l.qte));
    } catch (e) { return null; }
  }

  /* ═══════════ HTML EXTRACTION (fallback si pas de devis-meta) ═══════════ */
  _extractClientFromHtml(html) {
    // Chercher le nom client dans les patterns courants des devis
    // Pattern 1: <strong>NOM CLIENT</strong> dans la section client/destinataire
    const patterns = [
      /destinataire[\s\S]{0,200}<strong>([^<]+)<\/strong>/i,
      /client[\s\S]{0,200}<strong>([^<]+)<\/strong>/i,
      /<strong>([A-ZÀÉÈÊËÏÎÔÙÛÜÇŒ][A-ZÀÉÈÊËÏÎÔÙÛÜÇŒ\s\-&\.]{2,})<\/strong>/,
    ];
    for (const p of patterns) {
      const m = html.match(p);
      if (m && m[1].trim().length > 1 && m[1].trim() !== 'REWORKED') return m[1].trim();
    }
    return null;
  }

  _extractTypeFromHtml(html) {
    const lower = html.toLowerCase();
    if (lower.includes('animation') || lower.includes('événement') || lower.includes('live')) return 'event';
    if (lower.includes('création') || lower.includes('logo') || lower.includes('charte') || lower.includes('direction artistique')) return 'creation';
    if (lower.includes('location') || lower.includes('secabo')) return 'location';
    if (lower.includes('sourcing') || lower.includes('fournisseur')) return 'sourcing';
    if (lower.includes('prototype') || lower.includes('développement produit') || lower.includes('r&d')) return 'dev';
    return 'production';
  }

  _extractObjetFromHtml(html) {
    const patterns = [
      /objet[\s\S]{0,50}?(?:title|label)?[^>]*>([^<]{5,})/i,
      /<h[23][^>]*>(?:objet|description)[^<]*<\/h[23]>\s*(?:<[^>]+>)*([^<]{5,})/i,
      /background:\s*#f7f7f7[^>]*>(?:<[^>]+>)*([^<]{5,})/i,
    ];
    for (const p of patterns) {
      const m = html.match(p);
      if (m) return m[1].trim().slice(0, 120);
    }
    return null;
  }

  _extractMontantFromHtml(html) {
    // Chercher le total dans les tableaux : "Total", "Total TTC", "TOTAL"
    const patterns = [
      /total\s*(?:ttc|ht|général)?[\s\S]{0,100}?([\d\s]+(?:,\d{2})?\s*€)/i,
      /total[\s\S]{0,200}?<(?:td|span|strong|div)[^>]*>\s*([\d\s]+(?:,\d{2})?\s*€)/i,
      /([\d\s]+(?:,\d{2})?\s*€)\s*<\/(?:td|strong)>\s*<\/tr>\s*(?:<\/(?:tbody|table)>)/i,
    ];
    for (const p of patterns) {
      const m = html.match(p);
      if (m) {
        let val = m[1].trim();
        // Nettoyer les espaces multiples
        val = val.replace(/\s+/g, ' ');
        return val;
      }
    }
    return null;
  }

  /* ═══════════ HELPERS ═══════════ */
  typeLabel(t) { return { event:'Événement', production:'Production', creation:'Création', location:'Location', sourcing:'Sourcing', dev:'Développement', confection:'Confection' }[t] || t; }
  statusLabel(s) { return { brouillon:'Brouillon', envoye:'Envoyé', accepte:'Accepté', refuse:'Refusé' }[s] || s; }
  fmtDate(d) { if (!d) return ''; const [y,m,day] = d.split('-'); return `${parseInt(day)} ${['','jan','fév','mar','avr','mai','jun','jul','aoû','sep','oct','nov','déc'][parseInt(m)]} ${y}`; }
  fmtMonth(m) { if (!m) return ''; const [y,mo] = m.split('-'); return `${['','Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'][parseInt(mo)]} ${y}`; }
  toast(msg, type = '') { const el = document.createElement('div'); el.className = `toast ${type}`; el.textContent = msg; document.getElementById('toastContainer').appendChild(el); setTimeout(() => el.remove(), 3000); }

  /* ═══════════ DEFAULTS ═══════════ */
  _defaultDevis() { return [
    { id:'2026-03-002', date:'2026-03-21', client:'DEWOLPH', type:'production', objet:'Production textile personnalisée', montant:'2 560 - 4 128 €', statut:'envoye' },
    { id:'2026-03-003', date:'2026-03-23', client:'DEWOLPH', type:'production', objet:'Bonnets + Étiquettes (225 pcs)', montant:'915 - 1 374 €', statut:'envoye' },
    { id:'2026-03-004', date:'2026-03-26', client:'RAWRUN', type:'location', objet:'Location Secabo TC7 Smart', montant:'50 €', statut:'accepte' },
    { id:'2026-03-005', date:'2026-03-30', client:'LICORNE PASSION', type:'creation', objet:'Création logotype + charte', montant:'520 €', statut:'envoye' },
    { id:'2026-04-001', date:'2026-04-15', client:'LICORNE PASSION', type:'sourcing', objet:'Sourcing blanks collection (46 réf.)', montant:'2 300 €', statut:'envoye' },
    { id:'2026-05-001', date:'2026-05-01', client:'SNEAKAUMAX', type:'production', objet:'T-shirts F&F ouverture (10 pcs)', montant:'248 - 343 €', statut:'envoye' },
    { id:'2026-05-002', date:'2026-05-01', client:'RAWRUN', type:'event', objet:'Activation live Le Heat (20 mai)', montant:'380 - 500 €', statut:'accepte' },
    { id:'2026-05-003', date:'2026-05-01', client:'RAWRUN', type:'dev', objet:'Développement sportswear', montant:'40 €/proto', statut:'envoye' },
  ]; }
  _defaultClients() { return [
    { name:'RAWRUN', address:'6 Rue du Nordfeld, 68100 Mulhouse', siret:'992 885 244 00010', contacts:'Oscar Cheikh-Djavadi, Hugo Pommerette', source:'local' },
    { name:'DEWOLPH', address:'', siret:'', contacts:'', source:'local' },
    { name:'LICORNE PASSION', address:'229 Rue Saint-Honoré, 75001 Paris', siret:'935 397 711 00034', contacts:'', source:'local' },
    { name:'SNEAKAUMAX', address:'22 Rue Pizay, 69001 Lyon', siret:'', contacts:'Augustin Pierre', source:'local' },
  ]; }
  _defaultTarifs() { return {
    seri_impression_1c:{label:'Sérigraphie 1 couleur',price:2.50,unit:'tirage',cat:'Sérigraphie'},
    seri_impression_2c:{label:'Sérigraphie 2 couleurs',price:3.50,unit:'tirage',cat:'Sérigraphie'},
    seri_cadre:{label:'Cadre insolé A3+',price:20,unit:'cadre',cat:'Sérigraphie'},
    cuisson:{label:'Cuisson / fixation',price:1.00,unit:'pièce',cat:'Sérigraphie'},
    dtf_small:{label:'DTF petit (logo)',price:1.50,unit:'pièce',cat:'DTF'},
    dtf_medium:{label:'DTF moyen (A4)',price:3.50,unit:'pièce',cat:'DTF'},
    dtf_large:{label:'DTF grand (A3)',price:5.00,unit:'pièce',cat:'DTF'},
    dtf_presse:{label:'Presse à chaud',price:2.00,unit:'pièce',cat:'DTF'},
    base_rwrkd_1:{label:'RWRKD Blanks (1-9)',price:10.80,unit:'pièce',cat:'Bases textiles'},
    base_rwrkd_10:{label:'RWRKD Blanks (10-24)',price:8.80,unit:'pièce',cat:'Bases textiles'},
    base_rwrkd_25:{label:'RWRKD Blanks (25-49)',price:8.30,unit:'pièce',cat:'Bases textiles'},
    base_rwrkd_50:{label:'RWRKD Blanks (50-99)',price:7.90,unit:'pièce',cat:'Bases textiles'},
    base_rwrkd_100:{label:'RWRKD Blanks (100+)',price:7.60,unit:'pièce',cat:'Bases textiles'},
    etiquette:{label:'Étiquette personnalisée',price:0.90,unit:'pièce',cat:'Couture & Étiquettes'},
    couture_plate:{label:'Couture piqueuse plate',price:1.50,unit:'pièce',cat:'Couture & Étiquettes'},
    couture_surjet:{label:'Surjet pose latérale',price:1.30,unit:'pièce',cat:'Couture & Étiquettes'},
    etiq_col_tissee:{label:'Étiquette col cousue',price:2.40,unit:'pièce',cat:'Couture & Étiquettes'},
    etiq_col_dtf:{label:'Impression col DTF',price:2.00,unit:'pièce',cat:'Couture & Étiquettes'},
    animation_live:{label:'Animation sérigraphie live',price:340,unit:'forfait',cat:'Forfaits'},
    da_standard:{label:'Direction artistique',price:120,unit:'forfait',cat:'Forfaits'},
    logo_creation:{label:'Création logo + charte',price:520,unit:'forfait',cat:'Forfaits'},
    prototype:{label:'Prototype complet',price:40,unit:'pièce',cat:'Développement'},
    sourcing:{label:'Sourcing par référence',price:50,unit:'référence',cat:'Développement'},
    dev_forfait:{label:'Forfait dev (sourcing+perso)',price:40,unit:'pièce',cat:'Développement'},
    location_secabo:{label:'Location Secabo TC7',price:50,unit:'jour',cat:'Location'},
  }; }
  _defaultTarifsDev() { return {
    garments: {
      chemise:{label:'Chemise',basePrice:490,cat:'Hauts'},
      light_jersey:{label:'Light jersey tops (t-shirts)',basePrice:390,cat:'Hauts'},
      heavy_jersey:{label:'Heavy jersey / fleece tops',basePrice:550,cat:'Hauts'},
      veste_simple:{label:'Veste simple (sans doublure)',basePrice:690,cat:'Hauts'},
      veste_complexe:{label:'Veste complexe (doublée)',basePrice:790,cat:'Hauts'},
      pantalon:{label:'Pantalon / short denim / toile',basePrice:660,cat:'Bas'},
      jogger:{label:'Jersey jogger / short',basePrice:490,cat:'Bas'},
      sac_simple:{label:'Sac simple (tote bag)',basePrice:290,cat:'Sacs & accessoires'},
      sac_complexe:{label:'Sac complexe / sacoche',basePrice:490,cat:'Sacs & accessoires'},
    },
    coefficients: {
      coef1:{label:'Coef 1 — Pièce standard',mult:1},
      coef2:{label:'Coef 2 — Détails / construction recherchés',mult:1.25},
      coef3:{label:'Coef 3 — Pièce très complexe',mult:1.5},
    },
    options: {
      sourcing_textile:{label:'Sourcing textile',phase:'predev',mode:'flat_qty',value:40,unitLabel:'textile'},
      sourcing_trims:{label:'Sourcing trims',phase:'predev',mode:'flat_qty',value:25,unitLabel:'trim'},
      veille_recherche:{label:'Veille de recherches (finitions, détails)',phase:'predev',mode:'flat',value:80,unitLabel:''},
      toile_supp:{label:'Toile supplémentaire',phase:'dev',mode:'pct_qty',value:40,unitLabel:'toile'},
      modif_supp:{label:'Modification supplémentaire',phase:'dev',mode:'pct_qty',value:15,unitLabel:'modification'},
      proto_supp:{label:'Prototype matière finale supplémentaire',phase:'dev',mode:'pct_qty',value:45,unitLabel:'exemplaire'},
      gradation:{label:'Gradation',phase:'dev',mode:'pct_qty',value:15,unitLabel:'taille graduée'},
      fiche_technique:{label:'Fiche technique',phase:'postdev',mode:'flat',value:90,unitLabel:''},
      techpack:{label:'Techpack',phase:'postdev',mode:'pct',value:30,unitLabel:''},
      digitalisation:{label:'Digitalisation des patrons',phase:'postdev',mode:'pct',value:25,unitLabel:''},
    },
    included: ['1 patronage', '1 toile', '1 modification (légère)', '1 prototype en matière finale'],
  }; }
  _defaultTarifsConfection() { return {
    garments: {
      chemise:{label:'Chemise',basePrice:490,cat:'Hauts'},
      light_jersey:{label:'Light jersey tops (t-shirts)',basePrice:390,cat:'Hauts'},
      heavy_jersey:{label:'Heavy jersey / fleece tops',basePrice:550,cat:'Hauts'},
      veste_simple:{label:'Veste simple (sans doublure)',basePrice:690,cat:'Hauts'},
      veste_complexe:{label:'Veste complexe (doublée)',basePrice:790,cat:'Hauts'},
      pantalon:{label:'Pantalon / short denim / toile',basePrice:660,cat:'Bas'},
      jogger:{label:'Jersey jogger / short',basePrice:490,cat:'Bas'},
      sac_simple:{label:'Sac simple (tote bag)',basePrice:290,cat:'Sacs & accessoires'},
      sac_complexe:{label:'Sac complexe / sacoche',basePrice:490,cat:'Sacs & accessoires'},
    },
    coefficients: {
      coef1:{label:'Coef 1 — Pièce standard',mult:1},
      coef2:{label:'Coef 2 — Détails / construction recherchés',mult:1.25},
      coef3:{label:'Coef 3 — Pièce très complexe',mult:1.5},
    },
    pieceCountThresholds: [
      { maxPieces: 8, coefKey: 'coef1' },
      { maxPieces: 15, coefKey: 'coef2' },
      { maxPieces: 999, coefKey: 'coef3' },
    ],
    operations: {
      coupe:{label:'Coupe / placement',category:'included',pct:10},
      montage:{label:'Montage / assemblage',category:'included',pct:65},
      finitions:{label:'Finitions (ourlets, boutonnières, repassage)',category:'included',pct:15},
      controle:{label:'Contrôle qualité',category:'included',pct:10},
      conditionnement:{label:'Conditionnement / emballage',category:'addon',mode:'flat_qty',value:1,unitLabel:'pièce',defaultChecked:true},
      fournitures:{label:'Fournitures de montage (fil, etc.)',category:'addon',mode:'flat_qty',value:0.5,unitLabel:'pièce',defaultChecked:true},
    },
    quantityTiers: [
      { minQty:1, mult:1, label:'1-9' },
      { minQty:10, mult:0.9, label:'10-24' },
      { minQty:25, mult:0.85, label:'25-49' },
      { minQty:50, mult:0.8, label:'50-99' },
      { minQty:100, mult:0.75, label:'100+' },
    ],
    ouvertureDossierPrice: 70,
  }; }
  _defaultFicheTarifsIntro() {
    return `REWORKED est un atelier de sérigraphie et de confection basé à Lyon. Nous accompagnons nos clients de la création à la production : du marquage textile à la mise au point de nouvelles pièces, jusqu'à leur fabrication en série.

Les tarifs ci-dessous sont indicatifs et donnés à titre de référence. Chaque projet fait l'objet d'un devis personnalisé, le montant définitif dépendant de la quantité, de la complexité et des options choisies.

Acompte de 40% à la validation du devis, solde à la livraison. N'hésitez pas à nous contacter pour discuter de votre projet.`;
  }
}

const app = new DevisDashboard();
app.tryStoredAccess();
